import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import GradientButton from '../components/GradientButton'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import PageShell from '../components/PageShell'
import Drawer from '../components/Drawer'
import UnitOccupiedWarning from './lots/UnitOccupiedWarning'
import DeletedLotsSection from './lots/DeletedLotsSection'
import MoveFields, { type FishMoveStocks } from './lots/MoveFields'
import TransferProtocolFields, { type TransferRationDraft } from './lots/TransferProtocolFields'
import LotTransferPlanRow from './lots/LotTransferPlanRow'
import { readPlannedTransfer, withPlannedTransfer, withTransferRation } from '../utils/transferProtocol'
import { recordStockingDay } from '../utils/stockLotIntoUnits'
import { FishMoveOfflineError, findLotRecordAfter, findSameNameLotInUnit, parseArrivalCount, parseMoveDeaths, planFishMove, recordArrivalCount, recordFishMove, recordMoveDeaths } from '../utils/lotFishMove'
import { moveErrorKey } from './lots/moveErrors'
import LotTreatmentDialog from './lots/LotTreatmentDialog'
import LotTreatmentsSection, { WithdrawalWarning } from './lots/LotTreatmentsSection'
import { facilityCurrencyOrFallback } from '../utils/facilityCurrency'
import { StockIntoUnitsForm } from './lots/StockIntoUnitsForm'
import { useLots, invalidateLotCache } from '../hooks/useLots'
import { useSessionState } from '../hooks/useSessionState'
import { lotApi, type LotMutationResult, type LotMutationSyncState } from '../utils/lotApi'
import type { EntityBatchSyncStatus } from '../utils/entityMutationSync'
import { deriveLotSyncOutcome, type LotSyncOutcome } from '../utils/lotSyncOutcome'
import { reconcileSyncMutationState } from '../utils/syncMutationReconciler'
import { computeSyncBannerVisibility } from '../utils/syncBannerVisibility'
import {
  api,
  type CommunityActorContextRecord,
  type LotStoryGroupDeliveryRecord,
  type LotStoryGroupDeliveryReceiptRecord,
  type LotStoryGroupShareApprovalPreflightBlockingReason,
  type LotStoryGroupSharePromotionPreflightBlockingReason,
  type LotStoryGroupShareReleaseGatePreflightBlockingReason,
  type LotStoryGroupReadPreflightBlockingReason,
  type LotStoryGroupReadPreflightRecord,
  type LotStoryGroupShareReviewPreflightBlockingReason,
  type LotStoryGroupShareReviewPreflightRecord
} from '../utils/api'
import {
  ensureFarmList,
  findFarmById,
  type WizardFarm,
  type WizardProductionUnit
} from '../utils/farmWizard'
import type { Lot, LotEvent as LedgerLotEvent, LotStage } from '../types/lot'
import { scopeHistoryToLot } from '../utils/lotHistoryScope'
import { buildLotHistoryDetails, type HatcheryFieldChange, type LotHistoryDetail } from '../utils/lotHistoryDetails'
import type {
  LotStoryArtifactBundleV1,
  LotStoryArtifactDetailV1,
  LotStoryArtifactVersionV1,
  LotStoryProjectionV1
} from '../types/lotStory'
import DateInput from '../components/DateInput'
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { useT, type TranslationKey } from '../i18n'
import { recordLotMerge, recordLotMove, recordLotSplit, recordLotTransfer } from '../utils/lotLineageCommands'
import { normalizeSelectedProductionUnitId, resolveHydratedProductionUnitId } from './lots/contextSelection'
import {
  buildLotFormPresentation,
  getLotFormAction,
  getLotTransferIntentState,
  isLotTransferIntentSubmitDisabled,
  getLotTransferIntentSubmitErrorKey,
  type LotFormActionPayload,
  type LotFormIntent,
  type LotTransferTargetLocationType
} from './lots/formAction'
import {
  buildLotDestinationOptions,
  filterLotDestinationOptions,
  listFarmProcessingSites,
  type LotDestinationOption
} from './lots/destinationPicker'
import {
  resolveGroupDeliveryReceiptViewerLabel,
  resolveStoredActorContextLabel
} from './lots/storyActorLabels'
import {
  buildMergeSourceChipGroups,
  buildMergeSourceReviewState,
  buildLineageLotQuickOptions,
  buildLineageLotOptionGroups,
  filterLineageLotQuickOptions,
  mergeSourcesShareProductionUnit,
  reorderMergeSourceLotIds,
  summarizeMergeSourceSelection
} from './lots/lineageScope'
import {
  buildProcessingSiteActivityEntries,
  buildProcessingSiteActivityOriginRecencyBreakdown,
  buildProcessingSiteActivityOriginSummary,
  buildProcessingSiteActivityRecencySummary,
  buildProcessingSiteRecentLotProvenanceEntries,
  buildProcessingSiteRecentLotProvenanceEntriesMap,
  buildProcessingSiteRecentLotProvenanceMap,
  getProcessingSiteActivityOriginRecencyBadge,
  type ProcessingSiteActivityEntry,
  type ProcessingSiteActivityOriginRecencyBadge,
  type ProcessingSiteActivityOriginSummaryEntry,
  type ProcessingSiteRecentLotProvenance
} from './lots/processingSiteRecentLotProvenance'
import { buildProcessingSiteSummary } from './lots/processingSiteSummary'
import LotStoryLibraryPanel from './lots/LotStoryLibraryPanel'
import LotStoryPrivateWritePreflightCard from './lots/LotStoryPrivateWritePreflightCard'
import type {
  LotStoryGroupDeliveryStatus,
  LotStoryGroupDeliveryView,
  LotStoryGroupReadPreflightStatus,
  LotStoryGroupReadPreflightView,
  LotStoryGroupShareReviewPreflightStatus,
  LotStoryGroupShareReviewPreflightView,
  LotStoryGroupShareShellView,
  LotStoryLibraryPanelActions,
  LotStoryPrivateWritePreflightStatus,
  LotStoryPrivateWritePreflightView,
  LotStoryLibraryPanelState,
  RenderStoryProjectionReadOnlySectionsArgs
} from './lots/lotStoryLibraryTypes'
import {
  formatOptionalStoryNumber,
  formatOptionalStoryText,
  getStoryFamilyLabel,
  getStoryFamilyTechnicalId,
  getLotFamilyTechnicalIdHelperInfo,
  type LotStoryLibraryPresentation,
  type StoryPresentation
} from './lots/lotStoryPresentation'
import { buildStoryActiveDescendantLocationGroups } from './lots/storyActiveDescendantGroups'
import { buildStoryLineageGraphRelations } from './lots/storyLineageGraph'
import { buildStoryTimelineAuditSummary } from './lots/storyTimelineAudit'
import { buildFilteredStoryTimelineEvents, type StoryTimelineFilter } from './lots/storyTimelineFilter'
import { buildStoryTimelineEventCounts } from './lots/storyTimelineEventCounts'
import {
  getLotStoryArtifactVersionLabel,
  getLotStoryArtifactVersionBadgeStyle,
  getLotStoryReadinessBadgeStyle,
  getLotStoryReadinessLabel
} from './lots/lotStoryLibraryUi'
import { buildLotLineageSummaryMap, resolveDisplayedLotLineageSummary, type LotLineageSummary } from './lots/lineageSummary'
import { buildLotsTableSummary, getLotsTableEmptyKey, getLotsTableScope, sortLotsForDisplay } from './lots/tableScope'
import LotProjectionSection from './lots/LotProjectionSection'
import LotCountDifferencesSection from './lots/LotCountDifferencesSection'
import { readHarvestTargetWeightG } from '../utils/lotProjectionSettings'
import { isHatcheryLot } from '../utils/hatchery'
import LotHatcherySection from './lots/LotHatcherySection'
import CapacityTablePicker, { capacityAssignmentForChoice } from './capacity/CapacityTablePicker'
import LotCapacityRow from './capacity/LotCapacityRow'
import { withLotCapacityAssignment } from '../utils/carryingCapacity'
import LotActionsMenu, { type LotActionItem } from './lots/LotActionsMenu'
import { LotCostSection } from './lots/LotCostSection'
import { EggArrivalFields, emptyArrivalDraft, ownArrival, parseArrivalDraft, type ArrivalDraft } from './lots/eggArrivalForm'
import PillTabs from '../components/PillTabs'
import { readEggsReceived, withEggArrival } from '../utils/hatchery'
import { farmDateTimeInputValue, farmDayKey, farmTimeZone, farmToday, farmWallClockToInstant, formatFarmDateTime } from '../utils/farmTime'
import { isLotActiveForInteraction } from '../utils/lotInteraction'

const stageOptions: LotStage[] = ['Active', 'Inactive', 'Harvested', 'Planned', 'Dormant']

// Office UX v0 (findings #95): the lot list opens on Activos, with a tab per
// stage (and Todos) instead of every lot on one long page.
type LotListFilter = LotStage | 'all'
const LOT_LIST_FILTERS: LotListFilter[] = ['Active', 'Planned', 'Inactive', 'Dormant', 'Harvested', 'all']
const listStageOf = (stage: string | undefined | null): LotStage => (
  stage && (stageOptions as string[]).includes(stage) ? stage as LotStage : 'Active'
)

type LotDetailTab = 'summary' | 'hatchery' | 'projection' | 'history' | 'costs' | 'treatments' | 'traceability'
const LOT_DETAIL_TAB_LABEL_KEY: Record<LotDetailTab, TranslationKey> = {
  summary: 'lots.detail.tab.summary',
  hatchery: 'lots.detail.tab.hatchery',
  projection: 'lots.detail.tab.projection',
  history: 'lots.detail.tab.history',
  costs: 'lots.detail.tab.costs',
  treatments: 'lots.detail.tab.treatments',
  traceability: 'lots.detail.tab.traceability'
}
const MERGE_REVIEW_PREVIEW_LIMIT = 3

type LotsWorkspaceMode = 'operations' | 'stories'

const stageLabelKey: Record<LotStage, TranslationKey> = {
  Active: 'lots.stage.active',
  Inactive: 'lots.stage.inactive',
  Harvested: 'lots.stage.harvested',
  Planned: 'lots.stage.planned',
  Dormant: 'lots.stage.dormant'
}

const sourceFreshnessLabelKey: Record<LotStoryProjectionV1['readiness']['sourceFreshness'], TranslationKey> = {
  fresh: 'lots.story.readiness.sourceFreshness.fresh',
  stale: 'lots.story.readiness.sourceFreshness.stale',
  unknown: 'lots.story.readiness.sourceFreshness.unknown'
}

const lifeStageLabelKey: Record<string, TranslationKey> = {
  eggs: 'lots.hatchery.stage.eggs',
  yolkSac: 'lots.hatchery.stage.yolkSac',
  firstFeeding: 'lots.hatchery.stage.firstFeeding'
}

const lotHistoryEventLabelKey: Record<LedgerLotEvent['eventType'], TranslationKey> = {
  create: 'lots.history.eventType.create',
  update: 'lots.history.eventType.update',
  split: 'lots.history.eventType.split',
  merge: 'lots.history.eventType.merge',
  move: 'lots.history.eventType.move',
  transfer: 'lots.history.eventType.transfer'
}

type LotHistorySnapshotField = 'lotKey' | 'stage' | 'farmId' | 'productionUnitId' | 'species' | 'biomassKg'

const lotHistorySnapshotFieldLabelKey: Record<LotHistorySnapshotField, TranslationKey> = {
  lotKey: 'lots.history.field.lotKey',
  stage: 'lots.history.field.stage',
  farmId: 'lots.history.field.facility',
  productionUnitId: 'lots.history.field.productionUnit',
  species: 'lots.history.field.species',
  biomassKg: 'lots.history.field.biomassKg'
}

type LotFormState = {
  id: string | null
  lotKey: string
  farmId: string
  productionUnitId: string
  processingSiteId: string
  targetLocationType: LotTransferTargetLocationType
  stage: LotStage
  species: string
  stockingDate: string
  harvestDate: string
  biomassKg: string
}

const blankForm = (farmId: string, productionUnitId: string): LotFormState => ({
  id: null,
  lotKey: '',
  farmId,
  productionUnitId,
  processingSiteId: '',
  targetLocationType: 'production-unit',
  stage: 'Active',
  species: '',
  stockingDate: '',
  harvestDate: '',
  biomassKg: ''
})

const normalizeTransferTargetLocationType = (value?: Lot['locationType']): LotTransferTargetLocationType => (
  value === 'processing-site' ? 'processing-site' : value === 'farm' ? 'farm' : 'production-unit'
)

const buildFormFromLot = (lot: Lot, options?: { transferTarget?: boolean }): LotFormState => ({
  id: lot.id,
  lotKey: lot.lotKey ?? '',
  // Findings #190: Trasladar lote opens on the lot's own facility; only the unit is left to choose.
  farmId: lot.farmId,
  productionUnitId: options?.transferTarget ? '' : (lot.productionUnitId ?? ''),
  processingSiteId: options?.transferTarget ? '' : (lot.locationType === 'processing-site' ? (lot.locationId ?? '') : ''),
  targetLocationType: options?.transferTarget ? 'production-unit' : normalizeTransferTargetLocationType(lot.locationType),
  stage: lot.stage,
  species: lot.species ?? '',
  stockingDate: dateInputValue(lot.stockingDate),
  harvestDate: dateInputValue(lot.harvestDate),
  biomassKg: lot.biomassKg !== undefined && lot.biomassKg !== null ? String(lot.biomassKg) : ''
})

const dateInputValue = (value?: string): string => {
  if (!value) return ''
  const normalized = value.slice(0, 10)
  return normalized
}

type LotHistorySnapshot = {
  lotKey?: string
  stage?: LotStage
  farmId?: string
  productionUnitId?: string
  species?: string
  biomassKg?: number
}

// Utility: Safely cast a value to a plain object record, or null if not an object
function asObjectRecord(value: any): Record<string, any> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>
  return null
}

const isLotStage = (value: unknown): value is LotStage => stageOptions.includes(value as LotStage)

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  values.forEach((value) => {
    const key = String(value ?? '').trim()
    if (!key) return
    if (seen.has(key)) return
    seen.add(key)
    out.push(key)
  })
  return out
}

const getFarmScopedProcessingSiteKey = (farmId: string, processingSiteId: string): string => (
  `${farmId}::${processingSiteId}`
)

const formatHistoryLotIds = (ids: string[], lotKeyById: Map<string, string>): string => (
  uniqueStrings(ids.map((id) => lotKeyById.get(id) ?? id)).join(', ')
)

const formatHistoryTimestamp = (value: string): string => {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

const formatProcessingSiteActivityTimestamp = (value: string | null): string => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

const readHistorySnapshot = (event: LedgerLotEvent, key: 'before' | 'after'): LotHistorySnapshot | null => {
  const metadata = asObjectRecord(event.metadata)
  const snapshot = asObjectRecord(metadata?.[key])
  if (!snapshot) return null

  return {
    lotKey: typeof snapshot.lotKey === 'string' ? snapshot.lotKey : undefined,
    stage: isLotStage(snapshot.stage) ? snapshot.stage : undefined,
    farmId: typeof snapshot.farmId === 'string' ? snapshot.farmId : undefined,
    productionUnitId: typeof snapshot.productionUnitId === 'string' ? snapshot.productionUnitId : undefined,
    species: typeof snapshot.species === 'string' ? snapshot.species : undefined,
    biomassKg: typeof snapshot.biomassKg === 'number' ? snapshot.biomassKg : undefined
  }
}

const getLotHistoryBadgeStyle = (eventType: LedgerLotEvent['eventType']): React.CSSProperties => {
  switch (eventType) {
    case 'create':
      return { backgroundColor: '#dcfce7', color: '#166534' }
    case 'update':
      return { backgroundColor: '#dbeafe', color: '#1d4ed8' }
    case 'split':
      return { backgroundColor: '#ede9fe', color: '#6d28d9' }
    case 'merge':
      return { backgroundColor: '#fae8ff', color: '#a21caf' }
    case 'move':
      return { backgroundColor: '#cffafe', color: '#0f766e' }
    case 'transfer':
      return { backgroundColor: '#ffedd5', color: '#c2410c' }
    default:
      return { backgroundColor: '#e5e7eb', color: '#374151' }
  }
}

const getLotStoryReasonBadgeStyle = (severity: 'warning' | 'block'): React.CSSProperties => {
  if (severity === 'block') {
    return { backgroundColor: '#fee2e2', color: '#b91c1c' }
  }

  return { backgroundColor: '#fef3c7', color: '#92400e' }
}

const lotStoryCardStyle: React.CSSProperties = {
  border: '1px solid #fde68a',
  borderRadius: 10,
  backgroundColor: '#ffffff',
  padding: 12,
  display: 'grid',
  gap: 4
}

type LotMutationVerb = 'created' | 'updated' | 'deleted'

const getLotMutationStatusKey = (verb: LotMutationVerb, state: LotMutationSyncState): TranslationKey => {
  if (state === 'synced') return `lots.status.${verb}Synced`
  if (state === 'offline') return `lots.status.${verb}Offline`
  return `lots.status.${verb}Local`
}

const canMembershipWritePrivateLotStory = (role?: CommunityActorContextRecord['membershipRole']): boolean => (
  role === 'contributor' || role === 'editor' || role === 'admin'
)

const resolveGroupReadPreflightStatus = (args: {
  loading: boolean
  error: string | null
  record: LotStoryGroupReadPreflightRecord | null
}): LotStoryGroupReadPreflightStatus => {
  if (args.loading && !args.record) {
    return 'loading'
  }

  if (args.error) {
    return 'error'
  }

  if (!args.record) {
    return 'idle'
  }

  return args.record.groupReadAllowed ? 'allowed' : 'denied'
}

const resolveGroupDeliveryStatus = (args: {
  loading: boolean
  error: string | null
  record: LotStoryGroupDeliveryRecord | null
  visible: boolean
}): LotStoryGroupDeliveryStatus => {
  if (!args.visible) {
    return 'idle'
  }

  if (args.loading && !args.record) {
    return 'loading'
  }

  if (args.error) {
    return 'error'
  }

  if (!args.record) {
    return 'denied'
  }

  return 'allowed'
}

const getGroupReadBlockingReasonLabel = (
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  reason: LotStoryGroupReadPreflightBlockingReason
): string => {
  switch (reason) {
    case 'artifact-not-published':
      return t('lots.story.groupPreflight.reason.artifactNotPublished')
    case 'artifact-not-group-visible':
      return t('lots.story.groupPreflight.reason.artifactNotGroupVisible')
    case 'no-active-group-context':
      return t('lots.story.groupPreflight.reason.noActiveGroupContext')
    case 'no-target-group-selected':
      return t('lots.story.groupPreflight.reason.noTargetGroupSelected')
    case 'target-group-not-eligible':
      return t('lots.story.groupPreflight.reason.targetGroupNotEligible')
    case 'target-group-not-saved':
      return t('lots.story.groupPreflight.reason.targetGroupNotSaved')
    default:
      return reason
  }
}

const getGroupDeliveryBlockingReasonLabel = (
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  reason: 'no-target-group-selected' | 'target-group-not-saved' | 'group-read-not-allowed'
): string => {
  switch (reason) {
    case 'no-target-group-selected':
      return t('lots.story.groupDelivery.reason.noTargetGroupSelected')
    case 'target-group-not-saved':
      return t('lots.story.groupDelivery.reason.targetGroupNotSaved')
    case 'group-read-not-allowed':
      return t('lots.story.groupDelivery.reason.groupReadNotAllowed')
    default:
      return reason
  }
}

const resolveGroupShareReviewPreflightStatus = (args: {
  loading: boolean
  error: string | null
  record: LotStoryGroupShareReviewPreflightRecord | null
}): LotStoryGroupShareReviewPreflightStatus => {
  if (args.loading && !args.record) {
    return 'loading'
  }

  if (args.error) {
    return 'error'
  }

  if (!args.record) {
    return 'idle'
  }

  return args.record.groupShareReviewReady ? 'allowed' : 'denied'
}

const getGroupShareReviewBlockingReasonLabel = (
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  reason: LotStoryGroupShareReviewPreflightBlockingReason
): string => {
  switch (reason) {
    case 'artifact-not-published':
      return t('lots.story.groupShareReview.reason.artifactNotPublished')
    case 'artifact-readiness-blocked':
      return t('lots.story.groupShareReview.reason.artifactReadinessBlocked')
    case 'no-active-group-context':
      return t('lots.story.groupShareReview.reason.noActiveGroupContext')
    case 'no-target-group-selected':
      return t('lots.story.groupShareReview.reason.noTargetGroupSelected')
    case 'target-group-not-eligible':
      return t('lots.story.groupShareReview.reason.targetGroupNotEligible')
    case 'target-group-not-saved':
      return t('lots.story.groupShareReview.reason.targetGroupNotSaved')
    default:
      return reason
  }
}

const getGroupShareApprovalBlockingReasonLabel = (
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  reason: LotStoryGroupShareApprovalPreflightBlockingReason
): string => {
  switch (reason) {
    case 'review-not-recorded':
      return t('lots.story.groupShareReview.approvalReason.reviewNotRecorded')
    default:
      return getGroupShareReviewBlockingReasonLabel(t, reason)
  }
}

const getGroupShareReleaseGateBlockingReasonLabel = (
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  reason: LotStoryGroupShareReleaseGatePreflightBlockingReason
): string => {
  switch (reason) {
    case 'approval-not-recorded':
      return t('lots.story.groupShareReview.releaseGateReason.approvalNotRecorded')
    default:
      return getGroupShareApprovalBlockingReasonLabel(t, reason)
  }
}

const getGroupSharePromotionBlockingReasonLabel = (
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  reason: LotStoryGroupSharePromotionPreflightBlockingReason
): string => {
  switch (reason) {
    case 'release-gate-not-recorded':
      return t('lots.story.groupShareReview.promotionReason.releaseGateNotRecorded')
    case 'artifact-already-group-visible':
      return t('lots.story.groupShareReview.promotionReason.alreadyGroupVisible')
    default:
      return getGroupShareReleaseGateBlockingReasonLabel(t, reason)
  }
}

const formatGroupDeliveryReceiptTimestamp = (value?: string | null): string => {
  const trimmed = value?.trim()
  if (!trimmed) return ''

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toLocaleString()
}

const STORY_GROUP_DELIVERY_RECEIPT_PAGE_SIZE = 2

const buildGroupDeliveryReceiptHistoryKey = (
  receipt: LotStoryGroupDeliveryReceiptRecord['receiptHistory'][number]
): string => [
  receipt.viewerContext.tenantMembershipId ?? receipt.viewerContext.actorId ?? 'receipt',
  receipt.viewedAt,
  receipt.currentPublishedVersionId ?? '',
  receipt.grantedGroupId ?? ''
].join('::')

const mergeGroupDeliveryReceiptRecord = (
  current: LotStoryGroupDeliveryReceiptRecord | null,
  next: LotStoryGroupDeliveryReceiptRecord
): LotStoryGroupDeliveryReceiptRecord => {
  if (!current) {
    return next
  }

  const mergedReceiptHistory = [...current.receiptHistory]
  const seenReceiptKeys = new Set(mergedReceiptHistory.map((receipt) => buildGroupDeliveryReceiptHistoryKey(receipt)))

  next.receiptHistory.forEach((receipt) => {
    const receiptKey = buildGroupDeliveryReceiptHistoryKey(receipt)
    if (seenReceiptKeys.has(receiptKey)) {
      return
    }

    seenReceiptKeys.add(receiptKey)
    mergedReceiptHistory.push(receipt)
  })

  return {
    ...next,
    latestReceipt: current.latestReceipt ?? next.latestReceipt,
    receiptHistory: mergedReceiptHistory
  }
}

// PR-alpha4a U8+U10: `handleDelete` two-step confirm (finding #83). Pure
// decision function extracted (mirroring `resolveHarvestTransitionOutcome`
// in Biomass.tsx) so the guard-clause ordering + arm/apply transition is
// unit-testable without mounting the full `Lots` screen, which has a large
// data-bootstrap surface unrelated to this decision.
export type LotDeleteAction = 'blockedReadonly' | 'blockedProcessingPlant' | 'arm' | 'apply'

export const resolveLotDeleteAction = (
  canEditLots: boolean,
  selectedFacilityIsProcessingPlant: boolean,
  isConfirmArmed: boolean
): LotDeleteAction => {
  if (!canEditLots) return 'blockedReadonly'
  if (selectedFacilityIsProcessingPlant) return 'blockedProcessingPlant'
  return isConfirmArmed ? 'apply' : 'arm'
}

export default function Lots() {
  const t = useT()
  const location = useLocation()
  const principal = useStoredPrincipal()
  const canEditLots = !principal || principal.role === 'editor' || principal.role === 'admin'
  const canDraftLotStories = !principal || principal.role === 'contributor' || principal.role === 'editor' || principal.role === 'admin'
  const canRevokeLotStories = canDraftLotStories
  const initialActiveContext = useMemo(() => loadActiveContextSelection(), [])
  const routeSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const linkedWorkspaceMode = routeSearchParams.get('workspace')?.trim() === 'stories' ? 'stories' : null
  const linkedStoryArtifactId = routeSearchParams.get('storyArtifactId')?.trim() ?? ''
  const linkedStoryLineageRootId = routeSearchParams.get('lineageRootId')?.trim() ?? ''

  const [availableFarms, setAvailableFarms] = useState<WizardFarm[]>([])
  const [selectedFarmId, setSelectedFarmId] = useSessionState<string>('lots.selectedFarmId', initialActiveContext.farmId)
  const [selectedProductionUnitId, setSelectedProductionUnitId] = useSessionState<string>('lots.selectedProductionUnitId', initialActiveContext.productionUnitId)
  const [selectedProcessingSiteId, setSelectedProcessingSiteId] = useSessionState<string>('lots.selectedProcessingSiteId', '')
  const [workspaceMode, setWorkspaceMode] = useState<LotsWorkspaceMode>('operations')
  const [hasHydratedContext, setHasHydratedContext] = useState(false)

  const [lots, setLots] = useState<Lot[]>([])
  // Tratamientos v0 (findings #103)
  const [treatmentLot, setTreatmentLot] = useState<Lot | null>(null)
  const [treatmentRefreshKey, setTreatmentRefreshKey] = useState(0)
  const [loadingLots, setLoadingLots] = useState(false)
  const [lotsError, setLotsError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  // Findings #131: after "Sembrar en varias unidades", a link to record the
  // eggs' / seed cost for the whole lot (Costos, Genética, stocking date).
  const [stockedCostLink, setStockedCostLink] = useState<{ farmId: string; rootId: string; date: string; eggs: boolean; message: string } | null>(null)
  const [lotSyncStatus, setLotSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  // Mirror lotSyncStatus into a ref so applyLotMutationStatus can read the
  // freshest outbox state when reconciling the per-mutation toast (kills
  // v2.1 #24 contradictory label — same pattern as Biomass for #21).
  const lotSyncStatusRef = useRef<EntityBatchSyncStatus | null>(null)
  useEffect(() => {
    lotSyncStatusRef.current = lotSyncStatus
  }, [lotSyncStatus])
  const lotFormAnchorRef = useRef<HTMLFormElement | null>(null)
  const [lotSyncLastOutcome, setLotSyncLastOutcome] = useState<(LotSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const [retryingLotSync, setRetryingLotSync] = useState(false)
  const requestIdRef = useRef(0)
  const statusMessageVersionRef = useRef(0)
  // v2.1 finding #61 (WSL-09, 2026-07-01): forward-ref indirection so
  // `applyLotMutationStatus` (declared below `refreshCurrentLotSyncStatus`
  // but referenced from event handlers that fire long after render) can
  // trigger a post-drain outbox-status refresh from its `syncPromise.then`
  // callback. The `useEffect` below `refreshCurrentLotSyncStatus` keeps
  // the ref pointed at a closure that re-reads the CURRENT
  // `displayedLotIds` snapshot — which is exactly the id set the outbox
  // banner reads from.
  const refreshLotSyncStatusFnRef = useRef<() => Promise<EntityBatchSyncStatus | null>>(async () => null)
  const [selectedHistoryLotId, setSelectedHistoryLotId] = useState<string | null>(null)
  const [selectedHistoryLotLabel, setSelectedHistoryLotLabel] = useState('')
  const [historyEvents, setHistoryEvents] = useState<LedgerLotEvent[]>([])
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const historyRequestIdRef = useRef(0)
  const [processingSiteActivityEntries, setProcessingSiteActivityEntries] = useState<ProcessingSiteActivityEntry[]>([])
  const [processingSiteActivityFilter, setProcessingSiteActivityFilter] = useState<'all' | 'transfer' | 'move'>('all')
  const [processingSiteDisplayedLotProvenanceByLotId, setProcessingSiteDisplayedLotProvenanceByLotId] = useState<Record<string, ProcessingSiteRecentLotProvenance>>({})
  const [processingSiteDisplayedLotProvenanceEntriesByLotId, setProcessingSiteDisplayedLotProvenanceEntriesByLotId] = useState<Record<string, ProcessingSiteRecentLotProvenance[]>>({})
  const processingSiteDisplayedLotProvenanceRequestIdRef = useRef(0)
  const [selectedStoryLineageRootId, setSelectedStoryLineageRootId] = useState<string | null>(null)
  const [selectedStoryLotLabel, setSelectedStoryLotLabel] = useState('')
  const [storyPreview, setStoryPreview] = useState<LotStoryProjectionV1 | null>(null)
  const [storyPreviewError, setStoryPreviewError] = useState<string | null>(null)
  const [storyPreviewLoading, setStoryPreviewLoading] = useState(false)
  const [selectedStoryPreviewVersion, setSelectedStoryPreviewVersion] = useState<LotStoryArtifactVersionV1 | null>(null)
  const storyPreviewRequestIdRef = useRef(0)
  const [storyActorContext, setStoryActorContext] = useState<CommunityActorContextRecord | null>(null)
  const [storyActorContextError, setStoryActorContextError] = useState<string | null>(null)
  const [storyActorContextLoading, setStoryActorContextLoading] = useState(false)
  const storyActorContextRequestIdRef = useRef(0)
  const [storyDraftTitle, setStoryDraftTitle] = useState('')
  const [storyDraftSummary, setStoryDraftSummary] = useState('')
  const [storyDraftNotes, setStoryDraftNotes] = useState('')
  const [storyDraftSaving, setStoryDraftSaving] = useState(false)
  const [storyPublishing, setStoryPublishing] = useState(false)
  const [storyRevoking, setStoryRevoking] = useState(false)
  const [storyTimelineFilter, setStoryTimelineFilter] = useState<StoryTimelineFilter>('all')
  const [storyDraftError, setStoryDraftError] = useState<string | null>(null)
  const [savedStoryDraft, setSavedStoryDraft] = useState<LotStoryArtifactBundleV1 | null>(null)
  const [storyLibrary, setStoryLibrary] = useState<LotStoryArtifactBundleV1[]>([])
  const [storyLibraryLoading, setStoryLibraryLoading] = useState(false)
  const [storyLibraryError, setStoryLibraryError] = useState<string | null>(null)
  const [selectedStoryArtifact, setSelectedStoryArtifact] = useState<LotStoryArtifactDetailV1 | null>(null)
  const [selectedStoryArtifactLoading, setSelectedStoryArtifactLoading] = useState(false)
  const [selectedStoryArtifactError, setSelectedStoryArtifactError] = useState<string | null>(null)
  const [selectedStoryGroupReadGroupId, setSelectedStoryGroupReadGroupId] = useState('')
  const [storyGroupShareIntentSaving, setStoryGroupShareIntentSaving] = useState(false)
  const [storyGroupShareIntentStatus, setStoryGroupShareIntentStatus] = useState<string | null>(null)
  const [storyGroupShareIntentError, setStoryGroupShareIntentError] = useState<string | null>(null)
  const [storyGroupShareReviewRecording, setStoryGroupShareReviewRecording] = useState(false)
  const [storyGroupShareReviewStatus, setStoryGroupShareReviewStatus] = useState<string | null>(null)
  const [storyGroupShareReviewActionError, setStoryGroupShareReviewActionError] = useState<string | null>(null)
  const [storyGroupShareApprovalRecording, setStoryGroupShareApprovalRecording] = useState(false)
  const [storyGroupShareApprovalStatus, setStoryGroupShareApprovalStatus] = useState<string | null>(null)
  const [storyGroupShareApprovalActionError, setStoryGroupShareApprovalActionError] = useState<string | null>(null)
  const [storyGroupShareReleaseGateRecording, setStoryGroupShareReleaseGateRecording] = useState(false)
  const [storyGroupShareReleaseGateStatus, setStoryGroupShareReleaseGateStatus] = useState<string | null>(null)
  const [storyGroupShareReleaseGateActionError, setStoryGroupShareReleaseGateActionError] = useState<string | null>(null)
  const [storyGroupPromotionRecording, setStoryGroupPromotionRecording] = useState(false)
  const [storyGroupPromotionStatus, setStoryGroupPromotionStatus] = useState<string | null>(null)
  const [storyGroupPromotionActionError, setStoryGroupPromotionActionError] = useState<string | null>(null)
  const [storyGroupReadPreflightRecord, setStoryGroupReadPreflightRecord] = useState<LotStoryGroupReadPreflightRecord | null>(null)
  const [storyGroupReadPreflightError, setStoryGroupReadPreflightError] = useState<string | null>(null)
  const [storyGroupReadPreflightLoading, setStoryGroupReadPreflightLoading] = useState(false)
  const [storyGroupDeliveryRecord, setStoryGroupDeliveryRecord] = useState<LotStoryGroupDeliveryRecord | null>(null)
  const [storyGroupDeliveryError, setStoryGroupDeliveryError] = useState<string | null>(null)
  const [storyGroupDeliveryLoading, setStoryGroupDeliveryLoading] = useState(false)
  const [storyGroupDeliveryReceiptRecord, setStoryGroupDeliveryReceiptRecord] = useState<LotStoryGroupDeliveryReceiptRecord | null>(null)
  const [storyGroupDeliveryReceiptError, setStoryGroupDeliveryReceiptError] = useState<string | null>(null)
  const [storyGroupDeliveryReceiptLoading, setStoryGroupDeliveryReceiptLoading] = useState(false)
  const [storyGroupDeliveryReceiptLoadingMore, setStoryGroupDeliveryReceiptLoadingMore] = useState(false)
  const [storyGroupDeliveryReceiptLoadMoreError, setStoryGroupDeliveryReceiptLoadMoreError] = useState<string | null>(null)
  const [storyGroupShareReviewPreflightRecord, setStoryGroupShareReviewPreflightRecord] = useState<LotStoryGroupShareReviewPreflightRecord | null>(null)
  const [storyGroupShareReviewPreflightError, setStoryGroupShareReviewPreflightError] = useState<string | null>(null)
  const [storyGroupShareReviewPreflightLoading, setStoryGroupShareReviewPreflightLoading] = useState(false)
  const storyLibraryRequestIdRef = useRef(0)
  const storyArtifactDetailRequestIdRef = useRef(0)
  const storyGroupReadPreflightRequestIdRef = useRef(0)
  const storyGroupDeliveryRequestIdRef = useRef(0)
  const storyGroupDeliveryReceiptRequestIdRef = useRef(0)
  const storyGroupShareReviewPreflightRequestIdRef = useRef(0)
  const selectedStoryArtifactIdRef = useRef<string | null>(null)
  const [ledgerLineageSummaryByLotId, setLedgerLineageSummaryByLotId] = useState<Record<string, LotLineageSummary>>({})
  const lineageSummaryRequestIdRef = useRef(0)

  const [form, setForm] = useState<LotFormState>(() => blankForm('', ''))
  const [formIntent, setFormIntent] = useState<LotFormIntent>('default')
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Findings #178 / #183: Trasladar lote — when the fish moved, and how many join the same lot in the destination.
  const [moveAt, setMoveAt] = useState('')
  const [fishMoved, setFishMoved] = useState('')
  const [fishMoveStocks, setFishMoveStocks] = useState<FishMoveStocks | null>(null)
  // Findings #179: "Cantidad que llega" when the whole lot goes to a unit without that lot.
  const [arrivalCount, setArrivalCount] = useState('')
  // Findings #204: "Murieron en el traslado".
  const [diedInMove, setDiedInMove] = useState('')
  // Findings #210: the reduced ration after the move (Protocolo de traslado).
  const [transferRationDraft, setTransferRationDraft] = useState<TransferRationDraft | null>(null)
  // Findings #191: the destination unit's own lots — the screen list follows the Lotes unit filter.
  const [destinationUnitLots, setDestinationUnitLots] = useState<{ unitId: string; lots: Lot[] } | null>(null)
  const [destinationSearchQuery, setDestinationSearchQuery] = useState('')

  // Phase 3 UX redesign: split/merge lineage tools move from an inline
  // `<details>` card into a slide-out drawer, opened on demand.
  const [isLineageDrawerOpen, setIsLineageDrawerOpen] = useState(false)
  // findings #67 / #71: "Sembrar en varias unidades" drawer.
  const [isStockIntoUnitsOpen, setIsStockIntoUnitsOpen] = useState(false)
  // Office UX v0 (findings #95): list filter, lot detail tabs and the
  // create / edit / move form panel ("Nuevo lote" asks ¿Peces u ovas? first).
  const [lotListFilter, setLotListFilter] = useSessionState<LotListFilter>('lots.listFilter', 'Active')
  const [lotSearchQuery, setLotSearchQuery] = useState('')
  const [lotDetailTab, setLotDetailTab] = useState<LotDetailTab>('summary')
  const [lotFormPanelOpen, setLotFormPanelOpen] = useState(false)
  const [newLotKind, setNewLotKind] = useState<'fish' | 'eggs' | null>(null)
  // Capacidad de carga for a new lot (findings #151); '' = the facility default.
  const [newLotCapacityId, setNewLotCapacityId] = useState('')
  const [eggArrivalDraft, setEggArrivalDraft] = useState<ArrivalDraft>(() => emptyArrivalDraft(''))

  const [splitSourceLotId, setSplitSourceLotId] = useState('')
  const [splitSourceSearchQuery, setSplitSourceSearchQuery] = useState('')
  const [splitChildKeyA, setSplitChildKeyA] = useState('')
  const [splitChildKeyB, setSplitChildKeyB] = useState('')
  const [splitRecordedAt, setSplitRecordedAt] = useState(() => new Date().toISOString())
  const [splitNotes, setSplitNotes] = useState('')
  const [splitError, setSplitError] = useState<string | null>(null)
  const [splitSaving, setSplitSaving] = useState(false)

  const [mergeSourceLotIds, setMergeSourceLotIds] = useState<string[]>([])
  const [mergeSourceSearchQuery, setMergeSourceSearchQuery] = useState('')
  const [isMergeReviewExpanded, setIsMergeReviewExpanded] = useState(false)
  const [mergeTargetKey, setMergeTargetKey] = useState('')
  const [mergeRecordedAt, setMergeRecordedAt] = useState(() => new Date().toISOString())
  const [mergeNotes, setMergeNotes] = useState('')
  const [mergeMarkInactive, setMergeMarkInactive] = useState(false)
  const [mergeError, setMergeError] = useState<string | null>(null)
  const [mergeSaving, setMergeSaving] = useState(false)
  const isHydratingContext = !hasHydratedContext

  useEffect(() => {
    let cancelled = false

    const loadFarms = async () => {
      try {
        const farms = await ensureFarmList()
        if (cancelled) return
        setAvailableFarms(farms)

        const activeFarmId = initialActiveContext.farmId
        const nextFarmId = (
          activeFarmId && farms.some((farm) => farm.id === activeFarmId)
            ? activeFarmId
            : (selectedFarmId && farms.some((farm) => farm.id === selectedFarmId))
              ? selectedFarmId
              : (farms[0]?.id ?? '')
        )

        if (nextFarmId && nextFarmId !== selectedFarmId) {
          setSelectedFarmId(nextFarmId)
        }

        const farm = nextFarmId ? findFarmById(farms, nextFarmId) : undefined
        const units = farm?.productionUnits ?? []
        const nextUnitId = resolveHydratedProductionUnitId({
          activeProductionUnitId: initialActiveContext.productionUnitId,
          currentProductionUnitId: selectedProductionUnitId,
          units
        })

        if (nextUnitId !== selectedProductionUnitId) {
          setSelectedProductionUnitId(nextUnitId)
        }

        setHasHydratedContext(true)
      } catch (error) {
        console.error('Lots: failed to load farms', error)
      }
    }

    void loadFarms()

    return () => {
      cancelled = true
    }
  }, [setSelectedFarmId])

  useEffect(() => {
    if (!hasHydratedContext) return
    if (!selectedFarmId) return
    persistActiveContextSelection({ farmId: selectedFarmId })
  }, [hasHydratedContext, selectedFarmId])

  useEffect(() => {
    if (!hasHydratedContext) return
    persistActiveContextSelection({ productionUnitId: selectedProductionUnitId })
  }, [hasHydratedContext, selectedProductionUnitId])

  const selectedFarm = useMemo(
    () => findFarmById(availableFarms, selectedFarmId),
    [availableFarms, selectedFarmId]
  )

  const selectedFacilityIsProcessingPlant = selectedFarm?.facilityType === 'processing-plant'

  const {
    lots: lineageLots,
    loading: loadingLineageLots,
    refresh: refreshLineageLots
  } = useLots({
    farmId: selectedFarmId || undefined,
    enabled: Boolean(selectedFarmId)
  })

  const lineageSourceLots = useMemo(() => {
    if (selectedFarmId) {
      return lineageLots
    }
    return lots
  }, [lineageLots, lots, selectedFarmId])

  const lotKeyById = useMemo(() => {
    const map = new Map<string, string>()
    lineageSourceLots.forEach((lot) => {
      map.set(lot.id, lot.lotKey ?? lot.id)
    })
    lots.forEach((lot) => {
      map.set(lot.id, lot.lotKey ?? lot.id)
    })
    return map
  }, [lineageSourceLots, lots])

  const lotById = useMemo(() => {
    const map = new Map<string, Lot>()
    lineageSourceLots.forEach((lot) => {
      map.set(lot.id, lot)
    })
    lots.forEach((lot) => {
      map.set(lot.id, lot)
    })
    return map
  }, [lineageSourceLots, lots])

  const selectedHistoryLotDisplayLabel = useMemo(() => {
    if (!selectedHistoryLotId) return ''
    return selectedHistoryLotLabel || lotKeyById.get(selectedHistoryLotId) || selectedHistoryLotId
  }, [lotKeyById, selectedHistoryLotId, selectedHistoryLotLabel])

  const selectedHistoryLot = useMemo(() => {
    if (!selectedHistoryLotId) return null
    return lots.find((lot) => lot.id === selectedHistoryLotId)
      ?? lineageSourceLots.find((lot) => lot.id === selectedHistoryLotId)
      ?? null
  }, [lineageSourceLots, lots, selectedHistoryLotId])

  const selectedStoryLotDisplayLabel = useMemo(() => {
    if (!selectedStoryLineageRootId) return ''
    return selectedStoryLotLabel || lotKeyById.get(selectedStoryLineageRootId) || selectedStoryLineageRootId
  }, [lotKeyById, selectedStoryLineageRootId, selectedStoryLotLabel])

  const isStoryWorkspaceActive = workspaceMode === 'stories'
  const isHistoricalStoryPreview = selectedStoryPreviewVersion !== null
  const storyPrivateWriteAllowed = canMembershipWritePrivateLotStory(storyActorContext?.membershipRole)
  const storyPrivateWritePreflightState: LotStoryPrivateWritePreflightStatus = !canDraftLotStories
    ? 'readonly'
    : storyActorContextLoading && !storyActorContext
      ? 'loading'
      : storyActorContextError
        ? 'error'
        : storyActorContext
          ? storyPrivateWriteAllowed
            ? 'allowed'
            : 'denied'
          : 'idle'
  const storyPrivateIdentityLabel = storyActorContext?.communityDisplayName?.trim() || storyActorContext?.accountDisplayName || ''
  const storyPrivateRoleLabel = storyActorContext
    ? t(`role.${storyActorContext.membershipRole}`)
    : ''
  const storyPrivatePartnerOrganizationsLabel = useMemo(() => {
    if (!storyActorContext) return ''

    return uniqueStrings(
      storyActorContext.groupActorContexts
        .filter((context) => context.membershipStatus === 'active')
        .map((context) => context.partnerOrganizationName ?? '')
    ).join(', ')
  }, [storyActorContext])
  const storyPrivateWritePreflight = useMemo<LotStoryPrivateWritePreflightView>(() => ({
    status: storyPrivateWritePreflightState,
    identityLabel: storyPrivateIdentityLabel,
    roleLabel: storyPrivateRoleLabel,
    activeGroupCount: storyActorContext?.activeGroupContextCount ?? 0,
    partnerOrganizationsLabel: storyPrivatePartnerOrganizationsLabel,
    errorMessage: storyActorContextError
  }), [
    storyActorContext?.activeGroupContextCount,
    storyActorContextError,
    storyPrivateIdentityLabel,
    storyPrivatePartnerOrganizationsLabel,
    storyPrivateRoleLabel,
    storyPrivateWritePreflightState
  ])
  const storyGroupReadPreflightState = resolveGroupReadPreflightStatus({
    loading: storyGroupReadPreflightLoading,
    error: storyGroupReadPreflightError,
    record: storyGroupReadPreflightRecord
  })
  const storyGroupReadIdentityLabel = storyGroupReadPreflightRecord?.actorContext.communityDisplayName?.trim() || storyGroupReadPreflightRecord?.actorContext.accountDisplayName || ''
  const storyGroupReadRoleLabel = storyGroupReadPreflightRecord
    ? t(`role.${storyGroupReadPreflightRecord.actorContext.membershipRole}`)
    : ''
  const storyGroupReadTargetOptions = useMemo(() => {
    if (!storyGroupReadPreflightRecord) return []

    return storyGroupReadPreflightRecord.eligibleGroupActorContexts.map((context) => ({
      groupId: context.groupId,
      label: context.groupDisplayName
    }))
  }, [storyGroupReadPreflightRecord])
  const storyGroupReadSelectedGroupLabel = useMemo(() => {
    if (selectedStoryGroupReadGroupId) {
      const matchedOption = storyGroupReadTargetOptions.find((option) => option.groupId === selectedStoryGroupReadGroupId)
      if (matchedOption) {
        return matchedOption.label
      }

      return storyGroupReadPreflightRecord?.selectedGroupDisplayName
        ?? storyGroupReadPreflightRecord?.selectedGroupContext?.groupDisplayName
        ?? selectedStoryGroupReadGroupId
    }

    return storyGroupReadPreflightRecord?.selectedGroupDisplayName
      ?? storyGroupReadPreflightRecord?.selectedGroupContext?.groupDisplayName
      ?? ''
  }, [selectedStoryGroupReadGroupId, storyGroupReadPreflightRecord, storyGroupReadTargetOptions])
  const savedStoryGroupShareIntentId = selectedStoryArtifact?.artifact.groupId?.trim() ?? ''
  const savedStoryGroupShareIntentLabel = useMemo(() => {
    if (!savedStoryGroupShareIntentId) return ''

    const matchedOption = storyGroupReadTargetOptions.find((option) => option.groupId === savedStoryGroupShareIntentId)
    if (matchedOption) {
      return matchedOption.label
    }

    const readableLabel = storyGroupReadPreflightRecord?.savedGroupDisplayName
      ?? storyGroupShareReviewPreflightRecord?.savedGroupDisplayName
    if (readableLabel) {
      return readableLabel
    }

    return savedStoryGroupShareIntentId
  }, [savedStoryGroupShareIntentId, storyGroupReadPreflightRecord?.savedGroupDisplayName, storyGroupShareReviewPreflightRecord?.savedGroupDisplayName, storyGroupReadTargetOptions])
  const storyGroupReadPartnerOrganizationsLabel = useMemo(() => {
    if (!storyGroupReadPreflightRecord) return ''

    return uniqueStrings(
      (storyGroupReadPreflightRecord.selectedGroupContext
        ? [storyGroupReadPreflightRecord.selectedGroupContext]
        : storyGroupReadPreflightRecord.eligibleGroupActorContexts
      ).map((context) => context.partnerOrganizationName ?? '')
    ).join(', ')
  }, [storyGroupReadPreflightRecord])
  const storyGroupReadBlockingReasonLabels = useMemo(() => {
    if (!storyGroupReadPreflightRecord) return []

    return storyGroupReadPreflightRecord.blockingReasons.map((reason) => getGroupReadBlockingReasonLabel(t, reason))
  }, [storyGroupReadPreflightRecord, t])
  const storyGroupReadPreflight = useMemo<LotStoryGroupReadPreflightView>(() => ({
    status: storyGroupReadPreflightState,
    identityLabel: storyGroupReadIdentityLabel,
    roleLabel: storyGroupReadRoleLabel,
    selectedGroupId: selectedStoryGroupReadGroupId,
    selectedGroupLabel: storyGroupReadSelectedGroupLabel,
    groupOptions: storyGroupReadTargetOptions,
    artifactVisibilityLabel: storyGroupReadPreflightRecord
      ? t(`lots.story.library.visibility.${storyGroupReadPreflightRecord.artifactVisibility}`)
      : '',
    eligibleGroupCount: storyGroupReadPreflightRecord?.eligibleGroupContextCount ?? 0,
    partnerOrganizationsLabel: storyGroupReadPartnerOrganizationsLabel,
    blockingReasonLabels: storyGroupReadBlockingReasonLabels,
    errorMessage: storyGroupReadPreflightError
  }), [
    storyGroupReadBlockingReasonLabels,
    storyGroupReadIdentityLabel,
    storyGroupReadPartnerOrganizationsLabel,
    storyGroupReadPreflightError,
    storyGroupReadPreflightRecord,
    storyGroupReadPreflightState,
    storyGroupReadRoleLabel,
    storyGroupReadSelectedGroupLabel,
    storyGroupReadTargetOptions,
    selectedStoryGroupReadGroupId,
    t
  ])
  const isStoryGroupVisible = selectedStoryArtifact?.artifact.visibility === 'group'
  const storyGroupDeliveryReady = Boolean(
    selectedStoryArtifact?.artifact.artifactId?.trim()
    && isStoryGroupVisible
    && savedStoryGroupShareIntentId
    && selectedStoryGroupReadGroupId
    && selectedStoryGroupReadGroupId === savedStoryGroupShareIntentId
    && storyGroupReadPreflightState === 'allowed'
  )
  const storyGroupDeliveryBlockingReasonLabels = useMemo(() => {
    if (!isStoryGroupVisible) return []

    const reasons: Array<'no-target-group-selected' | 'target-group-not-saved' | 'group-read-not-allowed'> = []

    if (!selectedStoryGroupReadGroupId) {
      reasons.push('no-target-group-selected')
    } else if (savedStoryGroupShareIntentId && selectedStoryGroupReadGroupId !== savedStoryGroupShareIntentId) {
      reasons.push('target-group-not-saved')
    } else if (storyGroupReadPreflightState !== 'allowed') {
      reasons.push('group-read-not-allowed')
    }

    return reasons.map((reason) => getGroupDeliveryBlockingReasonLabel(t, reason))
  }, [isStoryGroupVisible, savedStoryGroupShareIntentId, selectedStoryGroupReadGroupId, storyGroupReadPreflightState, t])
  const storyGroupDeliveryPublishedAtLabel = useMemo(() => {
    const publishedAt = storyGroupDeliveryRecord?.version.publishedAt?.trim()
    if (!publishedAt) return ''

    const parsed = new Date(publishedAt)
    return Number.isNaN(parsed.getTime()) ? publishedAt : parsed.toLocaleString()
  }, [storyGroupDeliveryRecord?.version.publishedAt])
  const storyGroupDeliveryPublishedByLabel = useMemo(() => {
    return resolveStoredActorContextLabel(
      storyGroupDeliveryRecord?.version.publishedByContext,
      storyGroupReadPreflightRecord?.actorContext.tenantMembershipId,
      storyGroupReadIdentityLabel
    )
  }, [storyGroupDeliveryRecord?.version.publishedByContext, storyGroupReadIdentityLabel, storyGroupReadPreflightRecord?.actorContext.tenantMembershipId])
  const storyGroupDeliveryGrantedGroupRoleLabel = useMemo(() => {
    const groupRole = storyGroupDeliveryRecord?.deliveryContext.grantedGroupContext.groupRole
    return groupRole ? t(`ownerConsole.groups.role.${groupRole}`) : ''
  }, [storyGroupDeliveryRecord?.deliveryContext.grantedGroupContext.groupRole, t])
  const storyGroupDeliveryReceiptViewedAtLabel = useMemo(() => {
    return formatGroupDeliveryReceiptTimestamp(storyGroupDeliveryReceiptRecord?.latestReceipt?.viewedAt)
  }, [storyGroupDeliveryReceiptRecord?.latestReceipt?.viewedAt])
  const storyGroupDeliveryReceiptViewerLabel = useMemo(() => {
    return resolveGroupDeliveryReceiptViewerLabel(storyGroupDeliveryReceiptRecord?.latestReceipt)
  }, [storyGroupDeliveryReceiptRecord?.latestReceipt])
  const storyGroupDeliveryReceiptGrantedGroupRoleLabel = useMemo(() => {
    const groupRole = storyGroupDeliveryReceiptRecord?.latestReceipt?.grantedGroupRole
    return groupRole ? t(`ownerConsole.groups.role.${groupRole}` as TranslationKey) : ''
  }, [storyGroupDeliveryReceiptRecord?.latestReceipt?.grantedGroupRole, t])
  const storyGroupDeliveryEarlierReceiptHistory = useMemo(() => (
    storyGroupDeliveryReceiptRecord?.receiptHistory.slice(1) ?? []
  ), [storyGroupDeliveryReceiptRecord?.receiptHistory])
  const storyGroupDeliveryState = resolveGroupDeliveryStatus({
    loading: storyGroupDeliveryLoading,
    error: storyGroupDeliveryError,
    record: storyGroupDeliveryRecord,
    visible: isStoryGroupVisible
  })
  const storyGroupDelivery = useMemo<LotStoryGroupDeliveryView>(() => ({
    status: storyGroupDeliveryState,
    identityLabel: storyGroupReadIdentityLabel,
    roleLabel: storyGroupReadRoleLabel,
    selectedGroupLabel: storyGroupReadSelectedGroupLabel,
    savedGroupLabel: storyGroupDeliveryRecord?.deliveryContext.grantedGroupContext.groupDisplayName ?? savedStoryGroupShareIntentLabel,
    selectedGroupMatchesSavedIntent: Boolean(selectedStoryGroupReadGroupId && savedStoryGroupShareIntentId && selectedStoryGroupReadGroupId === savedStoryGroupShareIntentId),
    partnerOrganizationsLabel: storyGroupReadPartnerOrganizationsLabel,
    grantedGroupLabel: storyGroupDeliveryRecord?.deliveryContext.grantedGroupContext.groupDisplayName ?? savedStoryGroupShareIntentLabel,
    grantedGroupRoleLabel: storyGroupDeliveryGrantedGroupRoleLabel,
    publishedAtLabel: storyGroupDeliveryPublishedAtLabel,
    publishedByLabel: storyGroupDeliveryPublishedByLabel,
    deliveryTitle: storyGroupDeliveryRecord?.version.title ?? '',
    deliverySummary: storyGroupDeliveryRecord?.version.summary?.trim() ?? '',
    deliveryNotes: storyGroupDeliveryRecord?.version.notes?.trim() ?? '',
    blockingReasonLabels: storyGroupDeliveryBlockingReasonLabels,
    projection: storyGroupDeliveryRecord?.version.projectionSnapshot ?? null,
    errorMessage: storyGroupDeliveryError
  }), [
    savedStoryGroupShareIntentId,
    savedStoryGroupShareIntentLabel,
    selectedStoryGroupReadGroupId,
    storyGroupDeliveryBlockingReasonLabels,
    storyGroupDeliveryPublishedByLabel,
    storyGroupDeliveryError,
    storyGroupDeliveryGrantedGroupRoleLabel,
    storyGroupDeliveryPublishedAtLabel,
    storyGroupDeliveryRecord,
    storyGroupDeliveryState,
    storyGroupReadIdentityLabel,
    storyGroupReadPartnerOrganizationsLabel,
    storyGroupReadRoleLabel,
    storyGroupReadSelectedGroupLabel
  ])
  const handleLoadMoreStoryGroupDeliveryReceiptHistory = useCallback(() => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()
    const historyCursor = storyGroupDeliveryReceiptRecord?.nextReceiptHistoryCursor?.trim()

    if (
      !artifactId
      || !storyGroupDeliveryReady
      || !storyPrivateWriteAllowed
      || !storyGroupDeliveryReceiptRecord?.hasMoreReceiptHistory
      || !historyCursor
      || storyGroupDeliveryReceiptLoadingMore
    ) {
      return
    }

    const currentRequestId = storyGroupDeliveryReceiptRequestIdRef.current
    setStoryGroupDeliveryReceiptLoadingMore(true)
    setStoryGroupDeliveryReceiptLoadMoreError(null)

    void lotApi.getStoryGroupDeliveryReceipt(artifactId, {
      historyCursor,
      historyLimit: STORY_GROUP_DELIVERY_RECEIPT_PAGE_SIZE
    })
      .then((receipt) => {
        if (storyGroupDeliveryReceiptRequestIdRef.current !== currentRequestId) return
        setStoryGroupDeliveryReceiptRecord((current) => mergeGroupDeliveryReceiptRecord(current, receipt))
      })
      .catch((error) => {
        if (storyGroupDeliveryReceiptRequestIdRef.current !== currentRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.groupDelivery.receipt.error.loadFailed')
        setStoryGroupDeliveryReceiptLoadMoreError(message)
      })
      .finally(() => {
        if (storyGroupDeliveryReceiptRequestIdRef.current === currentRequestId) {
          setStoryGroupDeliveryReceiptLoadingMore(false)
        }
      })
  }, [
    selectedStoryArtifact?.artifact.artifactId,
    storyGroupDeliveryReady,
    storyGroupDeliveryReceiptLoadingMore,
    storyGroupDeliveryReceiptRecord?.hasMoreReceiptHistory,
    storyGroupDeliveryReceiptRecord?.nextReceiptHistoryCursor,
    storyPrivateWriteAllowed,
    t
  ])
  const storyGroupDeliveryRenderedReceipt = useMemo(() => {
    if (!isStoryGroupVisible || !storyPrivateWriteAllowed) {
      return undefined
    }

    if (storyGroupDeliveryReceiptLoading) {
      return (
        <div data-testid="lot-story-library-group-delivery-receipt-status" style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
          {t('lots.story.groupDelivery.receipt.loading')}
        </div>
      )
    }

    if (storyGroupDeliveryReceiptError) {
      return (
        <div data-testid="lot-story-library-group-delivery-receipt-status" style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
          {storyGroupDeliveryReceiptError}
        </div>
      )
    }

    if (!storyGroupDeliveryReceiptRecord?.latestReceipt) {
      return (
        <div data-testid="lot-story-library-group-delivery-receipt-status" style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
          {t('lots.story.groupDelivery.receipt.empty')}
        </div>
      )
    }

    return (
      <div style={{ border: '1px solid #bbf7d0', borderRadius: 10, backgroundColor: '#ffffff', padding: 10, display: 'grid', gap: 6 }}>
        <strong style={{ fontSize: 12, color: '#166534' }}>{t('lots.story.groupDelivery.receipt.title')}</strong>
        <div data-testid="lot-story-library-group-delivery-receipt-viewed-by" style={{ fontSize: 13, color: '#166534' }}>
          <strong>{t('lots.story.groupDelivery.receipt.viewedBy')}:</strong> {storyGroupDeliveryReceiptViewerLabel || t('lots.story.groupPreflight.none')}
        </div>
        <div data-testid="lot-story-library-group-delivery-receipt-viewed-at" style={{ fontSize: 13, color: '#166534' }}>
          <strong>{t('lots.story.groupDelivery.receipt.viewedAt')}:</strong> {storyGroupDeliveryReceiptViewedAtLabel || t('lots.story.groupPreflight.none')}
        </div>
        <div data-testid="lot-story-library-group-delivery-receipt-granted-role" style={{ fontSize: 13, color: '#166534' }}>
          <strong>{t('lots.story.groupDelivery.receipt.grantedGroupRole')}:</strong> {storyGroupDeliveryReceiptGrantedGroupRoleLabel || t('lots.story.groupPreflight.none')}
        </div>
        {storyGroupDeliveryEarlierReceiptHistory.length > 0 ? (
          <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
            <strong style={{ fontSize: 12, color: '#166534' }}>{t('lots.story.groupDelivery.receipt.historyTitle')}</strong>
            <div style={{ display: 'grid', gap: 8 }}>
              {storyGroupDeliveryEarlierReceiptHistory.map((receipt, index) => {
                const grantedGroupRoleLabel = receipt.grantedGroupRole
                  ? t(`ownerConsole.groups.role.${receipt.grantedGroupRole}` as TranslationKey)
                  : ''

                return (
                  <div
                    key={`${receipt.viewerContext.tenantMembershipId ?? receipt.viewerContext.actorId ?? 'receipt'}-${receipt.viewedAt}-${index}`}
                    data-testid={`lot-story-library-group-delivery-receipt-history-item-${index}`}
                    style={{ border: '1px solid #dcfce7', borderRadius: 8, backgroundColor: '#f8fffb', padding: 8, display: 'grid', gap: 4 }}
                  >
                    <div data-testid={`lot-story-library-group-delivery-receipt-history-item-${index}-viewed-by`} style={{ fontSize: 13, color: '#166534' }}>
                      <strong>{t('lots.story.groupDelivery.receipt.viewedBy')}:</strong> {resolveGroupDeliveryReceiptViewerLabel(receipt) || t('lots.story.groupPreflight.none')}
                    </div>
                    <div data-testid={`lot-story-library-group-delivery-receipt-history-item-${index}-viewed-at`} style={{ fontSize: 13, color: '#166534' }}>
                      <strong>{t('lots.story.groupDelivery.receipt.viewedAt')}:</strong> {formatGroupDeliveryReceiptTimestamp(receipt.viewedAt) || t('lots.story.groupPreflight.none')}
                    </div>
                    <div data-testid={`lot-story-library-group-delivery-receipt-history-item-${index}-granted-role`} style={{ fontSize: 13, color: '#166534' }}>
                      <strong>{t('lots.story.groupDelivery.receipt.grantedGroupRole')}:</strong> {grantedGroupRoleLabel || t('lots.story.groupPreflight.none')}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
        {storyGroupDeliveryReceiptLoadMoreError ? (
          <div data-testid="lot-story-library-group-delivery-receipt-load-more-status" style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
            {storyGroupDeliveryReceiptLoadMoreError}
          </div>
        ) : null}
        {storyGroupDeliveryReceiptRecord.hasMoreReceiptHistory ? (
          <button
            data-testid="lot-story-library-group-delivery-receipt-load-more"
            type="button"
            onClick={() => void handleLoadMoreStoryGroupDeliveryReceiptHistory()}
            disabled={storyGroupDeliveryReceiptLoadingMore}
            style={{
              justifySelf: 'start',
              border: '1px solid #16a34a',
              borderRadius: 999,
              backgroundColor: storyGroupDeliveryReceiptLoadingMore ? '#dcfce7' : '#f0fdf4',
              color: '#166534',
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 10px',
              cursor: storyGroupDeliveryReceiptLoadingMore ? 'wait' : 'pointer'
            }}
          >
            {storyGroupDeliveryReceiptLoadingMore
              ? t('lots.story.groupDelivery.receipt.loadingMore')
              : t('lots.story.groupDelivery.receipt.loadMore')}
          </button>
        ) : null}
      </div>
    )
  }, [
    handleLoadMoreStoryGroupDeliveryReceiptHistory,
    storyGroupDeliveryEarlierReceiptHistory,
    isStoryGroupVisible,
    storyGroupDeliveryReceiptError,
    storyGroupDeliveryReceiptGrantedGroupRoleLabel,
    storyGroupDeliveryReceiptLoading,
    storyGroupDeliveryReceiptLoadingMore,
    storyGroupDeliveryReceiptLoadMoreError,
    storyGroupDeliveryReceiptRecord?.latestReceipt,
    storyGroupDeliveryReceiptRecord?.hasMoreReceiptHistory,
    storyGroupDeliveryReceiptViewedAtLabel,
    storyGroupDeliveryReceiptViewerLabel,
    storyPrivateWriteAllowed,
    t
  ])
  const storyGroupShareShell = useMemo<LotStoryGroupShareShellView>(() => ({
    selectedGroupLabel: storyGroupReadSelectedGroupLabel,
    savedGroupLabel: savedStoryGroupShareIntentLabel,
    canSave: selectedStoryGroupReadGroupId.trim().length > 0 && selectedStoryGroupReadGroupId !== savedStoryGroupShareIntentId,
    saving: storyGroupShareIntentSaving,
    statusMessage: storyGroupShareIntentStatus,
    errorMessage: storyGroupShareIntentError
  }), [
    savedStoryGroupShareIntentId,
    savedStoryGroupShareIntentLabel,
    selectedStoryGroupReadGroupId,
    storyGroupReadSelectedGroupLabel,
    storyGroupShareIntentError,
    storyGroupShareIntentSaving,
    storyGroupShareIntentStatus
  ])
  const storyGroupShareReviewPreflightState = resolveGroupShareReviewPreflightStatus({
    loading: storyGroupShareReviewPreflightLoading,
    error: storyGroupShareReviewPreflightError,
    record: storyGroupShareReviewPreflightRecord
  })
  const storyGroupShareReviewIdentityLabel = storyGroupShareReviewPreflightRecord?.actorContext.communityDisplayName?.trim()
    || storyGroupShareReviewPreflightRecord?.actorContext.accountDisplayName
    || ''
  const storyGroupShareReviewRoleLabel = storyGroupShareReviewPreflightRecord
    ? t(`role.${storyGroupShareReviewPreflightRecord.actorContext.membershipRole}`)
    : ''
  const storyGroupShareReviewPartnerOrganizationsLabel = useMemo(() => {
    if (!storyGroupShareReviewPreflightRecord) return ''

    return uniqueStrings(
      (storyGroupShareReviewPreflightRecord.selectedGroupContext
        ? [storyGroupShareReviewPreflightRecord.selectedGroupContext]
        : storyGroupShareReviewPreflightRecord.eligibleGroupActorContexts
      ).map((context) => context.partnerOrganizationName ?? '')
    ).join(', ')
  }, [storyGroupShareReviewPreflightRecord])
  const storyGroupShareReviewBlockingReasonLabels = useMemo(() => {
    if (!storyGroupShareReviewPreflightRecord) return []

    return storyGroupShareReviewPreflightRecord.blockingReasons.map((reason) => getGroupShareReviewBlockingReasonLabel(t, reason))
  }, [storyGroupShareReviewPreflightRecord, t])
  const storyGroupShareApprovalBlockingReasonLabels = useMemo(() => {
    if (!storyGroupShareReviewPreflightRecord) return []

    return storyGroupShareReviewPreflightRecord.approvalBlockingReasons.map((reason) => getGroupShareApprovalBlockingReasonLabel(t, reason))
  }, [storyGroupShareReviewPreflightRecord, t])
  const storyGroupShareReleaseGateBlockingReasonLabels = useMemo(() => {
    if (!storyGroupShareReviewPreflightRecord) return []

    return storyGroupShareReviewPreflightRecord.releaseGateBlockingReasons.map((reason) => getGroupShareReleaseGateBlockingReasonLabel(t, reason))
  }, [storyGroupShareReviewPreflightRecord, t])
  const storyGroupPromotionBlockingReasonLabels = useMemo(() => {
    if (!storyGroupShareReviewPreflightRecord) return []

    return storyGroupShareReviewPreflightRecord.promotionBlockingReasons.map((reason) => getGroupSharePromotionBlockingReasonLabel(t, reason))
  }, [storyGroupShareReviewPreflightRecord, t])
  const storyGroupShareReviewRecordedAtLabel = useMemo(() => {
    const recordedAt = storyGroupShareReviewPreflightRecord?.reviewRecordedAt?.trim()
    if (!recordedAt) return ''

    const parsed = new Date(recordedAt)
    return Number.isNaN(parsed.getTime()) ? recordedAt : parsed.toLocaleString()
  }, [storyGroupShareReviewPreflightRecord?.reviewRecordedAt])
  const storyGroupShareReviewRecordedByLabel = useMemo(() => {
    return resolveStoredActorContextLabel(
      storyGroupShareReviewPreflightRecord?.reviewRecordedByContext,
      storyGroupShareReviewPreflightRecord?.actorContext.tenantMembershipId,
      storyGroupShareReviewIdentityLabel
    )
  }, [storyGroupShareReviewIdentityLabel, storyGroupShareReviewPreflightRecord])
  const storyGroupShareApprovalRecordedAtLabel = useMemo(() => {
    const recordedAt = storyGroupShareReviewPreflightRecord?.approvalRecordedAt?.trim()
    if (!recordedAt) return ''

    const parsed = new Date(recordedAt)
    return Number.isNaN(parsed.getTime()) ? recordedAt : parsed.toLocaleString()
  }, [storyGroupShareReviewPreflightRecord?.approvalRecordedAt])
  const storyGroupShareApprovalRecordedByLabel = useMemo(() => {
    return resolveStoredActorContextLabel(
      storyGroupShareReviewPreflightRecord?.approvalRecordedByContext,
      storyGroupShareReviewPreflightRecord?.actorContext.tenantMembershipId,
      storyGroupShareReviewIdentityLabel
    )
  }, [storyGroupShareReviewIdentityLabel, storyGroupShareReviewPreflightRecord])
  const storyGroupShareReleaseGateRecordedAtLabel = useMemo(() => {
    const recordedAt = storyGroupShareReviewPreflightRecord?.releaseGateRecordedAt?.trim()
    if (!recordedAt) return ''

    const parsed = new Date(recordedAt)
    return Number.isNaN(parsed.getTime()) ? recordedAt : parsed.toLocaleString()
  }, [storyGroupShareReviewPreflightRecord?.releaseGateRecordedAt])
  const storyGroupShareReleaseGateRecordedByLabel = useMemo(() => {
    return resolveStoredActorContextLabel(
      storyGroupShareReviewPreflightRecord?.releaseGateRecordedByContext,
      storyGroupShareReviewPreflightRecord?.actorContext.tenantMembershipId,
      storyGroupShareReviewIdentityLabel
    )
  }, [storyGroupShareReviewIdentityLabel, storyGroupShareReviewPreflightRecord])
  const storyGroupPromotionRecordedAtLabel = useMemo(() => {
    const recordedAt = storyGroupShareReviewPreflightRecord?.promotedAt?.trim()
    if (!recordedAt) return ''

    const parsed = new Date(recordedAt)
    return Number.isNaN(parsed.getTime()) ? recordedAt : parsed.toLocaleString()
  }, [storyGroupShareReviewPreflightRecord?.promotedAt])
  const storyGroupPromotionRecordedByLabel = useMemo(() => {
    return resolveStoredActorContextLabel(
      storyGroupShareReviewPreflightRecord?.promotedByContext,
      storyGroupShareReviewPreflightRecord?.actorContext.tenantMembershipId,
      storyGroupShareReviewIdentityLabel
    )
  }, [storyGroupShareReviewIdentityLabel, storyGroupShareReviewPreflightRecord])
  const storyGroupShareReviewPreflight = useMemo<LotStoryGroupShareReviewPreflightView>(() => ({
    status: storyGroupShareReviewPreflightState,
    identityLabel: storyGroupShareReviewIdentityLabel,
    roleLabel: storyGroupShareReviewRoleLabel,
    selectedGroupLabel: storyGroupReadSelectedGroupLabel,
    savedGroupLabel: savedStoryGroupShareIntentLabel,
    selectedGroupMatchesSavedIntent: storyGroupShareReviewPreflightRecord?.selectedGroupMatchesSavedIntent ?? false,
    reviewRecorded: storyGroupShareReviewPreflightRecord?.reviewRecorded ?? false,
    reviewRecordedAtLabel: storyGroupShareReviewRecordedAtLabel,
    reviewRecordedByLabel: storyGroupShareReviewRecordedByLabel,
    canRecordReview: storyGroupShareReviewPreflightState === 'allowed' && !(storyGroupShareReviewPreflightRecord?.reviewRecorded ?? false),
    recording: storyGroupShareReviewRecording,
    actionStatusMessage: storyGroupShareReviewStatus,
    actionErrorMessage: storyGroupShareReviewActionError,
    approvalReady: storyGroupShareReviewPreflightRecord?.groupShareApprovalReady ?? false,
    approvalBlockingReasonLabels: storyGroupShareApprovalBlockingReasonLabels,
    approvalRecorded: storyGroupShareReviewPreflightRecord?.approvalRecorded ?? false,
    approvalRecordedAtLabel: storyGroupShareApprovalRecordedAtLabel,
    approvalRecordedByLabel: storyGroupShareApprovalRecordedByLabel,
    canRecordApproval: (storyGroupShareReviewPreflightRecord?.groupShareApprovalReady ?? false) && !(storyGroupShareReviewPreflightRecord?.approvalRecorded ?? false),
    approvalRecording: storyGroupShareApprovalRecording,
    approvalActionStatusMessage: storyGroupShareApprovalStatus,
    approvalActionErrorMessage: storyGroupShareApprovalActionError,
    releaseGateReady: storyGroupShareReviewPreflightRecord?.groupShareReleaseGateReady ?? false,
    releaseGateBlockingReasonLabels: storyGroupShareReleaseGateBlockingReasonLabels,
    releaseGateRecorded: storyGroupShareReviewPreflightRecord?.releaseGateRecorded ?? false,
    releaseGateRecordedAtLabel: storyGroupShareReleaseGateRecordedAtLabel,
    releaseGateRecordedByLabel: storyGroupShareReleaseGateRecordedByLabel,
    canRecordReleaseGate: (storyGroupShareReviewPreflightRecord?.groupShareReleaseGateReady ?? false) && !(storyGroupShareReviewPreflightRecord?.releaseGateRecorded ?? false),
    releaseGateRecording: storyGroupShareReleaseGateRecording,
    releaseGateActionStatusMessage: storyGroupShareReleaseGateStatus,
    releaseGateActionErrorMessage: storyGroupShareReleaseGateActionError,
    promotionReady: storyGroupShareReviewPreflightRecord?.groupSharePromotionReady ?? false,
    promotionBlockingReasonLabels: storyGroupPromotionBlockingReasonLabels,
    promotionRecorded: storyGroupShareReviewPreflightRecord?.promotionRecorded ?? false,
    promotedAtLabel: storyGroupPromotionRecordedAtLabel,
    promotedByLabel: storyGroupPromotionRecordedByLabel,
    canPromoteToGroup: (storyGroupShareReviewPreflightRecord?.groupSharePromotionReady ?? false) && !(storyGroupShareReviewPreflightRecord?.promotionRecorded ?? false),
    promotionRecording: storyGroupPromotionRecording,
    promotionActionStatusMessage: storyGroupPromotionStatus,
    promotionActionErrorMessage: storyGroupPromotionActionError,
    artifactVisibilityLabel: storyGroupShareReviewPreflightRecord
      ? t(`lots.story.library.visibility.${storyGroupShareReviewPreflightRecord.artifactVisibility}`)
      : '',
    artifactReadinessLabel: storyGroupShareReviewPreflightRecord?.artifactReadinessStatus
      ? getLotStoryReadinessLabel(t, storyGroupShareReviewPreflightRecord.artifactReadinessStatus)
      : '',
    eligibleGroupCount: storyGroupShareReviewPreflightRecord?.eligibleGroupContextCount ?? 0,
    partnerOrganizationsLabel: storyGroupShareReviewPartnerOrganizationsLabel,
    blockingReasonLabels: storyGroupShareReviewBlockingReasonLabels,
    errorMessage: storyGroupShareReviewPreflightError
  }), [
    savedStoryGroupShareIntentLabel,
    storyGroupReadSelectedGroupLabel,
    storyGroupShareApprovalActionError,
    storyGroupShareApprovalBlockingReasonLabels,
    storyGroupShareApprovalRecordedAtLabel,
    storyGroupShareApprovalRecordedByLabel,
    storyGroupShareApprovalRecording,
    storyGroupShareApprovalStatus,
    storyGroupShareReleaseGateActionError,
    storyGroupShareReleaseGateBlockingReasonLabels,
    storyGroupShareReleaseGateRecordedAtLabel,
    storyGroupShareReleaseGateRecordedByLabel,
    storyGroupShareReleaseGateRecording,
    storyGroupShareReleaseGateStatus,
    storyGroupPromotionActionError,
    storyGroupPromotionBlockingReasonLabels,
    storyGroupPromotionRecordedAtLabel,
    storyGroupPromotionRecordedByLabel,
    storyGroupPromotionRecording,
    storyGroupPromotionStatus,
    storyGroupShareReviewActionError,
    storyGroupShareReviewBlockingReasonLabels,
    storyGroupShareReviewIdentityLabel,
    storyGroupShareReviewPartnerOrganizationsLabel,
    storyGroupShareReviewPreflightError,
    storyGroupShareReviewPreflightRecord,
    storyGroupShareReviewPreflightState,
    storyGroupShareReviewRecordedAtLabel,
    storyGroupShareReviewRecordedByLabel,
    storyGroupShareReviewRecording,
    storyGroupShareReviewRoleLabel,
    storyGroupShareReviewStatus,
    t
  ])

  const getStoryPrivateWriteBlockedMessage = useCallback(() => {
    if (!canDraftLotStories) {
      return t('lots.story.draft.readonly')
    }

    if (storyActorContextError) {
      return storyActorContextError
    }

    if (storyActorContextLoading || !storyActorContext) {
      return t('lots.story.preflight.loading')
    }

    return t('lots.story.preflight.error.denied')
  }, [canDraftLotStories, storyActorContext, storyActorContextError, storyActorContextLoading, t])

  useEffect(() => {
    if (!selectedHistoryLotId) {
      historyRequestIdRef.current += 1
      setHistoryEvents([])
      setHistoryError(null)
      setHistoryLoading(false)
      return
    }

    const nextRequestId = historyRequestIdRef.current + 1
    historyRequestIdRef.current = nextRequestId
    setHistoryLoading(true)
    setHistoryError(null)
    setHistoryEvents([])

    void lotApi.listEvents(selectedHistoryLotId, { scope: 'lineage' })
      .then((events) => {
        if (historyRequestIdRef.current !== nextRequestId) return
        setHistoryEvents(Array.isArray(events) ? events : [])
      })
      .catch((error) => {
        if (historyRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.history.error.loadFailed')
        setHistoryError(message)
      })
      .finally(() => {
        if (historyRequestIdRef.current === nextRequestId) {
          setHistoryLoading(false)
        }
      })
  }, [selectedHistoryLotId, t])

  useEffect(() => {
    if (!selectedStoryLineageRootId) {
      storyPreviewRequestIdRef.current += 1
      setStoryPreview(null)
      setStoryPreviewError(null)
      setStoryPreviewLoading(false)
      return
    }

    if (
      selectedStoryPreviewVersion &&
      selectedStoryPreviewVersion.projectionSnapshot.lineageRootId === selectedStoryLineageRootId
    ) {
      storyPreviewRequestIdRef.current += 1
      setStoryPreview(selectedStoryPreviewVersion.projectionSnapshot)
      setStoryPreviewError(null)
      setStoryPreviewLoading(false)
      return
    }

    const nextRequestId = storyPreviewRequestIdRef.current + 1
    storyPreviewRequestIdRef.current = nextRequestId
    setStoryPreviewLoading(true)
    setStoryPreviewError(null)
    setStoryPreview(null)

    void lotApi.previewStory({ lineageRootId: selectedStoryLineageRootId })
      .then((preview) => {
        if (storyPreviewRequestIdRef.current !== nextRequestId) return
        setStoryPreview(preview)
      })
      .catch((error) => {
        if (storyPreviewRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.error.loadFailed')
        setStoryPreviewError(message)
      })
      .finally(() => {
        if (storyPreviewRequestIdRef.current === nextRequestId) {
          setStoryPreviewLoading(false)
        }
      })
  }, [selectedStoryLineageRootId, selectedStoryPreviewVersion, t])

  useEffect(() => {
    if (!selectedStoryLineageRootId) {
      storyActorContextRequestIdRef.current += 1
      setStoryActorContext(null)
      setStoryActorContextError(null)
      setStoryActorContextLoading(false)
      return
    }

    const nextRequestId = storyActorContextRequestIdRef.current + 1
    storyActorContextRequestIdRef.current = nextRequestId
    setStoryActorContextLoading(true)
    setStoryActorContextError(null)
    setStoryActorContext(null)

    void api.communityActorContext.get()
      .then((context) => {
        if (storyActorContextRequestIdRef.current !== nextRequestId) return
        setStoryActorContext(context)
      })
      .catch((error) => {
        if (storyActorContextRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.preflight.error.loadFailed')
        setStoryActorContextError(message)
      })
      .finally(() => {
        if (storyActorContextRequestIdRef.current === nextRequestId) {
          setStoryActorContextLoading(false)
        }
      })
  }, [selectedStoryLineageRootId, t])

  useEffect(() => {
    setSelectedStoryGroupReadGroupId(selectedStoryArtifact?.artifact.groupId?.trim() ?? '')
    setStoryGroupShareIntentSaving(false)
    setStoryGroupShareIntentStatus(null)
    setStoryGroupShareIntentError(null)
    setStoryGroupShareReviewRecording(false)
    setStoryGroupShareReviewStatus(null)
    setStoryGroupShareReviewActionError(null)
    setStoryGroupShareApprovalRecording(false)
    setStoryGroupShareApprovalStatus(null)
    setStoryGroupShareApprovalActionError(null)
    setStoryGroupShareReleaseGateRecording(false)
    setStoryGroupShareReleaseGateStatus(null)
    setStoryGroupShareReleaseGateActionError(null)
    setStoryGroupPromotionRecording(false)
    setStoryGroupPromotionStatus(null)
    setStoryGroupPromotionActionError(null)
    storyGroupDeliveryRequestIdRef.current += 1
    setStoryGroupDeliveryRecord(null)
    setStoryGroupDeliveryError(null)
    setStoryGroupDeliveryLoading(false)
  }, [selectedStoryArtifact?.artifact.artifactId])

  useEffect(() => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId) {
      storyGroupReadPreflightRequestIdRef.current += 1
      setStoryGroupReadPreflightRecord(null)
      setStoryGroupReadPreflightError(null)
      setStoryGroupReadPreflightLoading(false)
      return
    }

    const nextRequestId = storyGroupReadPreflightRequestIdRef.current + 1
    storyGroupReadPreflightRequestIdRef.current = nextRequestId
    setStoryGroupReadPreflightLoading(true)
    setStoryGroupReadPreflightError(null)
    setStoryGroupReadPreflightRecord(null)

    void lotApi.getStoryGroupReadPreflight(artifactId, selectedStoryGroupReadGroupId || undefined)
      .then((preflight) => {
        if (storyGroupReadPreflightRequestIdRef.current !== nextRequestId) return
        setStoryGroupReadPreflightRecord(preflight)
      })
      .catch((error) => {
        if (storyGroupReadPreflightRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.groupPreflight.error.loadFailed')
        setStoryGroupReadPreflightError(message)
      })
      .finally(() => {
        if (storyGroupReadPreflightRequestIdRef.current === nextRequestId) {
          setStoryGroupReadPreflightLoading(false)
        }
      })
  }, [
    selectedStoryArtifact?.artifact.artifactId,
    selectedStoryArtifact?.artifact.status,
    selectedStoryArtifact?.artifact.visibility,
    selectedStoryArtifact?.artifact.currentDraftVersionId,
    selectedStoryArtifact?.artifact.currentPublishedVersionId,
    selectedStoryGroupReadGroupId,
    t
  ])

  useEffect(() => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId || !storyGroupDeliveryReady) {
      storyGroupDeliveryRequestIdRef.current += 1
      setStoryGroupDeliveryRecord(null)
      setStoryGroupDeliveryError(null)
      setStoryGroupDeliveryLoading(false)
      return
    }

    const nextRequestId = storyGroupDeliveryRequestIdRef.current + 1
    storyGroupDeliveryRequestIdRef.current = nextRequestId
    setStoryGroupDeliveryLoading(true)
    setStoryGroupDeliveryError(null)
    setStoryGroupDeliveryRecord(null)

    void lotApi.getStoryGroupDelivery(artifactId)
      .then((delivery) => {
        if (storyGroupDeliveryRequestIdRef.current !== nextRequestId) return
        setStoryGroupDeliveryRecord(delivery)
      })
      .catch((error) => {
        if (storyGroupDeliveryRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.groupDelivery.error.loadFailed')
        setStoryGroupDeliveryError(message)
      })
      .finally(() => {
        if (storyGroupDeliveryRequestIdRef.current === nextRequestId) {
          setStoryGroupDeliveryLoading(false)
        }
      })
  }, [selectedStoryArtifact?.artifact.artifactId, storyGroupDeliveryReady, t])

  useEffect(() => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId || !storyGroupDeliveryReady || !storyPrivateWriteAllowed) {
      storyGroupDeliveryReceiptRequestIdRef.current += 1
      setStoryGroupDeliveryReceiptRecord(null)
      setStoryGroupDeliveryReceiptError(null)
      setStoryGroupDeliveryReceiptLoading(false)
      setStoryGroupDeliveryReceiptLoadingMore(false)
      setStoryGroupDeliveryReceiptLoadMoreError(null)
      return
    }

    const nextRequestId = storyGroupDeliveryReceiptRequestIdRef.current + 1
    storyGroupDeliveryReceiptRequestIdRef.current = nextRequestId
    setStoryGroupDeliveryReceiptLoading(true)
    setStoryGroupDeliveryReceiptError(null)
    setStoryGroupDeliveryReceiptLoadingMore(false)
    setStoryGroupDeliveryReceiptLoadMoreError(null)
    setStoryGroupDeliveryReceiptRecord(null)

    void lotApi.getStoryGroupDeliveryReceipt(artifactId, {
      historyLimit: STORY_GROUP_DELIVERY_RECEIPT_PAGE_SIZE
    })
      .then((receipt) => {
        if (storyGroupDeliveryReceiptRequestIdRef.current !== nextRequestId) return
        setStoryGroupDeliveryReceiptRecord(receipt)
      })
      .catch((error) => {
        if (storyGroupDeliveryReceiptRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.groupDelivery.receipt.error.loadFailed')
        setStoryGroupDeliveryReceiptError(message)
      })
      .finally(() => {
        if (storyGroupDeliveryReceiptRequestIdRef.current === nextRequestId) {
          setStoryGroupDeliveryReceiptLoading(false)
        }
      })
  }, [selectedStoryArtifact?.artifact.artifactId, storyGroupDeliveryReady, storyPrivateWriteAllowed, t])

  useEffect(() => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId) {
      storyGroupShareReviewPreflightRequestIdRef.current += 1
      setStoryGroupShareReviewPreflightRecord(null)
      setStoryGroupShareReviewPreflightError(null)
      setStoryGroupShareReviewPreflightLoading(false)
      return
    }

    const nextRequestId = storyGroupShareReviewPreflightRequestIdRef.current + 1
    storyGroupShareReviewPreflightRequestIdRef.current = nextRequestId
    setStoryGroupShareReviewPreflightLoading(true)
    setStoryGroupShareReviewPreflightError(null)
    setStoryGroupShareReviewPreflightRecord(null)

    void lotApi.getStoryGroupShareReviewPreflight(artifactId, selectedStoryGroupReadGroupId || undefined)
      .then((preflight) => {
        if (storyGroupShareReviewPreflightRequestIdRef.current !== nextRequestId) return
        setStoryGroupShareReviewPreflightRecord(preflight)
      })
      .catch((error) => {
        if (storyGroupShareReviewPreflightRequestIdRef.current !== nextRequestId) return
        const message = error instanceof Error ? error.message : t('lots.story.groupShareReview.error.loadFailed')
        setStoryGroupShareReviewPreflightError(message)
      })
      .finally(() => {
        if (storyGroupShareReviewPreflightRequestIdRef.current === nextRequestId) {
          setStoryGroupShareReviewPreflightLoading(false)
        }
      })
  }, [
    selectedStoryArtifact?.artifact.artifactId,
    selectedStoryArtifact?.artifact.status,
    selectedStoryArtifact?.artifact.visibility,
    selectedStoryArtifact?.artifact.currentPublishedVersionId,
    selectedStoryArtifact?.artifact.groupId,
    selectedStoryGroupReadGroupId,
    t
  ])

  useEffect(() => {
    if (!storyGroupReadPreflightRecord || selectedStoryGroupReadGroupId) {
      return
    }

    const nextGroupId = storyGroupReadPreflightRecord.selectedGroupId?.trim()
      || storyGroupReadPreflightRecord.eligibleGroupActorContexts[0]?.groupId
      || ''

    if (nextGroupId !== selectedStoryGroupReadGroupId) {
      setSelectedStoryGroupReadGroupId(nextGroupId)
    }
  }, [selectedStoryGroupReadGroupId, storyGroupReadPreflightRecord])

  const loadStoryArtifactDetail = useCallback(async (artifactId: string) => {
    const resolvedArtifactId = artifactId.trim()
    if (!resolvedArtifactId) {
      storyArtifactDetailRequestIdRef.current += 1
      selectedStoryArtifactIdRef.current = null
      setSelectedStoryArtifact(null)
      setSelectedStoryArtifactError(null)
      setSelectedStoryArtifactLoading(false)
      return
    }

    selectedStoryArtifactIdRef.current = resolvedArtifactId
    const nextRequestId = storyArtifactDetailRequestIdRef.current + 1
    storyArtifactDetailRequestIdRef.current = nextRequestId
    setSelectedStoryArtifactLoading(true)
    setSelectedStoryArtifactError(null)

    try {
      const artifact = await lotApi.getStoryArtifact(resolvedArtifactId)
      if (storyArtifactDetailRequestIdRef.current !== nextRequestId) return
      setSelectedStoryArtifact(artifact)
    } catch (error) {
      if (storyArtifactDetailRequestIdRef.current !== nextRequestId) return
      const message = error instanceof Error ? error.message : t('lots.story.library.error.detailFailed')
      setSelectedStoryArtifactError(message)
      setSelectedStoryArtifact(null)
    } finally {
      if (storyArtifactDetailRequestIdRef.current === nextRequestId) {
        setSelectedStoryArtifactLoading(false)
      }
    }
  }, [t])

  const loadStoryLibrary = useCallback(async (preferredArtifactId?: string) => {
    const nextRequestId = storyLibraryRequestIdRef.current + 1
    storyLibraryRequestIdRef.current = nextRequestId
    setStoryLibraryLoading(true)
    setStoryLibraryError(null)

    try {
      const artifacts = await lotApi.listStoryArtifacts(
        selectedStoryLineageRootId
          ? { lineageRootId: selectedStoryLineageRootId }
          : undefined
      )
      if (storyLibraryRequestIdRef.current !== nextRequestId) return

      const nextArtifacts = Array.isArray(artifacts) ? artifacts : []
      setStoryLibrary(nextArtifacts)

      const nextSelectedArtifactId = preferredArtifactId && nextArtifacts.some((artifact) => artifact.artifact.artifactId === preferredArtifactId)
        ? preferredArtifactId
        : selectedStoryArtifactIdRef.current && nextArtifacts.some((artifact) => artifact.artifact.artifactId === selectedStoryArtifactIdRef.current)
          ? selectedStoryArtifactIdRef.current
          : nextArtifacts[0]?.artifact.artifactId ?? null

      selectedStoryArtifactIdRef.current = nextSelectedArtifactId

      if (nextSelectedArtifactId) {
        void loadStoryArtifactDetail(nextSelectedArtifactId)
      } else {
        storyArtifactDetailRequestIdRef.current += 1
        setSelectedStoryArtifact(null)
        setSelectedStoryArtifactError(null)
        setSelectedStoryArtifactLoading(false)
      }
    } catch (error) {
      if (storyLibraryRequestIdRef.current !== nextRequestId) return
      const message = error instanceof Error ? error.message : t('lots.story.library.error.loadFailed')
      setStoryLibraryError(message)
      setStoryLibrary([])
      selectedStoryArtifactIdRef.current = null
      setSelectedStoryArtifact(null)
    } finally {
      if (storyLibraryRequestIdRef.current === nextRequestId) {
        setStoryLibraryLoading(false)
      }
    }
  }, [loadStoryArtifactDetail, selectedStoryLineageRootId, t])

  useEffect(() => {
    void loadStoryLibrary()
  }, [loadStoryLibrary])

  useEffect(() => {
    if (linkedWorkspaceMode === 'stories' && workspaceMode !== 'stories') {
      setWorkspaceMode('stories')
    }
  }, [linkedWorkspaceMode, setWorkspaceMode, workspaceMode])

  useEffect(() => {
    if (!linkedStoryLineageRootId || linkedStoryLineageRootId === selectedStoryLineageRootId) {
      return
    }

    setSelectedStoryLineageRootId(linkedStoryLineageRootId)
  }, [linkedStoryLineageRootId, selectedStoryLineageRootId])

  useEffect(() => {
    if (!linkedStoryArtifactId) {
      return
    }

    selectedStoryArtifactIdRef.current = linkedStoryArtifactId
    void loadStoryLibrary(linkedStoryArtifactId)
  }, [linkedStoryArtifactId, loadStoryLibrary])

  const availableProductionUnits = useMemo<WizardProductionUnit[]>(() => {
    if (!selectedFarm) return []
    return selectedFarm.productionUnits ?? []
  }, [selectedFarm])

  const availableProcessingSites = useMemo(
    () => listFarmProcessingSites(selectedFarm),
    [selectedFarm]
  )

  useEffect(() => {
    if (selectedFacilityIsProcessingPlant) {
      if (selectedProductionUnitId) {
        setSelectedProductionUnitId('')
      }

      if (selectedProcessingSiteId && !availableProcessingSites.some((site) => site.id === selectedProcessingSiteId)) {
        setSelectedProcessingSiteId('')
      }
      return
    }

    if (selectedProcessingSiteId) {
      setSelectedProcessingSiteId('')
    }
  }, [
    availableProcessingSites,
    selectedFacilityIsProcessingPlant,
    selectedProcessingSiteId,
    selectedProductionUnitId,
    setSelectedProcessingSiteId,
    setSelectedProductionUnitId
  ])

  useEffect(() => {
    if (!selectedFarm) {
      setSelectedProductionUnitId('')
      return
    }

    if (!selectedFarm.productionUnits?.length) {
      setSelectedProductionUnitId('')
      return
    }

    const units = selectedFarm.productionUnits ?? []
    const nextUnitId = normalizeSelectedProductionUnitId({
      selectedProductionUnitId,
      units
    })
    if (nextUnitId !== selectedProductionUnitId) {
      setSelectedProductionUnitId(nextUnitId)
    }
  }, [selectedFarm, selectedProductionUnitId, setSelectedProductionUnitId])

  useEffect(() => {
    setForm((prev) => {
      if (prev.id) return prev

      const isPristine = (
        prev.lotKey.trim().length === 0 &&
        prev.stage === 'Active' &&
        prev.species.trim().length === 0 &&
        prev.stockingDate.trim().length === 0 &&
        prev.harvestDate.trim().length === 0 &&
        prev.biomassKg.trim().length === 0
      )

      if (isPristine) {
        return blankForm(selectedFarmId, selectedProductionUnitId)
      }

      // Avoid clobbering user-entered values (e.g., lot key) if farm defaults settle after
      // the user started typing. Only fill missing context fields.
      const nextFarmId = prev.farmId || selectedFarmId
      const nextProductionUnitId = prev.productionUnitId || selectedProductionUnitId
      if (nextFarmId === prev.farmId && nextProductionUnitId === prev.productionUnitId) return prev
      return {
        ...prev,
        farmId: nextFarmId,
        productionUnitId: nextProductionUnitId
      }
    })
  }, [selectedFarmId, selectedProductionUnitId])

  const farmLookup = useMemo(() => {
    const map = new Map<string, string>()
    availableFarms.forEach((farm) => {
      if (farm.id) {
        map.set(farm.id, farm.name ?? farm.id)
      }
    })
    return map
  }, [availableFarms])

  const productionUnitLookup = useMemo(() => {
    const map = new Map<string, string>()
    availableFarms.forEach((farm) => {
      farm.productionUnits?.forEach((unit) => {
        if (unit.id) {
          map.set(unit.id, unit.name ?? unit.id)
        }
      })
    })
    return map
  }, [availableFarms])

  const processingSiteLookup = useMemo(() => {
    const map = new Map<string, string>()
    availableFarms.forEach((farm) => {
      if (!farm.id) return

      listFarmProcessingSites(farm).forEach((site) => {
        map.set(getFarmScopedProcessingSiteKey(farm.id, site.id), site.label)
      })
    })
    return map
  }, [availableFarms])

  const hasRegisteredProcessingSites = useMemo(
    () => availableFarms.some((farm) => listFarmProcessingSites(farm).length > 0),
    [availableFarms]
  )

  const selectedFarmProcessingSites = useMemo(() => {
    if (!form.farmId) return []
    return listFarmProcessingSites(availableFarms.find((farm) => farm.id === form.farmId))
  }, [availableFarms, form.farmId])

  const selectedFormFarm = useMemo(
    () => availableFarms.find((farm) => farm.id === form.farmId),
    [availableFarms, form.farmId]
  )

  const selectedFormFacilityIsProcessingPlant = selectedFormFarm?.facilityType === 'processing-plant'

  const resolveProcessingSiteLabel = useCallback((farmId?: string, processingSiteId?: string): string | undefined => {
    const normalizedProcessingSiteId = processingSiteId?.trim()
    if (!normalizedProcessingSiteId) return undefined

    const normalizedFarmId = farmId?.trim()
    if (!normalizedFarmId) return normalizedProcessingSiteId

    return processingSiteLookup.get(
      getFarmScopedProcessingSiteKey(normalizedFarmId, normalizedProcessingSiteId)
    ) ?? normalizedProcessingSiteId
  }, [processingSiteLookup])

  const resolveLotSubLocationLabel = useCallback((lot: Pick<Lot, 'farmId' | 'productionUnitId' | 'locationType' | 'locationId'>): string | undefined => {
    if (lot.locationType === 'processing-site') {
      return resolveProcessingSiteLabel(lot.farmId, lot.locationId)
    }

    if (lot.productionUnitId) {
      return productionUnitLookup.get(lot.productionUnitId) ?? lot.productionUnitId
    }

    return undefined
  }, [productionUnitLookup, resolveProcessingSiteLabel])

  const formatLotLocationLabel = useCallback((lot: Pick<Lot, 'farmId' | 'productionUnitId' | 'locationType' | 'locationId'>): string => {
    const parts = [
      farmLookup.get(lot.farmId) ?? lot.farmId,
      resolveLotSubLocationLabel(lot)
    ]
      .map((value) => value?.trim() ?? '')
      .filter((value) => value.length > 0)

    return parts.length > 0 ? parts.join(' / ') : '—'
  }, [farmLookup, resolveLotSubLocationLabel])

  const selectedHistoryLotCurrentLocationLabel = useMemo(() => {
    if (!selectedHistoryLot) return '—'
    return formatLotLocationLabel(selectedHistoryLot)
  }, [formatLotLocationLabel, selectedHistoryLot])

  const selectedHistoryLotCurrentStageLabel = useMemo(() => {
    if (!selectedHistoryLot?.stage) return '—'
    return isLotStage(selectedHistoryLot.stage)
      ? t(stageLabelKey[selectedHistoryLot.stage])
      : selectedHistoryLot.stage
  }, [selectedHistoryLot, t])

  const lineageSourceOptionGroups = useMemo(() => buildLineageLotOptionGroups(lineageSourceLots, {
    productionUnitLookup,
    preferredProductionUnitId: selectedProductionUnitId || undefined
  }), [lineageSourceLots, productionUnitLookup, selectedProductionUnitId])

  const lineageQuickOptions = useMemo(
    () => buildLineageLotQuickOptions(lineageSourceOptionGroups),
    [lineageSourceOptionGroups]
  )

  const visibleSplitQuickOptions = useMemo(
    () => filterLineageLotQuickOptions(lineageQuickOptions, splitSourceSearchQuery, 6),
    [lineageQuickOptions, splitSourceSearchQuery]
  )

  const visibleMergeQuickOptions = useMemo(
    () => filterLineageLotQuickOptions(lineageQuickOptions, mergeSourceSearchQuery, 8),
    [lineageQuickOptions, mergeSourceSearchQuery]
  )

  const selectedFarmLabel = useMemo(() => {
    if (!selectedFarmId) return t('lots.filter.allFacilities')
    return farmLookup.get(selectedFarmId) ?? selectedFarmId
  }, [farmLookup, selectedFarmId, t])

  const selectedProcessingSiteLabel = useMemo(() => {
    if (!selectedProcessingSiteId) {
      return selectedFarmId ? t('lots.filter.allProcessingSitesInSelectedFacility') : t('lots.filter.allProcessingSites')
    }
    return resolveProcessingSiteLabel(selectedFarmId, selectedProcessingSiteId) ?? selectedProcessingSiteId
  }, [resolveProcessingSiteLabel, selectedFarmId, selectedProcessingSiteId, t])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!selectedProductionUnitId) {
      return selectedFarmId ? t('lots.filter.allUnitsInSelectedFacility') : t('lots.filter.allUnits')
    }
    return productionUnitLookup.get(selectedProductionUnitId) ?? selectedProductionUnitId
  }, [productionUnitLookup, selectedFarmId, selectedProductionUnitId, t])

  const selectedSubLocationLabel = selectedFacilityIsProcessingPlant
    ? selectedProcessingSiteLabel
    : selectedProductionUnitLabel

  const selectedSubLocationLabelKey: TranslationKey = selectedFacilityIsProcessingPlant
    ? 'lots.filter.processingSiteLabel'
    : 'lots.filter.productionUnitLabel'

  const lotsTableScope = useMemo(() => getLotsTableScope({
    selectedFarmId,
    selectedProductionUnitId,
    selectedProcessingSiteId
  }), [selectedFarmId, selectedProcessingSiteId, selectedProductionUnitId])

  const visibleLots = useMemo(() => {
    if (!selectedFacilityIsProcessingPlant || !selectedProcessingSiteId) {
      return lots
    }

    return lots.filter((lot) => (
      lot.farmId === selectedFarmId &&
      lot.locationType === 'processing-site' &&
      lot.locationId === selectedProcessingSiteId
    ))
  }, [lots, selectedFacilityIsProcessingPlant, selectedFarmId, selectedProcessingSiteId])

  const displayedLots = useMemo(() => sortLotsForDisplay(visibleLots, {
    scope: lotsTableScope,
    farmLookup,
    productionUnitLookup
  }), [farmLookup, lotsTableScope, productionUnitLookup, visibleLots])

  const displayedLotIds = useMemo(
    () => uniqueStrings(displayedLots.map((lot) => lot.id)),
    [displayedLots]
  )

  const lotStageCounts = useMemo(() => {
    const counts: Record<LotListFilter, number> = { Active: 0, Planned: 0, Inactive: 0, Dormant: 0, Harvested: 0, all: displayedLots.length }
    displayedLots.forEach((lot) => {
      counts[listStageOf(lot.stage)] += 1
    })
    return counts
  }, [displayedLots])

  const listedLots = useMemo(() => {
    const query = lotSearchQuery.trim().toLowerCase()
    return displayedLots.filter((lot) => {
      if (lotListFilter !== 'all' && listStageOf(lot.stage) !== lotListFilter) return false
      if (!query) return true
      return [lot.lotKey, lot.species, farmLookup.get(lot.farmId), resolveLotSubLocationLabel(lot)]
        .some((value) => typeof value === 'string' && value.toLowerCase().includes(query))
    })
  }, [displayedLots, farmLookup, lotListFilter, lotSearchQuery, resolveLotSubLocationLabel])

  const refreshCurrentLotSyncStatus = useCallback(async (lotIds: string[]): Promise<EntityBatchSyncStatus> => {
    if (lotIds.length === 0) {
      const emptyStatus: EntityBatchSyncStatus = {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      }
      setLotSyncStatus(emptyStatus)
      return emptyStatus
    }

    const status = await lotApi.readLotSyncStatus(lotIds)
    setLotSyncStatus(status)
    return status
  }, [])

  useEffect(() => {
    if (!hasHydratedContext) {
      setLotSyncStatus(null)
      setLotSyncLastOutcome(null)
      return
    }

    void refreshCurrentLotSyncStatus(displayedLotIds).catch(() => {
      setLotSyncStatus(null)
    })
  }, [displayedLotIds, hasHydratedContext, refreshCurrentLotSyncStatus])

  // v2.1 finding #61 (WSL-09): keep the post-drain refresh ref pointed at
  // a closure that re-reads the CURRENT `displayedLotIds` so
  // `applyLotMutationStatus` (declared later in the file) can call through
  // it from its `syncPromise.then` callback. See the ref's declaration
  // block above for the full rationale.
  useEffect(() => {
    refreshLotSyncStatusFnRef.current = () => refreshCurrentLotSyncStatus(displayedLotIds).catch(() => null)
  }, [displayedLotIds, refreshCurrentLotSyncStatus])

  const lineageSummaryLotIds = useMemo(
    () => uniqueStrings([...displayedLotIds, splitSourceLotId, ...mergeSourceLotIds]),
    [displayedLotIds, mergeSourceLotIds, splitSourceLotId]
  )

  const lotsTableSummary = useMemo(() => buildLotsTableSummary({
    lots: displayedLots,
    scope: lotsTableScope
  }), [displayedLots, lotsTableScope])

  const processingSiteSummary = useMemo(() => {
    if (!selectedFacilityIsProcessingPlant || !selectedProcessingSiteId) {
      return null
    }

    return buildProcessingSiteSummary(displayedLots)
  }, [displayedLots, selectedFacilityIsProcessingPlant, selectedProcessingSiteId])

  const processingSiteDisplayedLotIds = useMemo(
    () => processingSiteSummary ? displayedLotIds : [],
    [displayedLotIds, processingSiteSummary]
  )

  const lotsTableEmptyKey = useMemo(() => getLotsTableEmptyKey(lotsTableScope), [lotsTableScope])

  useEffect(() => {
    if (!processingSiteSummary || processingSiteDisplayedLotIds.length === 0) {
      processingSiteDisplayedLotProvenanceRequestIdRef.current += 1
      setProcessingSiteActivityEntries([])
      setProcessingSiteDisplayedLotProvenanceByLotId({})
      setProcessingSiteDisplayedLotProvenanceEntriesByLotId({})
      return
    }

    const nextRequestId = processingSiteDisplayedLotProvenanceRequestIdRef.current + 1
    processingSiteDisplayedLotProvenanceRequestIdRef.current = nextRequestId

    void lotApi.listLineageEventsForLots(processingSiteDisplayedLotIds)
      .then((events) => {
        if (processingSiteDisplayedLotProvenanceRequestIdRef.current !== nextRequestId) return
        setProcessingSiteActivityEntries(
          buildProcessingSiteActivityEntries(selectedProcessingSiteId, processingSiteDisplayedLotIds, events)
        )
        setProcessingSiteDisplayedLotProvenanceByLotId(
          buildProcessingSiteRecentLotProvenanceMap(processingSiteDisplayedLotIds, events)
        )
        setProcessingSiteDisplayedLotProvenanceEntriesByLotId(
          buildProcessingSiteRecentLotProvenanceEntriesMap(processingSiteDisplayedLotIds, events, 2)
        )
      })
      .catch((error) => {
        if (processingSiteDisplayedLotProvenanceRequestIdRef.current !== nextRequestId) return
        console.warn('[lots] failed to load displayed processing-site provenance', error)
        setProcessingSiteActivityEntries([])
        setProcessingSiteDisplayedLotProvenanceByLotId({})
        setProcessingSiteDisplayedLotProvenanceEntriesByLotId({})
      })
  }, [processingSiteDisplayedLotIds, processingSiteSummary, selectedProcessingSiteId])

  const processingSiteActivityBreakdown = useMemo(() => {
    let transferCount = 0
    let moveCount = 0

    processingSiteActivityEntries.forEach((entry) => {
      if (entry.eventType === 'transfer') {
        transferCount += 1
        return
      }

      if (entry.eventType === 'move') {
        moveCount += 1
      }
    })

    return {
      transferCount,
      moveCount
    }
  }, [processingSiteActivityEntries])

  const filteredProcessingSiteActivityEntries = useMemo(() => {
    if (processingSiteActivityFilter === 'all') {
      return processingSiteActivityEntries
    }

    return processingSiteActivityEntries.filter((entry) => entry.eventType === processingSiteActivityFilter)
  }, [processingSiteActivityEntries, processingSiteActivityFilter])

  const latestFilteredProcessingSiteActivityEntry = useMemo(
    () => filteredProcessingSiteActivityEntries[0] ?? null,
    [filteredProcessingSiteActivityEntries]
  )

  const processingSiteActivityOriginSummary = useMemo(
    () => buildProcessingSiteActivityOriginSummary(filteredProcessingSiteActivityEntries, 3),
    [filteredProcessingSiteActivityEntries]
  )

  const processingSiteActivityOriginRecencyBreakdown = useMemo(
    () => buildProcessingSiteActivityOriginRecencyBreakdown(processingSiteActivityOriginSummary),
    [processingSiteActivityOriginSummary]
  )

  const processingSiteActivityRecencySummary = useMemo(
    () => buildProcessingSiteActivityRecencySummary(filteredProcessingSiteActivityEntries),
    [filteredProcessingSiteActivityEntries]
  )

  useEffect(() => {
    setProcessingSiteActivityFilter('all')
  }, [selectedProcessingSiteId])

  const selectedSplitSourceLot = useMemo(() => {
    if (!splitSourceLotId) return null
    return lineageSourceLots.find((lot) => lot.id === splitSourceLotId) ?? null
  }, [lineageSourceLots, splitSourceLotId])

  const selectedMergeSourceLots = useMemo(() => (
    mergeSourceLotIds
      .map((id) => lineageSourceLots.find((lot) => lot.id === id))
      .filter((lot): lot is Lot => Boolean(lot))
  ), [lineageSourceLots, mergeSourceLotIds])

  const mergeSelectionSummary = useMemo(
    () => summarizeMergeSourceSelection(selectedMergeSourceLots),
    [selectedMergeSourceLots]
  )

  const mergeSourceReviewState = useMemo(() => buildMergeSourceReviewState(
    selectedMergeSourceLots.length,
    {
      previewLimit: MERGE_REVIEW_PREVIEW_LIMIT,
      expanded: isMergeReviewExpanded
    }
  ), [isMergeReviewExpanded, selectedMergeSourceLots.length])

  const visibleMergeReviewLots = useMemo(() => (
    selectedMergeSourceLots.slice(0, mergeSourceReviewState.visibleCount)
  ), [mergeSourceReviewState.visibleCount, selectedMergeSourceLots])

  const mergeSourceChipGroups = useMemo(() => buildMergeSourceChipGroups(
    selectedMergeSourceLots,
    productionUnitLookup
  ), [productionUnitLookup, selectedMergeSourceLots])

  const mergeSelectionGuidance = useMemo(() => {
    if (mergeSelectionSummary.selectedCount === 0) return null

    const formatUnitLabel = (productionUnitId: string): string => (
      productionUnitLookup.get(productionUnitId)?.trim() || productionUnitId
    )

    if (mergeSelectionSummary.selectedCount === 1) {
      return {
        tone: 'info' as const,
        message: t('lots.lineage.merge.guidance.selectOneMore', {
          unit: mergeSelectionSummary.sharedProductionUnitId
            ? formatUnitLabel(mergeSelectionSummary.sharedProductionUnitId)
            : '—'
        })
      }
    }

    if (!mergeSelectionSummary.canMerge) {
      return {
        tone: 'warning' as const,
        message: t('lots.lineage.merge.guidance.crossUnit', {
          units: mergeSelectionSummary.productionUnitIds.map(formatUnitLabel).join(', ')
        })
      }
    }

    return {
      tone: 'success' as const,
      message: t('lots.lineage.merge.guidance.ready', {
        unit: mergeSelectionSummary.sharedProductionUnitId
          ? formatUnitLabel(mergeSelectionSummary.sharedProductionUnitId)
          : '—'
      })
    }
  }, [mergeSelectionSummary, productionUnitLookup, t])

  useEffect(() => {
    if (lineageSummaryLotIds.length === 0) {
      lineageSummaryRequestIdRef.current += 1
      setLedgerLineageSummaryByLotId({})
      return
    }

    const nextRequestId = lineageSummaryRequestIdRef.current + 1
    lineageSummaryRequestIdRef.current = nextRequestId

    void lotApi.listLineageEventsForLots(lineageSummaryLotIds)
      .then((events) => {
        if (lineageSummaryRequestIdRef.current !== nextRequestId) return
        setLedgerLineageSummaryByLotId(buildLotLineageSummaryMap(
          lineageSummaryLotIds,
          Array.isArray(events) ? events : []
        ))
      })
      .catch((error) => {
        if (lineageSummaryRequestIdRef.current !== nextRequestId) return
        console.warn('[Lots] failed to read cached ledger lineage summary', error)
        setLedgerLineageSummaryByLotId({})
      })
  }, [lineageSummaryLotIds])

  const sourceLotForForm = useMemo(() => {
    if (!form.id) return null
    return lots.find((lot) => lot.id === form.id) ?? null
  }, [form.id, lots])

  const formBiomassValue = form.biomassKg.trim()
  const formBiomassKg = formBiomassValue ? Number(formBiomassValue) : undefined
  const formActionPayload: LotFormActionPayload = {
    lotKey: form.lotKey.trim(),
    farmId: form.farmId,
    productionUnitId: form.productionUnitId,
    stage: form.stage,
    species: form.species.trim() || undefined,
    stockingDate: form.stockingDate || undefined,
    harvestDate: form.harvestDate || undefined,
    biomassKg: Number.isNaN(formBiomassKg) ? undefined : formBiomassKg
  }
  const formPresentation = buildLotFormPresentation({
    sourceLot: sourceLotForForm,
    intent: formIntent,
    payload: formActionPayload,
    formFarmId: form.farmId,
    formProductionUnitId: form.productionUnitId,
    formProcessingSiteId: form.processingSiteId,
    targetLocationType: form.targetLocationType
  })

  const transferIntentState = getLotTransferIntentState({
    sourceLot: sourceLotForForm,
    intent: formIntent,
    payload: {
      farmId: form.farmId,
      productionUnitId: form.productionUnitId,
      processingSiteId: form.processingSiteId
    },
    targetLocationType: form.targetLocationType
  })

  const transferSubmitErrorKey = getLotTransferIntentSubmitErrorKey(transferIntentState)
  const isTransferSubmitBlocked = isLotTransferIntentSubmitDisabled(transferIntentState)

  const transferIntentGuidance = useMemo(() => {
    if (transferIntentState === 'inactive') return null

    if (transferIntentState === 'selectFacility') {
      return {
        tone: 'info' as const,
        message: t('lots.form.transfer.guidance.selectFacility')
      }
    }

    if (transferIntentState === 'selectUnit') {
      return {
        tone: 'info' as const,
        message: t('lots.form.transfer.guidance.selectUnit', {
          facility: form.farmId ? (farmLookup.get(form.farmId) ?? form.farmId) : '—'
        })
      }
    }

    if (transferIntentState === 'selectSite') {
      return {
        tone: 'info' as const,
        message: t('lots.form.transfer.guidance.selectSite', {
          facility: form.farmId ? (farmLookup.get(form.farmId) ?? form.farmId) : '—'
        })
      }
    }

    if (transferIntentState === 'sameLocation') {
      return {
        tone: 'warning' as const,
        message: t('lots.form.transfer.guidance.sameLocation')
      }
    }

    if (transferIntentState === 'moveInstead') {
      return {
        tone: 'warning' as const,
        message: t('lots.form.transfer.guidance.moveInstead', {
          facility: form.farmId ? (farmLookup.get(form.farmId) ?? form.farmId) : '—'
        })
      }
    }

    if (transferIntentState === 'processingSiteSameFacility') {
      return {
        tone: 'warning' as const,
        message: t('lots.form.transfer.guidance.processingSiteSameFacility', {
          facility: form.farmId ? (farmLookup.get(form.farmId) ?? form.farmId) : '—'
        })
      }
    }

    return {
      tone: 'success' as const,
      message: t(form.targetLocationType === 'farm'
        ? 'lots.form.transfer.guidance.readyFacility'
        : form.targetLocationType === 'processing-site'
          ? 'lots.form.transfer.guidance.readyProcessingSite'
        : 'lots.form.transfer.guidance.ready', {
        facility: form.farmId ? (farmLookup.get(form.farmId) ?? form.farmId) : '—',
        unit: form.productionUnitId ? (productionUnitLookup.get(form.productionUnitId) ?? form.productionUnitId) : '—',
        site: form.farmId && form.processingSiteId
          ? (processingSiteLookup.get(getFarmScopedProcessingSiteKey(form.farmId, form.processingSiteId)) ?? form.processingSiteId)
          : '—'
      })
    }
  }, [farmLookup, form.farmId, form.processingSiteId, form.productionUnitId, form.targetLocationType, processingSiteLookup, productionUnitLookup, t, transferIntentState])

  useEffect(() => {
    if (!sourceLotForForm || !form.farmId) {
      return
    }

    if (selectedFormFacilityIsProcessingPlant) {
      if (form.targetLocationType !== 'processing-site' || form.productionUnitId) {
        setForm((prev) => ({
          ...prev,
          targetLocationType: 'processing-site',
          productionUnitId: ''
        }))
      }
      return
    }

    if (form.targetLocationType === 'processing-site' && selectedFarmProcessingSites.length === 0) {
      setForm((prev) => ({
        ...prev,
        targetLocationType: 'production-unit',
        processingSiteId: ''
      }))
    }
  }, [
    form.farmId,
    form.productionUnitId,
    form.targetLocationType,
    selectedFarmProcessingSites.length,
    selectedFormFacilityIsProcessingPlant,
    sourceLotForForm
  ])

  useEffect(() => {
    if (formError !== t('lots.error.transferSameLocation')) {
      return
    }

    if (!transferSubmitErrorKey) {
      setFormError(null)
    }
  }, [formError, t, transferSubmitErrorKey])

  const formatDraftLocationLabel = (
    farmId: string,
    productionUnitId: string,
    targetLocationType: LotTransferTargetLocationType = 'production-unit',
    targetLocationId?: string
  ): string => {
    const farmLabel = farmId ? (farmLookup.get(farmId) ?? farmId) : ''

    if (targetLocationType === 'processing-site') {
      if (!farmId && !targetLocationId) {
        return t('lots.form.location.pendingFacilityAndProcessingSite')
      }

      if (!farmId) {
        return t('lots.form.location.pendingFacilityAndProcessingSite')
      }

      if (!targetLocationId) {
        return t('lots.form.location.pendingProcessingSite')
      }

      const processingSiteLabel = processingSiteLookup.get(
        getFarmScopedProcessingSiteKey(farmId, targetLocationId)
      ) ?? targetLocationId

      return t('lots.form.location.facilityWithProcessingSite', {
        facility: farmLabel,
        site: processingSiteLabel
      })
    }

    if (!farmId && !productionUnitId) {
      return t('lots.form.location.pendingFacilityAndUnit')
    }

    const productionUnitLabel = productionUnitId ? (productionUnitLookup.get(productionUnitId) ?? productionUnitId) : ''

    if (targetLocationType === 'farm') {
      if (!farmId) {
        return t('lots.form.location.pendingFacilityAndUnit')
      }
      if (!productionUnitId) {
        return farmLabel
      }

      return t('lots.form.location.facilityWithContext', {
        facility: farmLabel,
        unit: productionUnitLabel
      })
    }

    const parts: string[] = []
    if (farmLabel) {
      parts.push(farmLabel)
    }
    if (productionUnitLabel) {
      parts.push(productionUnitLabel)
    } else if (farmId) {
      parts.push(t('lots.form.location.pendingUnit'))
    }

    return parts.join(' / ')
  }

  const showTransferDestinationMode = Boolean(sourceLotForForm) && (
    formIntent === 'transfer' || formPresentation.action === 'transfer'
  )

  const showProcessingSiteSelector = showTransferDestinationMode && form.targetLocationType === 'processing-site'

  // Findings #178 / #183: Trasladar lote carries the day the fish moved, and a
  // destination unit that already holds the same lot takes the fish into it.
  const isMoveForm = Boolean(sourceLotForForm) && formIntent === 'transfer'
  // Editar no longer moves a lot: a unit change goes through Trasladar lote, with its date.
  const isUnitLockedInEdit = Boolean(sourceLotForForm?.productionUnitId) && formIntent === 'default'
  const moveDestinationUnitId = isMoveForm && sourceLotForForm && form.targetLocationType === 'production-unit'
    && form.farmId === sourceLotForForm.farmId && form.productionUnitId && form.productionUnitId !== sourceLotForForm.productionUnitId
    ? form.productionUnitId
    : ''
  useEffect(() => {
    if (!moveDestinationUnitId) {
      setDestinationUnitLots(null)
      return
    }
    let cancelled = false
    setDestinationUnitLots(null)
    void lotApi.list({ farmId: form.farmId, productionUnitId: moveDestinationUnitId })
      .catch(() => [] as Lot[])
      .then((unitLots) => {
        if (!cancelled) setDestinationUnitLots({ unitId: moveDestinationUnitId, lots: Array.isArray(unitLots) ? unitLots : [] })
      })
    return () => { cancelled = true }
  }, [form.farmId, moveDestinationUnitId])
  const checkingMoveDestination = Boolean(moveDestinationUnitId) && destinationUnitLots?.unitId !== moveDestinationUnitId
  const sameNameMoveTarget = useMemo(() => {
    if (!sourceLotForForm || !moveDestinationUnitId) return null
    const candidates = destinationUnitLots?.unitId === moveDestinationUnitId ? [...destinationUnitLots.lots, ...lots] : lots
    const lot = findSameNameLotInUnit({ lots: candidates, sourceLot: sourceLotForForm, farmId: form.farmId, productionUnitId: moveDestinationUnitId })
    if (!lot) return null
    return {
      lot,
      unit: {
        farmId: form.farmId,
        farmName: farmLookup.get(form.farmId),
        productionUnitId: form.productionUnitId,
        productionUnitName: productionUnitLookup.get(form.productionUnitId)
      }
    }
  }, [destinationUnitLots, farmLookup, form.farmId, form.productionUnitId, lots, moveDestinationUnitId, productionUnitLookup, sourceLotForForm])
  const arrivalUnit = moveDestinationUnitId && !checkingMoveDestination && !sameNameMoveTarget
    ? {
        farmId: form.farmId,
        farmName: farmLookup.get(form.farmId),
        productionUnitId: moveDestinationUnitId,
        productionUnitName: productionUnitLookup.get(moveDestinationUnitId)
      }
    : null

  // Findings #210: the unit the fish arrive in (same facility or another one), and how many end up there.
  const protocolDestination = isMoveForm && sourceLotForForm && form.targetLocationType === 'production-unit'
    && form.productionUnitId && form.productionUnitId !== sourceLotForForm.productionUnitId
    ? { farmId: form.farmId, productionUnitId: form.productionUnitId }
    : null
  const protocolFishAfterMove = (() => {
    if (sameNameMoveTarget) {
      if (!fishMoveStocks) return null
      const deaths = parseMoveDeaths(diedInMove)
      const plan = planFishMove({ fishMoved, sourceStock: fishMoveStocks.source, targetStock: fishMoveStocks.target, diedInMove: deaths.ok ? deaths.count : 0 })
      return plan.ok ? plan.targetAfter : null
    }
    const arrival = parseArrivalCount(arrivalCount)
    return arrival.ok ? arrival.count : null
  })()

  const transferDestinationModeOptions = useMemo<LotTransferTargetLocationType[]>(() => (
    selectedFormFacilityIsProcessingPlant
      ? ['processing-site']
      : hasRegisteredProcessingSites
      ? ['production-unit', 'farm', 'processing-site']
      : ['production-unit', 'farm']
  ), [hasRegisteredProcessingSites, selectedFormFacilityIsProcessingPlant])

  const destinationOptions = useMemo(() => buildLotDestinationOptions(availableFarms, {
    preferredFarmId: form.farmId || selectedFarmId || undefined,
    preferredProductionUnitId: form.productionUnitId || selectedProductionUnitId || undefined,
    preferredProcessingSiteId: form.processingSiteId || undefined,
    targetLocationType: form.targetLocationType
  }), [availableFarms, form.farmId, form.processingSiteId, form.productionUnitId, form.targetLocationType, selectedFarmId, selectedProductionUnitId])

  const visibleDestinationOptions = useMemo(
    () => filterLotDestinationOptions(destinationOptions, destinationSearchQuery, 6),
    [destinationOptions, destinationSearchQuery]
  )

  const applyLotMutationStatus = useCallback((
    verb: LotMutationVerb,
    lotLabel: string,
    result: Pick<LotMutationResult<unknown>, 'syncState' | 'syncPromise'>
  ) => {
    const nextVersion = statusMessageVersionRef.current + 1
    statusMessageVersionRef.current = nextVersion
    const variables = { lot: lotLabel }
    const reconciledInitial = reconcileSyncMutationState({
      mutationSyncState: result.syncState,
      outboxStatus: lotSyncStatusRef.current
    })
    setStatusMessage(t(getLotMutationStatusKey(verb, reconciledInitial), variables))

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (statusMessageVersionRef.current !== nextVersion) return
      const reconciledNext = reconcileSyncMutationState({
        mutationSyncState: nextState,
        outboxStatus: lotSyncStatusRef.current
      })

      // v2.1 finding #61 (WSL-09): reconcile the attention-banner outbox
      // snapshot so it flips from 'pending' to 'synced' immediately after
      // the drain. Placed BEFORE the `reconciledNext === reconciledInitial`
      // early-return so the refresh always fires — the reconciled label
      // is orthogonal to outbox-drain progress. Without this, finding
      // #58's `showActionMessage` rule keeps the stale attention banner
      // pinned while suppressing the post-save toast — operator-visible
      // regression is "save appears stuck".
      void refreshLotSyncStatusFnRef.current()

      if (reconciledNext === reconciledInitial) return
      setStatusMessage(t(getLotMutationStatusKey(verb, reconciledNext), variables))
    })
  }, [t])

  const loadLots = useCallback(async (options?: { preferLocal?: boolean }): Promise<boolean> => {
    if (!hasHydratedContext) {
      setLoadingLots(true)
      return false
    }

    const nextRequestId = requestIdRef.current + 1
    requestIdRef.current = nextRequestId
    setLoadingLots(true)
    setLotsError(null)

    try {
      const response = await lotApi.list({
        farmId: selectedFarmId || undefined,
        productionUnitId: selectedProductionUnitId || undefined
      }, options)
      if (requestIdRef.current !== nextRequestId) return false
      if (!Array.isArray(response)) {
        setLotsError('API response is not an array')
        setLots([])
        return false
      }
      setLots(response)
      return true
    } catch {
      if (requestIdRef.current !== nextRequestId) return false
      setLotsError(t('lots.error.unableToLoad'))
      setLots([])
      return false
    } finally {
      if (requestIdRef.current === nextRequestId) {
        setLoadingLots(false)
      }
    }
  }, [hasHydratedContext, selectedFarmId, selectedProductionUnitId, t])

  const refreshVisibleAndLineageLots = useCallback(async (options?: { preferLocal?: boolean }): Promise<boolean> => {
    const refreshed = await loadLots(options)
    if (selectedFarmId) {
      await refreshLineageLots()
    }
    return refreshed
  }, [loadLots, refreshLineageLots, selectedFarmId])

  // Always load lots when opening the screen (pull on mount)
  useEffect(() => {
    if (!hasHydratedContext) return
    void loadLots()
  }, [hasHydratedContext, loadLots])

  useEffect(() => {
    setSplitSourceLotId('')
    setSplitSourceSearchQuery('')
    setSplitChildKeyA('')
    setSplitChildKeyB('')
    setSplitNotes('')
    setSplitError(null)

    setMergeSourceLotIds([])
    setMergeSourceSearchQuery('')
    setIsMergeReviewExpanded(false)
    setMergeTargetKey('')
    setMergeNotes('')
    setMergeError(null)
  }, [selectedFarmId, selectedProductionUnitId])

  const resetForm = useCallback(() => {
    setForm(blankForm(selectedFarmId, selectedProductionUnitId))
    setFormIntent('default')
    setFormError(null)
    setDestinationSearchQuery('')
  }, [selectedFarmId, selectedProductionUnitId])

  const isPlantReadOnlyWriteContext = selectedFacilityIsProcessingPlant && formIntent !== 'transfer'
  const canUseTableMutationActions = canEditLots && !selectedFacilityIsProcessingPlant
  const canUseLineageTools = canEditLots && Boolean(selectedFarmId) && !selectedFacilityIsProcessingPlant

  const closeHistoryPanel = useCallback(() => {
    setSelectedHistoryLotId(null)
    setSelectedHistoryLotLabel('')
  }, [])

  const closeStoryPreview = useCallback(() => {
    setSelectedStoryLineageRootId(null)
    setSelectedStoryLotLabel('')
    setSelectedStoryPreviewVersion(null)
    setStoryActorContext(null)
    setStoryActorContextError(null)
    setStoryActorContextLoading(false)
    setStoryDraftTitle('')
    setStoryDraftSummary('')
    setStoryDraftNotes('')
    setStoryDraftError(null)
    setStoryDraftSaving(false)
    setStoryPublishing(false)
    setStoryRevoking(false)
    setStoryTimelineFilter('all')
    setSavedStoryDraft(null)
    setWorkspaceMode('operations')
  }, [setWorkspaceMode])

  const handleHistoryToggle = useCallback((lot: Lot) => {
    setHistoryError(null)
    if (selectedHistoryLotId === lot.id) {
      closeHistoryPanel()
      return
    }
    setSelectedHistoryLotId(lot.id)
    setSelectedHistoryLotLabel(lot.lotKey ?? lot.id)
  }, [closeHistoryPanel, selectedHistoryLotId])

  const handleStoryPreviewToggle = useCallback((lot: Lot) => {
    const lineageRootId = lot.lineageRootId ?? lot.id
    setWorkspaceMode('stories')
    setStoryPreviewError(null)
    setStoryDraftError(null)
    setStoryPublishing(false)
    setStoryRevoking(false)
    setStoryTimelineFilter('all')
    setSelectedStoryPreviewVersion(null)
    setSavedStoryDraft(null)
    if (selectedStoryLineageRootId === lineageRootId) {
      closeStoryPreview()
      return
    }
    setSelectedStoryLineageRootId(lineageRootId)
    setSelectedStoryLotLabel(lot.lotKey ?? lineageRootId)
    setStoryDraftTitle(t('lots.story.draft.defaultTitle', { lot: lot.lotKey ?? lineageRootId }))
    setStoryDraftSummary('')
    setStoryDraftNotes('')
  }, [closeStoryPreview, selectedStoryLineageRootId, setWorkspaceMode, t])

  const formatHistoryLocationPart = (farmId?: string, productionUnitId?: string, fallbackLocationId?: string): string | undefined => {
    const parts: string[] = []
    if (farmId) {
      parts.push(farmLookup.get(farmId) ?? farmId)
    }
    if (productionUnitId) {
      parts.push(productionUnitLookup.get(productionUnitId) ?? productionUnitId)
    } else if (fallbackLocationId && farmId && fallbackLocationId === farmId) {
      // The arrival lot of "Sembrar en varias unidades" sits at facility level.
      return t('lots.history.facilityLevel', { facility: farmLookup.get(farmId) ?? farmId })
    } else if (fallbackLocationId) {
      parts.push(resolveProcessingSiteLabel(farmId, fallbackLocationId) ?? productionUnitLookup.get(fallbackLocationId) ?? fallbackLocationId)
    }
    if (parts.length === 0) return undefined
    return parts.join(' / ')
  }

  const formatHistorySnapshotValue = (field: LotHistorySnapshotField, value: LotHistorySnapshot[LotHistorySnapshotField]): string => {
    if (value === undefined || value === null || value === '') return '—'

    if (field === 'stage' && typeof value === 'string' && isLotStage(value)) {
      return t(stageLabelKey[value])
    }

    if (field === 'farmId' && typeof value === 'string') {
      return farmLookup.get(value) ?? value
    }

    if (field === 'productionUnitId' && typeof value === 'string') {
      return productionUnitLookup.get(value) ?? value
    }

    if (field === 'biomassKg' && typeof value === 'number') {
      return String(value)
    }

    return String(value)
  }

  const formatStoryLocationRef = (locationType?: Lot['locationType'], locationId?: string, farmId?: string): string | undefined => {
    const normalizedId = locationId?.trim()
    if (!locationType && !normalizedId) return undefined
    if (locationType === 'farm') {
      return normalizedId ? (farmLookup.get(normalizedId) ?? normalizedId) : undefined
    }
    if (locationType === 'production-unit') {
      return normalizedId ? (productionUnitLookup.get(normalizedId) ?? normalizedId) : undefined
    }
    if (locationType === 'processing-site') {
      return resolveProcessingSiteLabel(farmId, normalizedId)
    }
    return normalizedId
  }

  const formatRecentLotProvenanceOrigin = (provenance: Pick<
    ProcessingSiteRecentLotProvenance,
    'fromLocationType' | 'fromLocationId' | 'fromFarmId' | 'fromProductionUnitId'
  >): string | undefined => {
    if (provenance.fromLocationType === 'farm') {
      const farmId = provenance.fromFarmId ?? provenance.fromLocationId
      return farmId ? (farmLookup.get(farmId) ?? farmId) : undefined
    }

    return formatHistoryLocationPart(provenance.fromFarmId, provenance.fromProductionUnitId, provenance.fromLocationId)
      ?? formatStoryLocationRef(provenance.fromLocationType, provenance.fromLocationId, provenance.fromFarmId)
  }

  const formatProcessingSiteActivityOriginSummaryLabel = (entry: ProcessingSiteActivityOriginSummaryEntry): string => {
    const origin = formatRecentLotProvenanceOrigin(entry)
    return origin ?? t('lots.processingSiteSummary.unknownOrigin')
  }

  const formatProcessingSiteActivityOriginRecencyBadgeLabel = (badge: ProcessingSiteActivityOriginRecencyBadge): string => {
    if (badge === 'last24h') {
      return t('lots.processingSiteSummary.originRecencyBadgeLast24h')
    }

    if (badge === 'last7d') {
      return t('lots.processingSiteSummary.originRecencyBadgeLast7d')
    }

    if (badge === 'stale') {
      return t('lots.processingSiteSummary.originRecencyBadgeStale')
    }

    return t('lots.processingSiteSummary.originRecencyBadgeUnknown')
  }

  const selectedHistoryLotLatestProvenance = useMemo(() => {
    if (!selectedHistoryLotId || historyEvents.length === 0) {
      return null
    }

    return buildProcessingSiteRecentLotProvenanceMap([selectedHistoryLotId], historyEvents)[selectedHistoryLotId] ?? null
  }, [historyEvents, selectedHistoryLotId])

  const selectedHistoryLotLatestProvenanceLabel = useMemo(() => {
    if (!selectedHistoryLotLatestProvenance) return null

    const origin = formatRecentLotProvenanceOrigin(selectedHistoryLotLatestProvenance)
    if (!origin) return null

    return t('lots.history.current.latestOriginValue', {
      eventType: t(lotHistoryEventLabelKey[selectedHistoryLotLatestProvenance.eventType]),
      origin,
      timestamp: formatHistoryTimestamp(selectedHistoryLotLatestProvenance.recordedAt)
    })
  }, [formatRecentLotProvenanceOrigin, selectedHistoryLotLatestProvenance, t])

  const selectedHistoryLotRecentProvenanceEntries = useMemo(() => {
    if (!selectedHistoryLotId || historyEvents.length === 0) {
      return []
    }

    return buildProcessingSiteRecentLotProvenanceEntries(selectedHistoryLotId, historyEvents)
  }, [historyEvents, selectedHistoryLotId])

  // Findings #176: only this lot's events (and the lots it came from), oldest first.
  const scopedHistoryEvents = useMemo(
    () => (selectedHistoryLotId ? scopeHistoryToLot(historyEvents, selectedHistoryLotId) : []),
    [historyEvents, selectedHistoryLotId]
  )
  const historyDetailsById = useMemo(() => buildLotHistoryDetails(scopedHistoryEvents), [scopedHistoryEvents])

  const formatHatcheryFieldValue = (field: string, value: HatcheryFieldChange['from'], timeZone: string): string => {
    if (value === undefined || value === '') return '—'
    if (typeof value === 'boolean') return value ? t('lots.history.yes') : t('lots.history.no')
    if (Array.isArray(value)) return value.map((trait) => t(`lots.hatchery.trait.${trait}` as TranslationKey)).join(', ')
    if (typeof value === 'number') return value.toLocaleString()
    if (/Date$/.test(field)) return formatFarmDateTime(value, timeZone, { dateOnly: true })
    return value
  }

  const formatHistoryDetail = (detail: LotHistoryDetail, farmId: string | undefined): string => {
    const timeZone = farmTimeZone(farmId)
    switch (detail.kind) {
      case 'stageChange': {
        const to = t(lifeStageLabelKey[detail.to] ?? 'lots.hatchery.stage.eggs')
        const date = detail.date ? formatFarmDateTime(detail.date, timeZone, { dateOnly: true }) : '—'
        return detail.from
          ? t('lots.history.stageChange', { from: t(lifeStageLabelKey[detail.from] ?? 'lots.hatchery.stage.eggs'), to, date })
          : t('lots.history.stageSet', { to, date })
      }
      case 'eggArrival':
        return t('lots.history.eggArrival', {
          details: [
            detail.eggs === undefined ? null : t('lots.history.eggArrival.eggs', { eggs: detail.eggs.toLocaleString() }),
            detail.supplier ?? null,
            detail.tua === undefined ? null : t('lots.history.eggArrival.tua', { tua: String(detail.tua) })
          ].filter(Boolean).join(' · ')
        })
      case 'capacityTable':
        return t('lots.history.capacityTable')
      case 'restored':
        return t('lots.history.restored')
      case 'hatcheryFields':
        return detail.changes.map((change) => t('lots.history.fieldChange', {
          field: change.field.startsWith('disinfection') && change.field !== 'disinfectionProduct'
            ? `${t('lots.history.disinfection')} ${t(`lots.hatchery.field.${change.field}` as TranslationKey).toLowerCase()}`
            : change.field === 'disinfectionProduct'
              ? `${t('lots.history.disinfection')} ${t('lots.hatchery.field.disinfectionProduct').toLowerCase()}`
              : change.field === 'stockTraits'
                ? t('lots.history.stockTraits')
                : t(`lots.hatchery.field.${change.field}` as TranslationKey),
          from: formatHatcheryFieldValue(change.field, change.from, timeZone),
          to: formatHatcheryFieldValue(change.field, change.to, timeZone)
        })).join(' · ')
      case 'fishMove':
        return t('lots.history.fishMove', {
          count: detail.fishMoved.toLocaleString(),
          source: detail.fromProductionUnitId ? (productionUnitLookup.get(detail.fromProductionUnitId) ?? '—') : '—',
          target: detail.toProductionUnitId ? (productionUnitLookup.get(detail.toProductionUnitId) ?? '—') : '—'
        })
    }
  }

  const formatStorySiteSummary = (site: LotStoryProjectionV1['identityContext']['currentSite'] | undefined): string => {
    if (!site) return t('lots.story.summary.noCurrentSite')

    if (site.locationType === 'farm') {
      const farmId = site.farmId ?? site.locationId
      return farmId ? (farmLookup.get(farmId) ?? site.displayName ?? farmId) : (site.displayName ?? t('lots.story.summary.noCurrentSite'))
    }

    const locationLabel = formatHistoryLocationPart(site.farmId, site.productionUnitId, site.locationId)
    return locationLabel
      ?? site.displayName
      ?? formatStoryLocationRef(site.locationType, site.locationId, site.farmId)
      ?? t('lots.story.summary.noCurrentSite')
  }

  const formatStoryDescendantSite = (descendant: LotStoryProjectionV1['identityContext']['activeDescendants'][number]): string | undefined => {
    if (descendant.locationType === 'farm') {
      const farmId = descendant.farmId ?? descendant.locationId
      return farmId ? (farmLookup.get(farmId) ?? farmId) : undefined
    }

    return formatHistoryLocationPart(descendant.farmId, descendant.productionUnitId, descendant.locationId)
      ?? formatStoryLocationRef(descendant.locationType, descendant.locationId, descendant.farmId)
  }

  const formatStoryDisposition = (projection: LotStoryProjectionV1): string => (
    projection.performanceSummary.descendantDisposition
      ? t('lots.story.metrics.dispositionValue', {
          active: projection.performanceSummary.descendantDisposition.activeCount,
          inactive: projection.performanceSummary.descendantDisposition.inactiveCount,
          harvested: projection.performanceSummary.descendantDisposition.harvestedCount,
          other: projection.performanceSummary.descendantDisposition.otherCount
        })
      : '—'
  )

  const formatStoryStage = (stage: LotStage | undefined): string => (
    stage ? t(stageLabelKey[stage]) : '—'
  )

  const formatStorySourceTenantValue = (projection: LotStoryProjectionV1): string => {
    const sourceTenantKey = projection.identityContext.sourceTenantKey?.trim()
    if (!sourceTenantKey) return '—'

    return principal?.tenantKey && sourceTenantKey === principal.tenantKey
      ? t('lots.story.summary.thisTenant')
      : sourceTenantKey
  }

  const formatStorySourceTenantTechnicalValue = (projection: LotStoryProjectionV1): string | undefined => {
    const sourceTenantKey = projection.identityContext.sourceTenantKey?.trim()
    if (!sourceTenantKey || !principal?.tenantKey || sourceTenantKey !== principal.tenantKey) {
      return undefined
    }

    return sourceTenantKey
  }

  const formatStoryReasonSourceRefs = (sourceRefs: string[]): string => (
    sourceRefs.map((sourceRef) => lotKeyById.get(sourceRef) ?? sourceRef).join(', ')
  )

  const storyPresentation: StoryPresentation = {
    formatStage: formatStoryStage,
    formatStoryDisposition,
    formatStorySiteSummary
  }

  const renderStoryProjectionReadOnlySections = ({
    projection,
    keyPrefix,
    accentText,
    accentBorder,
    compact = false,
    middleContent,
    footerContent
  }: RenderStoryProjectionReadOnlySectionsArgs) => {
    const getProjectionTestId = (suffix: string): string => `${keyPrefix}-${suffix}`
    const cardMinWidth = compact ? 160 : 180
    const summaryValueStyle: React.CSSProperties = compact
      ? { fontSize: 13, color: '#111827' }
      : { fontSize: 14, color: '#111827' }
    const metricValueStyle: React.CSSProperties = compact
      ? { fontSize: 13, color: '#111827' }
      : { fontSize: 20, fontWeight: 700, color: '#111827' }
    const reasonMessageStyle: React.CSSProperties = compact
      ? { fontSize: 13, color: '#111827' }
      : { fontSize: 13, color: accentText }
    const storyFamilyLabel = getStoryFamilyLabel(projection)
    const storyFamilyTechnicalId = getStoryFamilyTechnicalId(projection)
    const lotFamilyTechnicalIdHelperInfo = getLotFamilyTechnicalIdHelperInfo(projection)
    const storySourceTenantValue = formatStorySourceTenantValue(projection)
    const storySourceTenantTechnicalValue = formatStorySourceTenantTechnicalValue(projection)
    const activeDescendantLocationGroups = buildStoryActiveDescendantLocationGroups({
      descendants: projection.identityContext.activeDescendants,
      resolveLocationLabel: formatStoryDescendantSite,
      fallbackLocationLabel: t('lots.story.summary.activeDescendantsUnknownLocation')
    })
    const lineageGraphRelations = buildStoryLineageGraphRelations({
      events: projection.lifecycleTimeline,
      resolveLotLabel: (lotId) => lotKeyById.get(lotId) ?? lotId
    })
    const filteredTimelineEvents = buildFilteredStoryTimelineEvents({
      events: projection.lifecycleTimeline,
      filter: storyTimelineFilter
    })
    const timelineAuditSummary = buildStoryTimelineAuditSummary({
      events: filteredTimelineEvents,
      notedEntriesLimit: 3
    })
    const timelineEventTypeCounts = buildStoryTimelineEventCounts({
      events: filteredTimelineEvents
    })

    return (
      <>
        {projection.readiness.reasons.length > 0 ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.reasons.title')}</strong>
            <div style={{ display: 'grid', gap: 8 }}>
              {projection.readiness.reasons.map((reason, index) => (
                <div
                  key={`${keyPrefix}-${reason.code}-${index}`}
                  style={{
                    border: reason.severity === 'block' ? '1px solid #fecaca' : `1px solid ${accentBorder}`,
                    borderRadius: 10,
                    backgroundColor: '#ffffff',
                    padding: 12,
                    display: 'grid',
                    gap: 6
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <span style={{ ...getLotStoryReasonBadgeStyle(reason.severity), padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      {t(reason.severity === 'block' ? 'lots.story.reason.block' : 'lots.story.reason.warning')}
                    </span>
                    <span style={reasonMessageStyle}>{reason.message}</span>
                  </div>
                  {reason.sourceRefs && reason.sourceRefs.length > 0 ? (
                    <div style={{ fontSize: 12, color: accentText }}>{formatStoryReasonSourceRefs(reason.sourceRefs)}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.lineageRoot')}</strong>
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ ...summaryValueStyle, wordBreak: 'break-word' }}>{storyFamilyLabel}</div>
              {storyFamilyTechnicalId && lotFamilyTechnicalIdHelperInfo ? (
                <div
                  data-testid={getProjectionTestId('lineage-root-technical')}
                  style={{ fontSize: 12, color: accentText, wordBreak: 'break-word' }}
                  title={t(lotFamilyTechnicalIdHelperInfo.titleKey)}
                >
                  <span style={{ fontWeight: 600 }}>{t(lotFamilyTechnicalIdHelperInfo.helperLabelKey)}: </span>
                  {storyFamilyTechnicalId}
                </div>
              ) : null}
            </div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.sourceTenant')}</strong>
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={summaryValueStyle}>{storySourceTenantValue}</div>
              {storySourceTenantTechnicalValue ? (
                <div style={{ fontSize: 12, color: accentText, wordBreak: 'break-word' }}>{storySourceTenantTechnicalValue}</div>
              ) : null}
            </div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.origin')}</strong>
            <div style={summaryValueStyle}>{projection.identityContext.lineageOrigin?.lotKey ?? projection.identityContext.lineageOrigin?.lotId ?? '—'}</div>
            <div style={{ fontSize: 12, color: accentText }}>
              {projection.identityContext.lineageOrigin?.recordedAt
                ? formatHistoryTimestamp(projection.identityContext.lineageOrigin.recordedAt)
                : '—'}
            </div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.currentSite')}</strong>
            <div style={summaryValueStyle}>{formatStorySiteSummary(projection.identityContext.currentSite)}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.species')}</strong>
            <div style={summaryValueStyle}>{formatOptionalStoryText(projection.identityContext.species)}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.currentStage')}</strong>
            <div style={summaryValueStyle}>
              {projection.identityContext.stage ? t(stageLabelKey[projection.identityContext.stage]) : '—'}
            </div>
          </div>
        </div>

        {middleContent}

        <div style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.summary.activeDescendantsList')}</strong>
          {projection.identityContext.activeDescendants.length === 0 ? (
            <div style={{ fontSize: 13, color: accentText }}>{t('lots.story.summary.activeDescendantsEmpty')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
                {activeDescendantLocationGroups.map((group) => (
                  <div key={`${keyPrefix}-${group.locationKey}`} style={lotStoryCardStyle}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{group.locationLabel}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: accentText }}>
                      {t('lots.story.summary.activeDescendantsByLocationCount', { count: group.lotCount })}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: accentText }}>
                      {t('lots.story.summary.activeDescendantsByLocationLotKeys', { lotKeys: group.lotKeys.join(', ') })}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
                {projection.identityContext.activeDescendants.map((descendant) => {
                  const descendantSite = formatStoryDescendantSite(descendant)

                  return (
                    <div key={`${keyPrefix}-${descendant.lotId}`} style={lotStoryCardStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 14, color: '#111827' }}>{descendant.lotKey}</strong>
                        <span style={{ fontSize: 12, color: accentText }}>
                          {descendant.status ? t(stageLabelKey[descendant.status]) : '—'}
                        </span>
                      </div>
                      {descendantSite ? (
                        <div style={{ fontSize: 12, color: accentText }}>{descendantSite}</div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.lineageGraph.title')}</strong>
          {lineageGraphRelations.length === 0 ? (
            <div style={{ fontSize: 13, color: accentText }}>{t('lots.story.lineageGraph.empty')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
              {lineageGraphRelations.map((relation) => (
                <div key={`${keyPrefix}-${relation.parentLotId}`} style={lotStoryCardStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                    {relation.parentLabel}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: accentText }}>
                    {t('lots.story.lineageGraph.childCount', { count: relation.childLotIds.length })}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: accentText }}>
                    {t('lots.story.lineageGraph.children', { children: relation.childLabels.join(', ') })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.metrics.title')}</strong>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.timelineEvents')}</strong>
              <div style={metricValueStyle}>{projection.lifecycleTimeline.length}</div>
            </div>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.feedLogs')}</strong>
              <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.actualFeed?.logCount)}</div>
              <div style={{ fontSize: 12, color: accentText }}>
                {t('lots.story.metrics.totalFeedKg', { value: formatOptionalStoryNumber(projection.performanceSummary.actualFeed?.totalFeedKg) })}
              </div>
            </div>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.waterLogs')}</strong>
              <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.waterContextSummary?.logCount)}</div>
            </div>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.productionRecords')}</strong>
              <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.productionOutcomeSummary?.recordCount)}</div>
              <div style={{ fontSize: 12, color: accentText }}>
                {t('lots.story.metrics.totalMortality', { value: formatOptionalStoryNumber(projection.performanceSummary.mortality?.totalDailyMortality) })}
              </div>
            </div>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.currentBiomassKg')}</strong>
              <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.biomassTrend?.currentBiomassKg)}</div>
            </div>
          </div>
        </div>

        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.disposition')}</strong>
          <div style={{ fontSize: 13, color: compact ? '#111827' : accentText }}>{formatStoryDisposition(projection)}</div>
        </div>

        <div data-testid={getProjectionTestId('readiness-metadata')} style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.readinessMetadata.title')}</strong>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
            <div data-testid={getProjectionTestId('readiness-status')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.status')}</strong>
              <div style={summaryValueStyle}>{getLotStoryReadinessLabel(t, projection.readiness.status)}</div>
            </div>
            <div data-testid={getProjectionTestId('readiness-source-freshness')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.sourceFreshness')}</strong>
              <div style={summaryValueStyle}>{t(sourceFreshnessLabelKey[projection.readiness.sourceFreshness])}</div>
            </div>
            <div data-testid={getProjectionTestId('readiness-pending-lineage-sync')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.pendingLineageSync')}</strong>
              <div style={summaryValueStyle}>{projection.readiness.pendingLineageSync ? t('lots.story.value.yes') : t('lots.story.value.no')}</div>
            </div>
            <div data-testid={getProjectionTestId('readiness-unresolved-evidence')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.unresolvedEvidence')}</strong>
              <div style={summaryValueStyle}>{projection.readiness.unresolvedEvidenceCount}</div>
            </div>
            <div data-testid={getProjectionTestId('readiness-ambiguous-legacy-match')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.ambiguousLegacyMatches')}</strong>
              <div style={summaryValueStyle}>{projection.readiness.ambiguousLegacyMatchCount}</div>
            </div>
          </div>
        </div>

        <div data-testid={getProjectionTestId('evidence-package')} style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.evidence.title')}</strong>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
            <div data-testid={getProjectionTestId('evidence-feed-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.feedRefs')}</strong>
              <div style={summaryValueStyle}>{projection.evidencePackage.feedRefs.length}</div>
            </div>
            <div data-testid={getProjectionTestId('evidence-water-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.waterRefs')}</strong>
              <div style={summaryValueStyle}>{projection.evidencePackage.waterRefs.length}</div>
            </div>
            <div data-testid={getProjectionTestId('evidence-production-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.productionRefs')}</strong>
              <div style={summaryValueStyle}>{projection.evidencePackage.productionRefs.length}</div>
            </div>
            <div data-testid={getProjectionTestId('evidence-report-snapshot-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.reportSnapshotRefs')}</strong>
              <div style={summaryValueStyle}>{projection.evidencePackage.reportSnapshotRefs.length}</div>
            </div>
            <div data-testid={getProjectionTestId('evidence-dashboard-snapshot-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.dashboardSnapshotRefs')}</strong>
              <div style={summaryValueStyle}>{projection.evidencePackage.dashboardSnapshotRefs.length}</div>
            </div>
            <div data-testid={getProjectionTestId('source-lot-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceLotRefs')}</strong>
              <div style={summaryValueStyle}>{projection.sourceRefs.sourceLotIds.length}</div>
            </div>
            <div data-testid={getProjectionTestId('source-event-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceEventRefs')}</strong>
              <div style={summaryValueStyle}>{projection.sourceRefs.sourceEventIds.length}</div>
            </div>
            <div data-testid={getProjectionTestId('source-feed-record-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceFeedRecordRefs')}</strong>
              <div style={summaryValueStyle}>{projection.sourceRefs.feedRecordIds.length}</div>
            </div>
            <div data-testid={getProjectionTestId('source-water-record-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceWaterRecordRefs')}</strong>
              <div style={summaryValueStyle}>{projection.sourceRefs.waterRecordIds.length}</div>
            </div>
            <div data-testid={getProjectionTestId('source-production-record-refs')} style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceProductionRecordRefs')}</strong>
              <div style={summaryValueStyle}>{projection.sourceRefs.productionRecordIds.length}</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.timeline.title')}</strong>
          {projection.lifecycleTimeline.length > 0 ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 12, color: accentText }}>{t('lots.story.timeline.filter.label')}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {([
                  { value: 'all', labelKey: 'lots.story.timeline.filter.all' },
                  { value: 'notes-only', labelKey: 'lots.story.timeline.filter.notesOnly' },
                  { value: 'relationship-only', labelKey: 'lots.story.timeline.filter.relationshipOnly' }
                ] as Array<{ value: StoryTimelineFilter; labelKey: TranslationKey }>).map((option) => {
                  const selected = storyTimelineFilter === option.value
                  return (
                    <button
                      key={`${keyPrefix}-${option.value}`}
                      type="button"
                      onClick={() => setStoryTimelineFilter(option.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 999,
                        border: selected ? '1px solid #b45309' : '1px solid #fcd34d',
                        backgroundColor: selected ? '#fef3c7' : '#ffffff',
                        color: selected ? '#78350f' : '#92400e',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {t(option.labelKey)}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}

          {filteredTimelineEvents.length > 0 ? (
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` }}>
              <div style={lotStoryCardStyle}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.timeline.audit.totalEvents')}</strong>
                <div style={summaryValueStyle}>{timelineAuditSummary.totalEvents}</div>
              </div>
              <div style={lotStoryCardStyle}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.timeline.audit.notedEvents')}</strong>
                <div style={summaryValueStyle}>{timelineAuditSummary.notedEventsCount}</div>
              </div>
              <div style={lotStoryCardStyle}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.timeline.audit.latestEventAt')}</strong>
                <div style={summaryValueStyle}>
                  {timelineAuditSummary.latestEventAt ? formatHistoryTimestamp(timelineAuditSummary.latestEventAt) : '—'}
                </div>
              </div>
              <div style={lotStoryCardStyle}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.timeline.audit.latestNotedEventAt')}</strong>
                <div style={summaryValueStyle}>
                  {timelineAuditSummary.latestNotedEventAt ? formatHistoryTimestamp(timelineAuditSummary.latestNotedEventAt) : '—'}
                </div>
              </div>
            </div>
          ) : null}

          {timelineAuditSummary.recentNotedEntries.length > 0 ? (
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.timeline.audit.recentNotes')}</strong>
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                {timelineAuditSummary.recentNotedEntries.map((entry) => (
                  <div key={`${keyPrefix}-${entry.eventId}`} style={{ fontSize: 12, color: '#111827' }}>
                    <strong>{t(lotHistoryEventLabelKey[entry.eventType])}</strong>
                    <span style={{ color: accentText }}> · {formatHistoryTimestamp(entry.recordedAt)}</span>
                    <div style={{ marginTop: 2 }}>{entry.notes}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {timelineEventTypeCounts.length > 0 ? (
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.timeline.eventTypeBreakdown')}</strong>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {timelineEventTypeCounts.map((entry) => (
                  <span
                    key={`${keyPrefix}-${entry.eventType}`}
                    style={{
                      border: '1px solid #fde68a',
                      backgroundColor: '#fff7ed',
                      color: '#92400e',
                      borderRadius: 999,
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: 700
                    }}
                  >
                    {t('lots.story.timeline.eventTypeCount', {
                      eventType: t(lotHistoryEventLabelKey[entry.eventType]),
                      count: entry.count
                    })}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {projection.lifecycleTimeline.length === 0 ? (
            <div style={{ fontSize: 13, color: accentText }}>{t('lots.story.timeline.empty')}</div>
          ) : filteredTimelineEvents.length === 0 ? (
            <div style={{ fontSize: 13, color: accentText }}>{t('lots.story.timeline.filteredEmpty')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {filteredTimelineEvents.map((event) => {
                const sourceLots = event.sourceLotIds.length > 0
                  ? formatHistoryLotIds(event.sourceLotIds, lotKeyById)
                  : undefined
                const targetLots = event.targetLotIds.length > 0
                  ? formatHistoryLotIds(event.targetLotIds, lotKeyById)
                  : undefined
                const fromLocation = formatStoryLocationRef(event.fromLocationType, event.fromLocationId, event.fromFarmId)
                const toLocation = formatStoryLocationRef(event.toLocationType, event.toLocationId, event.toFarmId)

                return (
                  <article key={`${keyPrefix}-${event.eventId}`} style={{ border: `1px solid ${accentBorder}`, borderRadius: 10, backgroundColor: '#ffffff', padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ ...getLotHistoryBadgeStyle(event.eventType), padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {t(lotHistoryEventLabelKey[event.eventType])}
                      </span>
                      <span style={{ fontSize: 12, color: accentText }}>{formatHistoryTimestamp(event.recordedAt)}</span>
                    </div>

                    <div style={{ display: 'grid', gap: 8, fontSize: 13, color: '#111827' }}>
                      {sourceLots ? <div><strong>{t('lots.history.sourceLots')}</strong> {sourceLots}</div> : null}
                      {targetLots ? <div><strong>{t('lots.history.targetLots')}</strong> {targetLots}</div> : null}
                      {fromLocation ? <div><strong>{t('lots.story.location.from')}</strong> {fromLocation}</div> : null}
                      {toLocation ? <div><strong>{t('lots.story.location.to')}</strong> {toLocation}</div> : null}
                      {event.notes ? <div><strong>{t('lots.history.notes')}</strong> {event.notes}</div> : null}
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </div>

        {footerContent}
      </>
    )
  }

  const renderStoryPriorityCallouts = (projection: LotStoryProjectionV1): React.ReactNode => {
    const callouts: Array<{ key: string; title: string; message: string }> = []
    const hasBlockedCrossFarmTrust = projection.readiness.reasons.some((reason) => (
      reason.code === 'current-owner-ambiguous' && reason.severity === 'block'
    ))
    const hasUnregisteredProcessingSite = projection.readiness.reasons.some((reason) => (
      reason.code === 'processing-site-unregistered' && reason.severity === 'block'
    ))

    if (projection.readiness.pendingLineageSync) {
      callouts.push({
        key: 'pending-lineage-sync',
        title: t('lots.story.pendingSync.title'),
        message: t('lots.story.pendingSync.message')
      })
    }

    if (hasBlockedCrossFarmTrust) {
      callouts.push({
        key: 'current-owner-ambiguous',
        title: t('lots.story.crossFarmTrust.title'),
        message: t('lots.story.crossFarmTrust.message')
      })
    }

    if (hasUnregisteredProcessingSite) {
      callouts.push({
        key: 'processing-site-unregistered',
        title: t('lots.story.processingSiteRegistry.title'),
        message: t('lots.story.processingSiteRegistry.message')
      })
    }

    if (callouts.length === 0) {
      return null
    }

    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {callouts.map((callout) => (
          <div
            key={callout.key}
            style={{
              border: '1px solid #fca5a5',
              borderRadius: 10,
              backgroundColor: '#fef2f2',
              padding: 12,
              display: 'grid',
              gap: 4
            }}
          >
            <strong style={{ fontSize: 13, color: '#991b1b' }}>{callout.title}</strong>
            <div style={{ fontSize: 13, color: '#b91c1c' }}>{callout.message}</div>
          </div>
        ))}
      </div>
    )
  }

  const renderStoryDraftPanel = (): React.ReactNode => {
    const savedStoryDraftFamilyLabel = savedStoryDraft
      ? getStoryFamilyLabel(savedStoryDraft.version.projectionSnapshot, selectedStoryLotDisplayLabel)
      : undefined

    return (
      <div style={{ display: 'grid', gap: 10, border: '1px solid #fde68a', borderRadius: 12, backgroundColor: '#ffffff', padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <strong style={{ fontSize: 14, color: '#78350f' }}>{t('lots.story.draft.title')}</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#92400e' }}>{t('lots.story.draft.subtitle')}</p>
        </div>
        {/* v2.1 #26 — stable versionStatus badge so the operator can read
            the current artifact lifecycle stage at a glance. */}
        {selectedStoryArtifact?.version?.versionStatus ? (
          <span
            data-testid="lot-story-draft-status-badge"
            title={t('lots.story.draft.currentArtifactStatus')}
            style={{
              ...getLotStoryArtifactVersionBadgeStyle(selectedStoryArtifact.version.versionStatus),
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
              whiteSpace: 'nowrap'
            }}
          >
            {getLotStoryArtifactVersionLabel(t, selectedStoryArtifact.version.versionStatus)}
          </span>
        ) : null}
      </div>

      {!canDraftLotStories ? (
        <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#f8fafc', color: '#475569', fontSize: 13 }}>
          {t('lots.story.draft.readonly')}
        </div>
      ) : isHistoricalStoryPreview && selectedStoryPreviewVersion ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 13, display: 'grid', gap: 4 }}>
            <strong>{t('lots.story.snapshot.title', { version: selectedStoryPreviewVersion.versionNumber })}</strong>
            <div>{t('lots.story.snapshot.readonly')}</div>
          </div>

          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.title')}</strong>
              <div style={{ fontSize: 14, color: '#111827' }}>{storyDraftTitle || '—'}</div>
            </div>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.library.detail.currentVersionStatus')}</strong>
              <div style={{ fontSize: 14, color: '#111827' }}>{getLotStoryArtifactVersionLabel(t, selectedStoryPreviewVersion.versionStatus)}</div>
            </div>
            <div style={lotStoryCardStyle}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.library.history.createdAt')}</strong>
              <div style={{ fontSize: 14, color: '#111827' }}>{formatHistoryTimestamp(selectedStoryPreviewVersion.createdAt)}</div>
            </div>
          </div>

          {storyDraftSummary ? (
            <div style={{ display: 'grid', gap: 4 }}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.summary')}</strong>
              <div style={{ fontSize: 13, color: '#111827' }}>{storyDraftSummary}</div>
            </div>
          ) : null}

          {storyDraftNotes ? (
            <div style={{ display: 'grid', gap: 4 }}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.notes')}</strong>
              <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap' }}>{storyDraftNotes}</div>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <LotStoryPrivateWritePreflightCard
            t={t}
            preflight={storyPrivateWritePreflight}
            testIdPrefix="lot-story-private-preflight"
          />

          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>{t('lots.story.draft.field.title')}</span>
            <input
              type="text"
              value={storyDraftTitle}
              onChange={(event) => setStoryDraftTitle(event.target.value)}
              disabled={storyDraftSaving}
              placeholder={t('lots.story.draft.placeholder.title')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #fcd34d' }}
            />
          </label>

          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>{t('lots.story.draft.field.summary')}</span>
            <textarea
              value={storyDraftSummary}
              onChange={(event) => setStoryDraftSummary(event.target.value)}
              disabled={storyDraftSaving}
              rows={2}
              placeholder={t('lots.story.draft.placeholder.summary')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #fcd34d' }}
            />
          </label>

          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>{t('lots.story.draft.field.notes')}</span>
            <textarea
              value={storyDraftNotes}
              onChange={(event) => setStoryDraftNotes(event.target.value)}
              disabled={storyDraftSaving}
              rows={3}
              placeholder={t('lots.story.draft.placeholder.notes')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #fcd34d' }}
            />
          </label>

          {storyDraftError ? (
            <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
              {storyDraftError}
            </div>
          ) : null}

          {savedStoryDraft ? (
            <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#ecfdf5', color: '#166534', fontSize: 13, display: 'grid', gap: 4 }}>
              <strong>
                {savedStoryDraft.version.versionStatus === 'revoked'
                  ? t('lots.story.revoke.revoked')
                  : savedStoryDraft.version.versionStatus === 'published'
                  ? t('lots.story.publish.published')
                  : t('lots.story.draft.saved')}
              </strong>
              <div>
                {savedStoryDraft.version.versionStatus === 'revoked'
                  ? t('lots.story.revoke.revokedVersion', { version: savedStoryDraft.version.versionNumber })
                  : savedStoryDraft.version.versionStatus === 'published'
                  ? t('lots.story.publish.publishedVersion', { version: savedStoryDraft.version.versionNumber })
                  : t('lots.story.draft.savedVersion', { version: savedStoryDraft.version.versionNumber })}
              </div>
              {savedStoryDraftFamilyLabel ? (
                <div>{t('lots.story.draft.savedFamily', { lot: savedStoryDraftFamilyLabel })}</div>
              ) : null}
              <div style={{ fontSize: 12, color: '#15803d' }}>
                {t('lots.story.draft.savedArtifact', { artifactId: savedStoryDraft.artifact.artifactId })}
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            {/* v2.1 #26 — surface Revoke published version inline in the
                draft panel so the operator doesn't have to dig into the
                library detail panel to find it. */}
            {canRevokeLotStories
              && selectedStoryArtifact?.artifact.status === 'published'
              && selectedStoryArtifact?.version.versionStatus === 'published' ? (
              <GradientButton
                type="button"
                onClick={() => void handleStoryArtifactRevoke()}
                disabled={storyRevoking || storyDraftSaving || storyPublishing || storyPrivateWritePreflightState !== 'allowed'}
                data-testid="lot-story-draft-revoke-button"
                title={t('lots.story.draft.revokePublishedHint')}
              >
                {storyRevoking ? t('lots.story.revoke.revoking') : t('lots.story.revoke.button')}
              </GradientButton>
            ) : null}
            {savedStoryDraft?.version.versionStatus === 'draft' ? (
              <GradientButton
                type="button"
                onClick={() => void handleStoryDraftPublish()}
                disabled={storyDraftSaving || storyPublishing || storyPrivateWritePreflightState !== 'allowed'}
              >
                {storyPublishing ? t('lots.story.publish.publishing') : t('lots.story.publish.button')}
              </GradientButton>
            ) : null}
            <GradientButton
              type="button"
              onClick={() => void handleStoryDraftSave()}
              disabled={storyDraftSaving || storyPublishing || storyRevoking || storyPrivateWritePreflightState !== 'allowed'}
            >
              {storyDraftSaving ? t('lots.story.draft.saving') : t('lots.story.draft.saveButton')}
            </GradientButton>
          </div>
        </>
      )}
      </div>
    )
  }

  const buildHistoryFieldChanges = (event: LedgerLotEvent): Array<{ field: LotHistorySnapshotField; before: string; after: string }> => {
    if (event.eventType !== 'update') return []

    const before = readHistorySnapshot(event, 'before')
    const after = readHistorySnapshot(event, 'after')
    if (!before && !after) return []

    return (['lotKey', 'stage', 'farmId', 'productionUnitId', 'species', 'biomassKg'] as LotHistorySnapshotField[])
      .flatMap((field) => {
        const beforeValue = before?.[field]
        const afterValue = after?.[field]
        if (beforeValue === afterValue) {
          return []
        }

        return [{
          field,
          before: formatHistorySnapshotValue(field, beforeValue),
          after: formatHistorySnapshotValue(field, afterValue)
        }]
      })
  }

  const buildHistoryLocationSummary = (event: LedgerLotEvent): string | undefined => {
    const metadata = asObjectRecord(event.metadata)
    const fromFarmId = typeof metadata?.fromFarmId === 'string' ? metadata.fromFarmId : undefined
    const toFarmId = typeof metadata?.toFarmId === 'string' ? metadata.toFarmId : undefined
    const fromProductionUnitId = typeof metadata?.fromProductionUnitId === 'string' ? metadata.fromProductionUnitId : undefined
    const toProductionUnitId = typeof metadata?.toProductionUnitId === 'string' ? metadata.toProductionUnitId : undefined

    const fromLabel = formatHistoryLocationPart(fromFarmId, fromProductionUnitId, event.fromLocationId)
    const toLabel = formatHistoryLocationPart(toFarmId, toProductionUnitId, event.toLocationId)

    if (!fromLabel && !toLabel) return undefined
    if (!fromLabel) return toLabel
    if (!toLabel) return fromLabel
    // Findings follow-up: "R5 -> R5" said nothing; the place shows only when it changed.
    if (fromLabel === toLabel) return event.eventType === 'update' ? undefined : toLabel
    return `${fromLabel} -> ${toLabel}`
  }

  const renderLineageSourceContext = (lot: Lot) => {
    const lineageSummary = resolveDisplayedLotLineageSummary({
      lotId: lot.id,
      ledgerLineageSummaryByLotId
    })
    const locationLabel = formatLotLocationLabel(lot)

    return (
      <div
        key={lot.id}
        style={{
          marginBottom: 10,
          borderRadius: 8,
          border: '1px solid #dbeafe',
          backgroundColor: '#eff6ff',
          padding: 10
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          {lot.lotKey ?? lot.id}
        </div>
        <div style={{ fontSize: 12, color: '#475569' }}>
          <strong>{t('lots.history.location')}</strong> {locationLabel}
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
          <strong>{t('lots.table.lineage')}</strong> {t('lots.lineage.summary', {
            parents: lineageSummary.parents.length,
            children: lineageSummary.children.length
          })}
        </div>
      </div>
    )
  }

  const toggleMergeSourceLotId = (lotId: string) => {
    setMergeSourceLotIds((prev) => (
      prev.includes(lotId)
        ? prev.filter((value) => value !== lotId)
        : [...prev, lotId]
    ))
  }

  const moveMergeSourceLotId = (lotId: string, direction: 'earlier' | 'later') => {
    setMergeSourceLotIds((prev) => reorderMergeSourceLotIds(prev, lotId, direction))
  }

  const handleSplit = async () => {
    if (!canEditLots) return
    if (selectedFacilityIsProcessingPlant) {
      setSplitError(t('lots.lineage.processingPlantNotice'))
      return
    }
    if (!selectedFarmId) {
      setSplitError(t('lots.lineage.error.selectContextFirst'))
      return
    }

    const source = lineageSourceLots.find((lot) => lot.id === splitSourceLotId)
    if (!source) {
      setSplitError(t('lots.lineage.error.chooseSourceLot'))
      return
    }

    const nextKeys = uniqueStrings([splitChildKeyA, splitChildKeyB])
    if (nextKeys.length < 2) {
      setSplitError(t('lots.lineage.error.enterTwoKeys'))
      return
    }

    setSplitSaving(true)
    setSplitError(null)

    try {
      const { createdLots: created } = await recordLotSplit({
        sourceLot: source,
        childLotKeys: nextKeys,
        recordedAt: splitRecordedAt,
        notes: splitNotes
      })

      invalidateLotCache({ farmId: source.farmId })
      setStatusMessage(t('lots.status.splitSuccess', { source: source.lotKey, targets: created.map((l) => l.lotKey).join(', ') }))
      setSplitChildKeyA('')
      setSplitChildKeyB('')
      setSplitNotes('')
      setSplitSourceLotId('')
      setSplitSourceSearchQuery('')
      setSplitRecordedAt(new Date().toISOString())
      closeStoryPreview()
      closeHistoryPanel()
      await refreshVisibleAndLineageLots({ preferLocal: true })
    } catch (error) {
      console.error('Lots: split failed', error)
      setSplitError(error instanceof Error ? error.message : t('lots.lineage.error.splitFailed'))
    } finally {
      setSplitSaving(false)
    }
  }

  const handleMerge = async () => {
    if (!canEditLots) return
    if (selectedFacilityIsProcessingPlant) {
      setMergeError(t('lots.lineage.processingPlantNotice'))
      return
    }
    if (!selectedFarmId) {
      setMergeError(t('lots.lineage.error.selectContextFirst'))
      return
    }

    const sources = mergeSourceLotIds
      .map((id) => lineageSourceLots.find((lot) => lot.id === id))
      .filter((lot): lot is Lot => Boolean(lot))

    if (sources.length < 2) {
      setMergeError(t('lots.lineage.error.mergeSelectAtLeastTwo'))
      return
    }

    if (!mergeSourcesShareProductionUnit(sources)) {
      setMergeError(t('lots.lineage.error.mergeSameUnit'))
      return
    }

    const targetKey = mergeTargetKey.trim()
    if (!targetKey) {
      setMergeError(t('lots.lineage.error.mergeEnterTargetKey'))
      return
    }

    setMergeSaving(true)
    setMergeError(null)
    try {
      const primary = sources[0]
      const { targetLot: target } = await recordLotMerge({
        sourceLots: sources,
        targetLotKey: targetKey,
        recordedAt: mergeRecordedAt,
        notes: mergeNotes,
        markSourceLotsInactive: mergeMarkInactive
      })

      invalidateLotCache({ farmId: primary.farmId })
      setStatusMessage(t('lots.status.mergeSuccess', { sources: sources.map((l) => l.lotKey).join(', '), target: target.lotKey }))
      setMergeSourceLotIds([])
      setMergeSourceSearchQuery('')
      setIsMergeReviewExpanded(false)
      setMergeTargetKey('')
      setMergeNotes('')
      setMergeRecordedAt(new Date().toISOString())
      closeStoryPreview()
      closeHistoryPanel()
      await refreshVisibleAndLineageLots({ preferLocal: true })
    } catch (error) {
      console.error('Lots: merge failed', error)
      setMergeError(error instanceof Error ? error.message : t('lots.lineage.error.mergeFailed'))
    } finally {
      setMergeSaving(false)
    }
  }

  const handleEdit = (lot: Lot) => {
    setNewLotKind(null)
    setLotFormPanelOpen(true)
    if (selectedFacilityIsProcessingPlant) {
      setFormError(t('lots.form.processingPlantWriteGuard'))
      return
    }
    setForm(buildFormFromLot(lot))
    setFormIntent('default')
    setFormError(null)
    setDestinationSearchQuery('')
  }

  const handleTransfer = (lot: Lot) => {
    setNewLotKind(null)
    setLotFormPanelOpen(true)
    setForm(buildFormFromLot(lot, { transferTarget: true }))
    setFormIntent('transfer')
    setFormError(null)
    setDestinationSearchQuery('')
    setMoveAt(farmDateTimeInputValue(new Date().toISOString(), farmTimeZone(lot.farmId)))
    setFishMoved('')
    setFishMoveStocks(null)
    setArrivalCount('')
    setDiedInMove('')
    setTransferRationDraft(null)
  }

  // Office UX v0 (findings #95): "Cosechar" is Editar with Estado = Cosechado
  // (and today's farm date if the lot has no harvest date) — the same form,
  // the same save and the same lot history as before.
  const handleHarvest = (lot: Lot) => {
    handleEdit(lot)
    if (selectedFacilityIsProcessingPlant) return
    const today = farmToday(farmTimeZone(lot.farmId))
    setForm((prev) => ({
      ...prev,
      stage: 'Harvested',
      harvestDate: prev.harvestDate || today
    }))
  }

  const openNewLotPanel = () => {
    resetForm()
    setNewLotKind(null)
    setEggArrivalDraft(emptyArrivalDraft(farmToday(farmTimeZone(selectedFarmId || undefined))))
    setLotFormPanelOpen(true)
  }

  const chooseNewLotKind = (kind: 'fish' | 'eggs') => {
    setFormError(null)
    setNewLotKind(kind)
    setNewLotCapacityId('')
  }

  const closeLotFormPanel = () => {
    setLotFormPanelOpen(false)
    setNewLotKind(null)
    resetForm()
  }

  const openLotDetail = (lot: Lot, tab: LotDetailTab) => {
    setHistoryError(null)
    setSelectedHistoryLotId(lot.id)
    setSelectedHistoryLotLabel(lot.lotKey ?? lot.id)
    setLotDetailTab(tab)
  }

  const openSplitFor = (lot: Lot) => {
    closeHistoryPanel()
    setSplitSourceLotId(lot.id)
    setSplitError(null)
    setIsLineageDrawerOpen(true)
  }

  const openMergeFor = (lot: Lot) => {
    closeHistoryPanel()
    setMergeSourceLotIds((prev) => (prev.includes(lot.id) ? prev : [lot.id, ...prev]))
    setMergeError(null)
    setIsLineageDrawerOpen(true)
  }

  // v2.1 finding #83: iOS Chrome silently suppresses `window.confirm()` in
  // some event-handler contexts (WebKit dialog gating). Migrated to
  // `useTwoStepConfirm` (src/hooks/useTwoStepConfirm.ts) — two-step in-app
  // confirm pattern established in LotCostSection.tsx:142 and adopted in
  // Feed.tsx and Biomass.tsx α3b.
  const deleteConfirm = useTwoStepConfirm()

  const handleDelete = async (lot: Lot) => {
    const action = resolveLotDeleteAction(canEditLots, selectedFacilityIsProcessingPlant, deleteConfirm.isPending(lot.id))
    if (action === 'blockedReadonly') {
      setStatusMessage(null)
      setLotsError(t('lots.error.readonlyCannotDelete'))
      return
    }
    if (action === 'blockedProcessingPlant') {
      setStatusMessage(null)
      setLotsError(t('lots.form.processingPlantWriteGuard'))
      return
    }
    if (action === 'arm') {
      deleteConfirm.arm(lot.id)
      return
    }
    try {
      const result = await lotApi.delete(lot.id)
      applyLotMutationStatus('deleted', lot.lotKey, result)
      invalidateLotCache({ farmId: lot.farmId })
      await refreshVisibleAndLineageLots({ preferLocal: true })
      if (form.id === lot.id) {
        resetForm()
      }
      closeStoryPreview()
      if (selectedHistoryLotId === lot.id) {
        closeHistoryPanel()
      }
    } catch (error) {
      console.error('Lots: failed to delete', error)
      setStatusMessage(null)
      // If the error is a 404, prompt the user to refresh the page
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        setLotsError(t('lots.error.lotNotFoundRefresh'))
      } else {
        setLotsError(t('lots.error.couldNotDelete'))
      }
    } finally {
      deleteConfirm.reset()
    }
  }

  // Office UX v0 (findings #95): one "Acciones" menu per lot (list row and
  // lot detail) with the operations that used to be buttons and page sections.
  const buildLotActionItems = (lot: Lot): LotActionItem[] => {
    const items: LotActionItem[] = []
    if (canUseTableMutationActions) {
      items.push({ key: 'edit', label: t('common.edit'), testId: 'lots-action-edit', onSelect: () => { closeHistoryPanel(); handleEdit(lot) } })
    }
    items.push({
      key: 'transfer',
      label: t('lots.button.transfer'),
      testId: 'lots-action-transfer',
      disabled: !canEditLots,
      onSelect: () => { closeHistoryPanel(); handleTransfer(lot) }
    })
    if (canUseTableMutationActions && lot.stage !== 'Harvested') {
      items.push({ key: 'harvest', label: t('lots.actions.harvest'), testId: 'lots-action-harvest', onSelect: () => { closeHistoryPanel(); handleHarvest(lot) } })
    }
    const lineageBlockedTitle = canUseLineageTools
      ? undefined
      : selectedFacilityIsProcessingPlant
        ? t('lots.lineage.processingPlantNotice')
        : !canEditLots
          ? t('lots.lineage.readonly.banner', { role: principal?.role ?? 'viewer' })
          : t('lots.lineage.hint.selectContext')
    items.push({ key: 'split', label: t('lots.actions.split'), testId: 'lots-action-split', disabled: !canUseLineageTools, title: lineageBlockedTitle, onSelect: () => openSplitFor(lot) })
    items.push({ key: 'merge', label: t('lots.actions.merge'), testId: 'lots-action-merge', disabled: !canUseLineageTools, title: lineageBlockedTitle, onSelect: () => openMergeFor(lot) })
    if (canEditLots && lot.stage !== 'Harvested') {
      items.push({ key: 'treatment', label: t('lots.actions.treatment'), testId: 'lots-action-treatment', onSelect: () => setTreatmentLot(lot) })
    }
    items.push({
      key: 'story',
      label: t('lots.story.button.show'),
      testId: 'lots-action-story',
      onSelect: () => { closeHistoryPanel(); handleStoryPreviewToggle(lot) }
    })
    if (canUseTableMutationActions) {
      const armed = deleteConfirm.isPending(lot.id)
      items.push({
        key: 'delete',
        label: armed ? t('lots.confirm.deleteCta') : t('common.delete'),
        testId: 'lots-action-delete',
        tone: armed ? 'dangerArmed' : 'danger',
        title: armed ? t('lots.confirm.delete', { lotKey: lot.lotKey }) : undefined,
        keepOpen: !armed,
        onSelect: () => { void handleDelete(lot) }
      })
      if (armed) {
        items.push({ key: 'deleteCancel', label: t('common.cancel'), onSelect: () => deleteConfirm.reset() })
      }
    }
    return items
  }

  const handleChange = <K extends keyof LotFormState>(key: K, value: LotFormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleDestinationSelect = (option: LotDestinationOption) => {
    setForm((prev) => ({
      ...prev,
      farmId: option.farmId,
      productionUnitId: prev.targetLocationType === 'production-unit' ? option.productionUnitId : '',
      processingSiteId: prev.targetLocationType === 'processing-site' ? option.processingSiteId : ''
    }))
    setDestinationSearchQuery('')
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setFormError(null)

    if (!canEditLots) {
      setFormError(t('lots.error.readonlyCannotCreateOrEdit'))
      return
    }
    if (isPlantReadOnlyWriteContext) {
      setFormError(t('lots.form.processingPlantWriteGuard'))
      return
    }
    if (!form.farmId) {
      setFormError(t('lots.error.selectFacility'))
      return
    }

    if (selectedFormFacilityIsProcessingPlant && form.targetLocationType !== 'processing-site') {
      setFormError(t('lots.error.selectProcessingSite'))
      return
    }

    if (form.targetLocationType === 'processing-site') {
      if (!form.processingSiteId) {
        setFormError(t('lots.error.selectProcessingSite'))
        return
      }
    } else {
      const requiresProductionUnit = !(
        form.id &&
        sourceLotForForm &&
        form.targetLocationType === 'farm' &&
        form.farmId !== sourceLotForForm.farmId
      )

      if (requiresProductionUnit && !form.productionUnitId) {
        setFormError(t('lots.error.selectProductionUnit'))
        return
      }
    }

    if (transferSubmitErrorKey) {
      setFormError(t(transferSubmitErrorKey))
      return
    }

    const biomassValue = formBiomassValue
    const biomassKg = formBiomassKg
    if (biomassValue && Number.isNaN(biomassKg)) {
      setFormError(t('lots.error.biomassMustBeNumber'))
      return
    }

    const payload: LotFormActionPayload = {
      ...formActionPayload,
      biomassKg
    }

    // Nuevo lote → Ovas (findings #95): the lot and its egg arrival record
    // (Hatchery v0, #84) are created together; arrival date = stocking date.
    let eggLotCreate: { arrivalDate: string; metadata: Record<string, unknown> } | null = null
    if (!form.id && newLotKind === 'eggs') {
      const parsed = parseArrivalDraft(eggArrivalDraft)
      if (!parsed.ok) {
        setFormError(t(parsed.errorKey))
        return
      }
      eggLotCreate = {
        arrivalDate: parsed.value.arrivalDate,
        metadata: withEggArrival({ metadata: {} as Record<string, unknown> }, {
          arrival: ownArrival(parsed.value),
          supplier: eggArrivalDraft.supplier,
          strain: eggArrivalDraft.strain,
          arrivalDate: parsed.value.arrivalDate,
          recordedAt: new Date().toISOString()
        }).metadata as Record<string, unknown>
      }
    }

    setSaving(true)

    try {
      if (form.id) {
        const sourceLot = sourceLotForForm
        const formAction = getLotFormAction(sourceLot, payload, {
          targetLocationType: form.targetLocationType,
          processingSiteId: form.processingSiteId
        })

        // Findings #178: a move is recorded with the day and time the fish moved.
        const moveTimeZone = farmTimeZone(sourceLot?.farmId)
        const isRecordedMove = Boolean(sourceLot) && isMoveForm && (formAction === 'transfer' || formAction === 'move' || Boolean(sameNameMoveTarget))
        const moveAtIso = isRecordedMove ? farmWallClockToInstant(moveAt, moveTimeZone) : null
        if (isRecordedMove) {
          if (!moveAtIso) {
            setFormError(t('lots.move.error.dateRequired'))
            return
          }
          if (Date.parse(moveAtIso) > Date.now() + 10 * 60 * 1000) {
            setFormError(t('lots.move.error.future'))
            return
          }
        }
        if (isRecordedMove && checkingMoveDestination) {
          setFormError(t('lots.move.checkingDestination'))
          return
        }
        const arrival = arrivalUnit && formAction === 'move' ? parseArrivalCount(arrivalCount) : { ok: true as const, count: null }
        if (!arrival.ok) {
          setFormError(t('lots.move.error.arrivalInvalid'))
          return
        }
        const moveDeaths = formAction === 'move' && (arrivalUnit || sameNameMoveTarget) ? parseMoveDeaths(diedInMove) : { ok: true as const, count: 0 }
        if (!moveDeaths.ok) {
          setFormError(t('lots.move.died.invalid'))
          return
        }
        const moveDeathsNote = t('lots.move.died.note', {
          source: productionUnitLookup.get(sourceLot?.productionUnitId ?? '') ?? '—',
          target: sameNameMoveTarget?.unit.productionUnitName ?? arrivalUnit?.productionUnitName ?? '—'
        })
        if (sourceLot && moveAtIso && !sameNameMoveTarget && (formAction === 'transfer' || formAction === 'move') && sourceLot.productionUnitId) {
          // The whole lot leaves its unit: nothing of it may be recorded there after the move.
          const recordAfter = await findLotRecordAfter({ farmId: sourceLot.farmId, productionUnitId: sourceLot.productionUnitId, lotId: sourceLot.id, after: moveAtIso })
          if (recordAfter) {
            setFormError(t('lots.move.error.recordsAfter', {
              lot: sourceLot.lotKey,
              unit: productionUnitLookup.get(sourceLot.productionUnitId) ?? '—',
              date: formatFarmDateTime(recordAfter, moveTimeZone)
            }))
            return
          }
        }

        if (sourceLot && sameNameMoveTarget && moveAtIso) {
          // Findings #183: the fish join the same lot in the destination unit.
          if (!fishMoveStocks) {
            setFormError(t('lots.move.sameLot.loadingStock'))
            return
          }
          const plan = planFishMove({ fishMoved, sourceStock: fishMoveStocks.source, targetStock: fishMoveStocks.target, diedInMove: moveDeaths.count })
          const sourceUnitName = productionUnitLookup.get(sourceLot.productionUnitId ?? '') ?? '—'
          const targetUnitName = sameNameMoveTarget.unit.productionUnitName ?? '—'
          if (!plan.ok) {
            setFormError(t(plan.error === 'fishMoreThanSource' ? (moveDeaths.count > 0 ? 'lots.move.error.fishAndDeathsMoreThanSource' : 'lots.move.error.fishMoreThanSource') : 'lots.move.error.fishRequired', {
              count: fishMoveStocks.source === null ? '—' : fishMoveStocks.source.toLocaleString(),
              unit: sourceUnitName
            }))
            return
          }
          const countNotes = t('lots.move.sameLot.countNote', {
            count: plan.fishMoved.toLocaleString(),
            source: sourceUnitName,
            target: targetUnitName
          })
          const result = await recordFishMove({
            sourceLot,
            targetLot: sameNameMoveTarget.lot,
            source: {
              farmId: sourceLot.farmId,
              farmName: farmLookup.get(sourceLot.farmId),
              productionUnitId: sourceLot.productionUnitId ?? '',
              productionUnitName: productionUnitLookup.get(sourceLot.productionUnitId ?? '')
            },
            target: sameNameMoveTarget.unit,
            fishMoved: plan.fishMoved,
            sourceAfter: plan.sourceAfter,
            targetAfter: plan.targetAfter,
            recordedAt: moveAtIso,
            countNotes,
            diedInMove: { count: moveDeaths.count, notes: moveDeathsNote, timeZone: moveTimeZone }
          })
          setStatusMessage(t(result.closedSource ? 'lots.move.sameLot.doneClosed' : 'lots.move.sameLot.done', {
            count: plan.fishMoved.toLocaleString(),
            lot: sourceLot.lotKey,
            source: sourceUnitName,
            target: targetUnitName
          }))
          invalidateLotCache({ farmId: sourceLot.farmId })
        } else if (sourceLot && formAction === 'transfer') {
          const result = await recordLotTransfer({
            sourceLot,
            targetFarmId: payload.farmId,
            targetProductionUnitId: form.targetLocationType === 'processing-site'
              ? undefined
              : payload.productionUnitId || undefined,
            targetLocationType: form.targetLocationType,
            targetLocationId: form.targetLocationType === 'farm'
              ? payload.farmId
              : form.targetLocationType === 'processing-site'
                ? form.processingSiteId
                : payload.productionUnitId,
            recordedAt: moveAtIso ?? new Date().toISOString()
          })
          applyLotMutationStatus('updated', payload.lotKey, result)
          invalidateLotCache({ farmId: sourceLot.farmId })
        } else if (sourceLot && formAction === 'move') {
          const result = await recordLotMove(
            form.targetLocationType === 'processing-site'
              ? {
                  sourceLot,
                  targetLocationType: 'processing-site',
                  targetLocationId: form.processingSiteId,
                  recordedAt: moveAtIso ?? new Date().toISOString()
                }
              : {
                  sourceLot,
                  targetProductionUnitId: payload.productionUnitId,
                  recordedAt: moveAtIso ?? new Date().toISOString()
                }
          )
          applyLotMutationStatus('updated', payload.lotKey, result)
          if (moveDeaths.count > 0 && moveAtIso && sourceLot.productionUnitId) {
            // Findings #204: deaths during the move, in the unit the fish left, just before the move.
            await recordMoveDeaths({
              farmId: sourceLot.farmId,
              farmName: farmLookup.get(sourceLot.farmId),
              productionUnitId: sourceLot.productionUnitId,
              productionUnitName: productionUnitLookup.get(sourceLot.productionUnitId),
              lot: sourceLot,
              moveAt: moveAtIso,
              count: moveDeaths.count,
              notes: moveDeathsNote,
              timeZone: moveTimeZone
            })
          }
          if (arrivalUnit && arrival.count !== null && moveAtIso) {
            // Findings #179: the arrival count is written only once the move is on the server.
            if (result.syncState === 'synced') {
              await recordArrivalCount({
                ...arrivalUnit,
                lot: { ...sourceLot, productionUnitId: arrivalUnit.productionUnitId },
                recordedAt: moveAtIso,
                count: arrival.count,
                notes: t('lots.move.arrival.countNote', { source: productionUnitLookup.get(sourceLot.productionUnitId ?? '') ?? '—' })
              })
            } else {
              setStatusMessage(t('lots.move.arrival.offline', { lot: sourceLot.lotKey, unit: arrivalUnit.productionUnitName ?? '—' }))
            }
          }
          invalidateLotCache({ farmId: sourceLot.farmId })
        } else {
          const result = await lotApi.update(form.id, payload)
          applyLotMutationStatus('updated', payload.lotKey, result)
        }
        // Findings #210: the reduced ration goes on the lot that arrived; its planned move is done.
        const movedOn = moveAtIso ? farmDayKey(moveAtIso, moveTimeZone) : null
        if (sourceLot && movedOn && transferRationDraft && protocolDestination && (formAction === 'move' || formAction === 'transfer' || sameNameMoveTarget)) {
          try {
            const arrivedLotId = sameNameMoveTarget ? sameNameMoveTarget.lot.id : sourceLot.id
            const fresh = await lotApi.list({ farmId: protocolDestination.farmId }, { preferLocal: true }).catch(() => [] as Lot[])
            const arrived = fresh.find((lot) => lot.id === arrivedLotId) ?? (sameNameMoveTarget ? sameNameMoveTarget.lot : sourceLot)
            await lotApi.update(arrivedLotId, {
              metadata: withTransferRation(arrived, { ...transferRationDraft, movedOn, productionUnitId: protocolDestination.productionUnitId }).metadata
            })
            if (sameNameMoveTarget && readPlannedTransfer(sourceLot)) {
              const source = fresh.find((lot) => lot.id === sourceLot.id) ?? sourceLot
              await lotApi.update(sourceLot.id, { metadata: withPlannedTransfer(source, null).metadata }).catch(() => undefined)
            }
          } catch (error) {
            console.warn('Lots: failed to save the reduced ration', error)
            setStatusMessage(t('lots.transferProtocol.saveFailed'))
          }
        }
      } else {
        const capacity = capacityAssignmentForChoice(availableFarms.find((candidate) => candidate.id === payload.farmId) as { metadata?: Record<string, unknown> } | undefined, newLotCapacityId)
        const createPayload = eggLotCreate
          ? { ...payload, stockingDate: eggLotCreate.arrivalDate, metadata: eggLotCreate.metadata }
          : payload
        const result = await lotApi.create(capacity
          ? { ...createPayload, metadata: withLotCapacityAssignment({ metadata: { ...((createPayload as { metadata?: Record<string, unknown> }).metadata ?? {}) } }, capacity).metadata }
          : createPayload)
        applyLotMutationStatus('created', payload.lotKey, result)
        // findings #110: the eggs received are the egg lot's opening stock (a count on the arrival day).
        const eggsReceived = eggLotCreate ? readEggsReceived({ metadata: eggLotCreate.metadata }) : null
        if (eggLotCreate && eggsReceived && result.data?.id && payload.productionUnitId) {
          const farm = availableFarms.find((candidate) => candidate.id === payload.farmId)
          await recordStockingDay({
            farmId: payload.farmId,
            farmName: farm?.name,
            productionUnitId: payload.productionUnitId,
            productionUnitName: farm?.productionUnits?.find((unit) => unit.id === payload.productionUnitId)?.name,
            lotId: result.data.id,
            lotKey: payload.lotKey,
            date: `${eggLotCreate.arrivalDate}T00:00:00.000Z`,
            count: eggsReceived
          })
        }
      }
      invalidateLotCache({ farmId: payload.farmId })
      closeStoryPreview()
      if (form.id && selectedHistoryLotId === form.id) {
        closeHistoryPanel()
      }
      resetForm()
      setLotFormPanelOpen(false)
      setNewLotKind(null)
      await refreshVisibleAndLineageLots({ preferLocal: true })
    } catch (error) {
      console.error('Lots: failed to save', error)
      setStatusMessage(null)
      const message = error instanceof Error ? error.message : t('lots.error.unableToSave')
      // Findings #183: the move refusals read in Spanish.
      const moveKey = error instanceof FishMoveOfflineError ? 'lots.move.error.offline' : moveErrorKey(message)
      setFormError(moveKey ? t(moveKey, { lot: form.lotKey.trim() }) : message)
    } finally {
      setSaving(false)
    }
  }

  const isEditing = Boolean(form.id)


  // Handler for manual refresh
  const handleManualRefresh = async () => {
    setLotsError(null)
    setStatusMessage(t('lots.sync.refreshing'))
    const refreshed = await refreshVisibleAndLineageLots()

    if (!refreshed) {
      setStatusMessage(t('lots.sync.refreshFailed'))
      return
    }

    const nextSyncStatus = await refreshCurrentLotSyncStatus(displayedLotIds)
    const nextOutcome = deriveLotSyncOutcome({
      status: nextSyncStatus,
      skippedNotFoundCount: 0
    })

    setLotSyncLastOutcome({
      ...nextOutcome,
      attemptedAtIso: new Date().toISOString()
    })
    setStatusMessage(t('lots.sync.refreshComplete'))
  }

  const lotSyncAttentionMessage = useMemo(() => {
    if (!lotSyncStatus || lotSyncStatus.syncState === 'synced') return null

    if (lotSyncStatus.blockedCount > 0) {
      return lotSyncStatus.errorMessage
        ? t('lots.sync.blockedWithReason', { count: lotSyncStatus.blockedCount, reason: lotSyncStatus.errorMessage })
        : t('lots.sync.blocked', { count: lotSyncStatus.blockedCount })
    }

    if (lotSyncStatus.syncState === 'offline') {
      return t('lots.sync.retryOffline')
    }

    return t('lots.sync.retryPending')
  }, [lotSyncStatus, t])

  const lotSyncLastResultSummary = useMemo(() => {
    if (!lotSyncLastOutcome) return null

    const resultMessage = t(lotSyncLastOutcome.resultKey, lotSyncLastOutcome.resultParams)
    const attemptedAtLabel = new Date(lotSyncLastOutcome.attemptedAtIso).toLocaleString()

    return {
      resultLabel: t('lots.sync.lastResult', { result: resultMessage }),
      attemptedAtLabel: t('lots.sync.lastAttempted', { time: attemptedAtLabel })
    }
  }, [lotSyncLastOutcome, t])

  const handleLotSyncRetry = useCallback(async (): Promise<void> => {
    if (displayedLotIds.length === 0) {
      setLotSyncStatus({
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      })
      return
    }

    setRetryingLotSync(true)
    setLotsError(null)

    try {
      const retryReport = await lotApi.retryLotSyncStatusWithReport(displayedLotIds)
      const nextOutcome = deriveLotSyncOutcome({
        status: retryReport.status,
        skippedNotFoundCount: retryReport.skippedNotFoundCount
      })

      setLotSyncStatus(retryReport.status)
      setLotSyncLastOutcome({
        ...nextOutcome,
        attemptedAtIso: new Date().toISOString()
      })
      setStatusMessage(t(nextOutcome.resultKey, nextOutcome.resultParams))

      await refreshVisibleAndLineageLots({ preferLocal: true })
      await refreshCurrentLotSyncStatus(displayedLotIds)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.sync.retryFailed')
      setLotsError(message)
    } finally {
      setRetryingLotSync(false)
    }
  }, [displayedLotIds, refreshCurrentLotSyncStatus, refreshVisibleAndLineageLots, t])

  const handleStoryDraftSave = async () => {
    if (!selectedStoryLineageRootId || !storyPreview) {
      setStoryDraftError(t('lots.story.draft.error.noPreview'))
      return
    }

    if (selectedStoryPreviewVersion) {
      setStoryDraftError(t('lots.story.snapshot.readonly'))
      return
    }

    if (storyPrivateWritePreflightState !== 'allowed') {
      setStoryDraftError(getStoryPrivateWriteBlockedMessage())
      return
    }

    const title = storyDraftTitle.trim()
    if (!title) {
      setStoryDraftError(t('lots.story.draft.error.titleRequired'))
      return
    }

    setStoryDraftSaving(true)
    setStoryDraftError(null)

    try {
      const savedDraft = await lotApi.createStoryDraft({
        lineageRootId: selectedStoryLineageRootId,
        from: storyPreview.selectedWindow?.from,
        to: storyPreview.selectedWindow?.to,
        title,
        summary: storyDraftSummary.trim() || undefined,
        notes: storyDraftNotes.trim() || undefined
      })
      setSavedStoryDraft(savedDraft)
      void loadStoryLibrary(savedDraft.artifact.artifactId)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.draft.error.saveFailed')
      setStoryDraftError(message)
    } finally {
      setStoryDraftSaving(false)
    }
  }

  const handleStoryDraftPublish = async () => {
    const artifactId = savedStoryDraft?.artifact.artifactId?.trim()
    if (!artifactId || savedStoryDraft?.version.versionStatus !== 'draft') {
      setStoryDraftError(t('lots.story.publish.error.noDraft'))
      return
    }

    if (storyPrivateWritePreflightState !== 'allowed') {
      setStoryDraftError(getStoryPrivateWriteBlockedMessage())
      return
    }

    setStoryPublishing(true)
    setStoryDraftError(null)

    try {
      const publishedDraft = await lotApi.publishStoryDraft(artifactId)
      setSavedStoryDraft(publishedDraft)
      void loadStoryLibrary(publishedDraft.artifact.artifactId)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.publish.error.failed')
      setStoryDraftError(message)
    } finally {
      setStoryPublishing(false)
    }
  }

  const handleStoryArtifactRevoke = async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()
    if (!artifactId || selectedStoryArtifact?.artifact.status !== 'published' || selectedStoryArtifact.version.versionStatus !== 'published') {
      setStoryDraftError(t('lots.story.revoke.error.noPublished'))
      return
    }

    setStoryRevoking(true)
    setStoryDraftError(null)
    setSelectedStoryArtifactError(null)

    try {
      const revokedArtifact = await lotApi.revokePublishedStory(artifactId)
      if (savedStoryDraft?.artifact.artifactId === revokedArtifact.artifact.artifactId) {
        setSavedStoryDraft(revokedArtifact)
      }
      void loadStoryLibrary(revokedArtifact.artifact.artifactId)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.revoke.error.failed')
      setSelectedStoryArtifactError(message)
    } finally {
      setStoryRevoking(false)
    }
  }

  const handleStoryArtifactPublish = async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()
    if (!artifactId || selectedStoryArtifact?.version.versionStatus !== 'draft') {
      setSelectedStoryArtifactError(t('lots.story.publish.error.noDraft'))
      return
    }

    setStoryPublishing(true)
    setStoryDraftError(null)
    setSelectedStoryArtifactError(null)

    try {
      const publishedArtifact = await lotApi.publishStoryDraft(artifactId)
      if (savedStoryDraft?.artifact.artifactId === publishedArtifact.artifact.artifactId) {
        setSavedStoryDraft(publishedArtifact)
      }
      void loadStoryLibrary(publishedArtifact.artifact.artifactId)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.publish.error.failed')
      setSelectedStoryArtifactError(message)
    } finally {
      setStoryPublishing(false)
    }
  }

  const handleStoryArtifactOpen = useCallback((artifact: LotStoryArtifactBundleV1) => {
    setWorkspaceMode('stories')
    setSelectedStoryLineageRootId(artifact.artifact.lineageRootId)
    setSelectedStoryPreviewVersion(null)
    setSelectedStoryLotLabel(getStoryFamilyLabel(artifact.version.projectionSnapshot))
    setStoryPreviewError(null)
    setStoryDraftError(null)
    setSavedStoryDraft(artifact)
    setStoryDraftTitle(artifact.version.title)
    setStoryDraftSummary(artifact.version.summary ?? '')
    setStoryDraftNotes(artifact.version.notes ?? '')
    void loadStoryArtifactDetail(artifact.artifact.artifactId)
  }, [loadStoryArtifactDetail, setWorkspaceMode])

  const handleStoryArtifactVersionOpen = useCallback((artifact: LotStoryArtifactDetailV1, version: LotStoryArtifactVersionV1) => {
    setWorkspaceMode('stories')
    selectedStoryArtifactIdRef.current = artifact.artifact.artifactId
    setSelectedStoryArtifact(artifact)
    setSelectedStoryLineageRootId(artifact.artifact.lineageRootId)
    setSelectedStoryPreviewVersion(version)
    setSelectedStoryLotLabel(getStoryFamilyLabel(version.projectionSnapshot))
    setStoryPreview(version.projectionSnapshot)
    setStoryPreviewError(null)
    setStoryPreviewLoading(false)
    setStoryDraftError(null)
    setStoryDraftSaving(false)
    setStoryPublishing(false)
    setStoryRevoking(false)
    setSavedStoryDraft(null)
    setStoryDraftTitle(version.title)
    setStoryDraftSummary(version.summary ?? '')
    setStoryDraftNotes(version.notes ?? '')
  }, [setWorkspaceMode])

  const selectedStoryPreviewVersionId = selectedStoryPreviewVersion?.artifactVersionId ?? null
  const savedStoryDraftArtifactId = savedStoryDraft?.artifact.artifactId ?? null
  const lotStoryLibraryState: LotStoryLibraryPanelState = {
    storyLibraryLoading,
    selectedStoryArtifactLoading,
    storyLibraryError,
    selectedStoryArtifactError,
    storyLibrary,
    selectedStoryArtifact,
    selectedStoryPreviewVersionId,
    canDraftLotStories,
    canRevokeLotStories,
    storyPrivateWritePreflight,
    storyGroupReadPreflight,
    storyGroupDelivery,
    storyGroupDeliveryRenderedReceipt,
    storyGroupShareReviewPreflight,
    storyGroupShareShell,
    storyPublishing,
    storyRevoking
  }
  const lotStoryLibraryPresentation: LotStoryLibraryPresentation = {
    lotStoryCardStyle,
    formatHistoryTimestamp,
    storyPresentation,
    renderStoryProjectionReadOnlySections
  }

  const handleStoryArtifactSelect = useCallback((artifactId: string) => {
    void loadStoryArtifactDetail(artifactId)
  }, [loadStoryArtifactDetail])

  const handleStoryLibraryRefresh = useCallback(() => {
    void loadStoryLibrary(selectedStoryArtifactIdRef.current ?? savedStoryDraftArtifactId ?? undefined)
  }, [loadStoryLibrary, savedStoryDraftArtifactId])
  const handleStoryGroupReadPreflightGroupSelect = useCallback((groupId: string) => {
    setSelectedStoryGroupReadGroupId(groupId.trim())
    storyGroupDeliveryRequestIdRef.current += 1
    setStoryGroupDeliveryRecord(null)
    setStoryGroupDeliveryError(null)
    setStoryGroupDeliveryLoading(false)
    setStoryGroupShareIntentStatus(null)
    setStoryGroupShareIntentError(null)
    setStoryGroupShareReviewStatus(null)
    setStoryGroupShareReviewActionError(null)
    setStoryGroupShareApprovalStatus(null)
    setStoryGroupShareApprovalActionError(null)
    setStoryGroupShareReleaseGateStatus(null)
    setStoryGroupShareReleaseGateActionError(null)
    setStoryGroupPromotionStatus(null)
    setStoryGroupPromotionActionError(null)
  }, [])
  const handleStoryGroupShareIntentSave = useCallback(async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()
    const groupId = selectedStoryGroupReadGroupId.trim()

    if (!artifactId || !groupId) {
      setStoryGroupShareIntentError(t('lots.story.groupShareShell.error.saveFailed'))
      return
    }

    setStoryGroupShareIntentSaving(true)
    setStoryGroupShareIntentStatus(null)
    setStoryGroupShareIntentError(null)
    setStoryGroupShareReviewStatus(null)
    setStoryGroupShareReviewActionError(null)
    setStoryGroupShareApprovalStatus(null)
    setStoryGroupShareApprovalActionError(null)
    setStoryGroupShareReleaseGateStatus(null)
    setStoryGroupShareReleaseGateActionError(null)
    setStoryGroupPromotionStatus(null)
    setStoryGroupPromotionActionError(null)

    try {
      const updatedArtifact = await lotApi.updateStoryGroupShareIntent(artifactId, { groupId })
      selectedStoryArtifactIdRef.current = updatedArtifact.artifact.artifactId
      setSelectedStoryArtifact(updatedArtifact)
      setStoryLibrary((prev) => prev.map((bundle) => (
        bundle.artifact.artifactId === updatedArtifact.artifact.artifactId
          ? {
              ...bundle,
              artifact: {
                ...bundle.artifact,
                groupId: updatedArtifact.artifact.groupId,
                updatedAt: updatedArtifact.artifact.updatedAt,
                updatedByActorId: updatedArtifact.artifact.updatedByActorId,
                updatedByTokenId: updatedArtifact.artifact.updatedByTokenId
              }
            }
          : bundle
      )))
      setSavedStoryDraft((prev) => (
        prev && prev.artifact.artifactId === updatedArtifact.artifact.artifactId
          ? {
              ...prev,
              artifact: {
                ...prev.artifact,
                groupId: updatedArtifact.artifact.groupId,
                updatedAt: updatedArtifact.artifact.updatedAt,
                updatedByActorId: updatedArtifact.artifact.updatedByActorId,
                updatedByTokenId: updatedArtifact.artifact.updatedByTokenId
              }
            }
          : prev
      ))
      setStoryGroupShareIntentStatus(t('lots.story.groupShareShell.saved'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.groupShareShell.error.saveFailed')
      setStoryGroupShareIntentError(message)
    } finally {
      setStoryGroupShareIntentSaving(false)
    }
  }, [selectedStoryArtifact?.artifact.artifactId, selectedStoryGroupReadGroupId, t])
  const handleStoryGroupShareReviewRecord = useCallback(async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId) {
      setStoryGroupShareReviewActionError(t('lots.story.groupShareReview.error.recordFailed'))
      return
    }

    setStoryGroupShareReviewRecording(true)
    setStoryGroupShareReviewStatus(null)
    setStoryGroupShareReviewActionError(null)
    setStoryGroupShareApprovalStatus(null)
    setStoryGroupShareApprovalActionError(null)
    setStoryGroupShareReleaseGateStatus(null)
    setStoryGroupShareReleaseGateActionError(null)
    setStoryGroupPromotionStatus(null)
    setStoryGroupPromotionActionError(null)

    try {
      const updatedPreflight = await lotApi.recordStoryGroupShareReview(artifactId)
      setStoryGroupShareReviewPreflightRecord(updatedPreflight)
      setStoryGroupShareReviewStatus(t('lots.story.groupShareReview.reviewRecorded'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.groupShareReview.error.recordFailed')
      setStoryGroupShareReviewActionError(message)
    } finally {
      setStoryGroupShareReviewRecording(false)
    }
  }, [selectedStoryArtifact?.artifact.artifactId, t])
  const handleStoryGroupShareApprovalRecord = useCallback(async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId) {
      setStoryGroupShareApprovalActionError(t('lots.story.groupShareReview.error.approvalRecordFailed'))
      return
    }

    setStoryGroupShareApprovalRecording(true)
    setStoryGroupShareApprovalStatus(null)
    setStoryGroupShareApprovalActionError(null)
    setStoryGroupShareReleaseGateStatus(null)
    setStoryGroupShareReleaseGateActionError(null)

    try {
      const updatedPreflight = await lotApi.recordStoryGroupShareApproval(artifactId)
      setStoryGroupShareReviewPreflightRecord(updatedPreflight)
      setStoryGroupShareApprovalStatus(t('lots.story.groupShareReview.approvalRecorded'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.groupShareReview.error.approvalRecordFailed')
      setStoryGroupShareApprovalActionError(message)
    } finally {
      setStoryGroupShareApprovalRecording(false)
    }
  }, [selectedStoryArtifact?.artifact.artifactId, t])
  const handleStoryGroupShareReleaseGateRecord = useCallback(async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId) {
      setStoryGroupShareReleaseGateActionError(t('lots.story.groupShareReview.error.releaseGateRecordFailed'))
      return
    }

    setStoryGroupShareReleaseGateRecording(true)
    setStoryGroupShareReleaseGateStatus(null)
    setStoryGroupShareReleaseGateActionError(null)
    setStoryGroupPromotionStatus(null)
    setStoryGroupPromotionActionError(null)

    try {
      const updatedPreflight = await lotApi.recordStoryGroupShareReleaseGate(artifactId)
      setStoryGroupShareReviewPreflightRecord(updatedPreflight)
      setStoryGroupShareReleaseGateStatus(t('lots.story.groupShareReview.releaseGateRecorded'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.groupShareReview.error.releaseGateRecordFailed')
      setStoryGroupShareReleaseGateActionError(message)
    } finally {
      setStoryGroupShareReleaseGateRecording(false)
    }
  }, [selectedStoryArtifact?.artifact.artifactId, t])
  const handleStoryPromoteToGroup = useCallback(async () => {
    const artifactId = selectedStoryArtifact?.artifact.artifactId?.trim()

    if (!artifactId) {
      setStoryGroupPromotionActionError(t('lots.story.groupShareReview.error.promoteFailed'))
      return
    }

    setStoryGroupPromotionRecording(true)
    setStoryGroupPromotionStatus(null)
    setStoryGroupPromotionActionError(null)

    try {
      const updatedPreflight = await lotApi.promoteStoryToGroup(artifactId)
      setStoryGroupShareReviewPreflightRecord(updatedPreflight)
      await loadStoryArtifactDetail(artifactId)
      setStoryGroupPromotionStatus(t('lots.story.groupShareReview.promoted'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('lots.story.groupShareReview.error.promoteFailed')
      setStoryGroupPromotionActionError(message)
    } finally {
      setStoryGroupPromotionRecording(false)
    }
  }, [loadStoryArtifactDetail, selectedStoryArtifact?.artifact.artifactId, t])
  const lotStoryLibraryActions: LotStoryLibraryPanelActions = {
    onRefreshLibrary: handleStoryLibraryRefresh,
    onSelectArtifactId: handleStoryArtifactSelect,
    onOpenArtifact: handleStoryArtifactOpen,
    onOpenArtifactVersion: handleStoryArtifactVersionOpen,
    onSelectGroupReadPreflightGroup: handleStoryGroupReadPreflightGroupSelect,
    onRecordGroupShareReview: handleStoryGroupShareReviewRecord,
    onRecordGroupShareApproval: handleStoryGroupShareApprovalRecord,
    onRecordGroupShareReleaseGate: handleStoryGroupShareReleaseGateRecord,
    onPromoteStoryToGroup: handleStoryPromoteToGroup,
    onSaveGroupShareIntent: handleStoryGroupShareIntentSave,
    onPublishArtifact: handleStoryArtifactPublish,
    onRevokeArtifact: handleStoryArtifactRevoke
  }

  const selectedHistoryLotLineage = selectedHistoryLotId
    ? resolveDisplayedLotLineageSummary({ lotId: selectedHistoryLotId, ledgerLineageSummaryByLotId })
    : { parents: [] as string[], children: [] as string[] }
  const isLotDetailEligible = Boolean(
    selectedHistoryLot
    && (!selectedHistoryLot.stage || selectedHistoryLot.stage === 'Active')
    && selectedHistoryLot.productionUnitId
  )
  // Incubación OR Proyección — only the one that applies (Hatchery v0, #84).
  const lotDetailTabs: LotDetailTab[] = [
    'summary',
    ...(isLotDetailEligible && selectedHistoryLot
      ? [isHatcheryLot(selectedHistoryLot) ? 'hatchery' as const : 'projection' as const]
      : []),
    'history',
    ...(selectedHistoryLot ? ['costs' as const, 'treatments' as const] : []),
    'traceability'
  ]
  const activeLotDetailTab: LotDetailTab = lotDetailTabs.includes(lotDetailTab) ? lotDetailTab : 'summary'
  const isNewLotForm = !form.id
  const isEggLotForm = isNewLotForm && newLotKind === 'eggs'

  return (
    <PageShell>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <h2 style={{ marginBottom: 8 }}>{t('lots.title')}</h2>
          <p style={{ margin: 0, color: '#4b5563' }}>
            {t('lots.subtitle')}
          </p>
          <p style={{ margin: '10px 0 0 0', fontSize: 13, color: '#0f766e' }}>
            {t('lots.instructions.refreshHint')}
          </p>
          {!canEditLots && (
            <p style={{ margin: '8px 0 0 0', fontSize: 12, opacity: 0.8 }}>
              {t('lots.readonly.banner', { role: principal?.role ?? 'viewer' })}
            </p>
          )}
        </div>

        {!hasHydratedContext ? (
          <div style={{ padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: '#ffffff', color: '#4b5563' }}>
            {t('lots.filter.loadingContext')}
          </div>
        ) : (
          <>
            <CurrentContextBar
              fields={[
                { label: t('lots.filter.facilityLabel'), value: selectedFarmLabel },
                { label: t(selectedSubLocationLabelKey), value: selectedSubLocationLabel }
              ]}
            />

            {/* v2.2 T3-C-06 (2026-07-13): interactive facility + PU/processing-
                site selector, mirrors the peer convention used by
                Water/Feed/Biomass/Datatable (`CurrentContextBar` →
                `ContextSelectorBar`). Replaces the raw-`<select>` filter
                section previously nested with the cost card — same
                underlying state (`selectedFarmId`,
                `selectedProductionUnitId`, `selectedProcessingSiteId`), just
                hoisted to the top of the screen and rendered through the
                shared component. When the selected facility is a processing
                plant, the second selector swaps to processing-site scope
                (label + options + onChange) so the sub-location state stays
                coherent with the facility type. (Cost card itself was
                extracted to `/costs` under T3-C-07, 2026-07-15.) */}
            <ContextSelectorBar
              farm={{
                id: 'lots-filter-farm',
                label: t('lots.filter.facilityLabel'),
                value: selectedFarmId,
                onChange: (value) => {
                  setSelectedFarmId(value)
                  setSelectedProductionUnitId('')
                  setSelectedProcessingSiteId('')
                },
                placeholder: t('lots.filter.allFacilities'),
                options: availableFarms.map((farm) => ({
                  key: farm.id ?? farm.name,
                  value: String(farm.id ?? ''),
                  label: farm.name
                }))
              }}
              productionUnit={{
                id: 'lots-filter-production-unit',
                label: t(selectedFacilityIsProcessingPlant ? 'lots.filter.processingSiteLabel' : 'lots.filter.productionUnitLabel'),
                value: selectedFacilityIsProcessingPlant ? selectedProcessingSiteId : selectedProductionUnitId,
                onChange: (value) => {
                  if (selectedFacilityIsProcessingPlant) {
                    setSelectedProcessingSiteId(value)
                    return
                  }
                  setSelectedProductionUnitId(value)
                },
                disabled: !selectedFarm || (selectedFacilityIsProcessingPlant && availableProcessingSites.length === 0),
                placeholder: !selectedFarm
                  ? t(selectedFacilityIsProcessingPlant ? 'lots.filter.allProcessingSites' : 'lots.filter.allUnits')
                  : t(selectedFacilityIsProcessingPlant ? 'lots.filter.allProcessingSitesInSelectedFacility' : 'lots.filter.allUnitsInSelectedFacility'),
                options: selectedFacilityIsProcessingPlant
                  ? availableProcessingSites.map((site) => ({
                    key: site.id,
                    value: site.id,
                    label: site.label
                  }))
                  : availableProductionUnits.map((unit) => ({
                    key: unit.id ?? unit.name,
                    value: String(unit.id ?? ''),
                    label: unit.name
                  }))
              }}
            />

            {/* v2.2 T3-C-06: sync banners moved BELOW the context selector so
                the operator confirms which facility/PU they're viewing before
                reading sync feedback (peer convention — see Water/Feed/
                Biomass/Datatable). Was previously between the header and the
                hydration gate. */}
            {statusMessage && computeSyncBannerVisibility({
              hasActionMessage: Boolean(statusMessage),
              hasAttentionMessage: Boolean(lotSyncAttentionMessage),
              hasLastResultSummary: Boolean(lotSyncLastResultSummary)
            }).showActionMessage && (
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#ecfdf5', color: '#047857', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{statusMessage}</span>
                {stockedCostLink && stockedCostLink.farmId === selectedFarmId && stockedCostLink.message === statusMessage ? (
                  <Link
                    data-testid="lots-stock-units-register-cost"
                    to={`/costs?farmId=${encodeURIComponent(stockedCostLink.farmId)}&lotGroup=${encodeURIComponent(stockedCostLink.rootId)}&costCategory=G${stockedCostLink.date ? `&costDate=${stockedCostLink.date}` : ''}`}
                    style={{ fontWeight: 700, color: '#065f46' }}
                  >
                    {t(stockedCostLink.eggs ? 'lots.stockUnits.registerCostEggs' : 'lots.stockUnits.registerCostFish')}
                  </Link>
                ) : null}
              </div>
            )}

            {lotSyncLastResultSummary && (
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', color: '#1d4ed8' }}>
                <div>{lotSyncLastResultSummary.resultLabel}</div>
                <div>{lotSyncLastResultSummary.attemptedAtLabel}</div>
              </div>
            )}

            {lotSyncAttentionMessage && (
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fff7ed', color: '#9a3412' }}>
                <div>{lotSyncAttentionMessage}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <GradientButton
                    type="button"
                    size="sm"
                    onClick={() => {
                      void handleLotSyncRetry()
                    }}
                    loading={retryingLotSync}
                    disabled={retryingLotSync || loadingLots}
                  >
                    {retryingLotSync ? t('lots.sync.retrying') : t('lots.sync.retryAction')}
                  </GradientButton>
                  <GradientButton
                    type="button"
                    size="sm"
                    onClick={handleManualRefresh}
                    disabled={loadingLots || retryingLotSync}
                  >
                    {loadingLots ? t('lots.sync.refreshing') : t('lots.sync.refreshAction')}
                  </GradientButton>
                </div>
              </div>
            )}

            {lotsError && (
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                {lotsError}
              </div>
            )}

            <section
              // v2.2 T3-C-06 follow-up (2026-07-13): compact workspace-mode
              // switcher. Was a full-width card (~90px tall) with a label +
              // subtitle + two pills; now a lean right-aligned pill toolbar
              // (~40px). Reclaims vertical space above the primary
              // create-lot / lots-table flow so the operator lands closer to
              // work-in-progress. Pills still route through the same
              // `setWorkspaceMode('operations' | 'stories')` state, so
              // deep-links via `?workspace=stories` continue to work.
              data-testid="lots-workspace-switcher"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}
            >
              {(['operations', 'stories'] as const).map((mode) => {
                const isSelected = workspaceMode === mode
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setWorkspaceMode(mode)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 999,
                      border: isSelected ? '1px solid #0f766e' : '1px solid #cbd5e1',
                      backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                      color: isSelected ? '#0f766e' : '#334155',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {t((mode === 'operations' ? 'lots.workspace.operations' : 'lots.workspace.story') as TranslationKey)}
                  </button>
                )
              })}
            </section>

            {isStoryWorkspaceActive ? (
              <>
                <section style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ flex: '1 1 760px', minWidth: 0, display: 'grid', gap: 16 }}>
                    <div style={{ border: '1px solid #fde68a', borderRadius: 12, backgroundColor: '#fffbeb', padding: 16, display: 'grid', gap: 6 }}>
                      <strong style={{ fontSize: 14, color: '#78350f' }}>{t('lots.workspace.story' as TranslationKey)}</strong>
                      <div style={{ fontSize: 13, color: '#92400e' }}>{t('lots.workspace.story.subtitle' as TranslationKey)}</div>
                      {selectedStoryLineageRootId ? (
                        <div style={{ fontSize: 12, color: '#78350f' }}>
                          {t('lots.workspace.story.activeSelection' as TranslationKey, { lot: selectedStoryLotDisplayLabel })}
                        </div>
                      ) : null}
                    </div>

                    <LotStoryLibraryPanel
                      t={t}
                      libraryState={lotStoryLibraryState}
                      libraryActions={lotStoryLibraryActions}
                      presentation={lotStoryLibraryPresentation}
                    />
                  </div>

                  <div style={{ flex: '0 1 460px', minWidth: 320, border: '1px solid #fde68a', borderRadius: 12, backgroundColor: '#fffbeb', padding: 16, alignSelf: 'flex-start' }}>
                    {selectedStoryLineageRootId ? (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
                          <div>
                            <h4 style={{ margin: '0 0 4px 0' }}>{t('lots.story.title', { lot: selectedStoryLotDisplayLabel })}</h4>
                            <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>{t('lots.story.subtitle')}</p>
                          </div>
                          <button
                            type="button"
                            onClick={closeStoryPreview}
                            style={{
                              padding: '6px 10px',
                              borderRadius: 6,
                              border: '1px solid #fcd34d',
                              backgroundColor: '#ffffff',
                              color: '#78350f',
                              cursor: 'pointer'
                            }}
                          >
                            {t('sidebar.close')}
                          </button>
                        </div>

                        {storyPreviewLoading ? (
                          <div style={{ fontSize: 13, color: '#92400e' }}>{t('lots.story.loading')}</div>
                        ) : storyPreviewError ? (
                          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
                            {storyPreviewError}
                          </div>
                        ) : !storyPreview ? (
                          <div style={{ fontSize: 13, color: '#92400e' }}>{t('lots.story.empty')}</div>
                        ) : (
                          <div style={{ display: 'grid', gap: 16 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                              <span style={{ ...getLotStoryReadinessBadgeStyle(storyPreview.readiness.status), padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                {getLotStoryReadinessLabel(t, storyPreview.readiness.status)}
                              </span>
                              <span style={{ fontSize: 12, color: '#92400e' }}>
                                {t('lots.story.summary.generatedAt')}: {formatHistoryTimestamp(storyPreview.generatedAt)}
                              </span>
                            </div>

                            {renderStoryPriorityCallouts(storyPreview)}

                            {renderStoryProjectionReadOnlySections({
                              projection: storyPreview,
                              keyPrefix: selectedStoryPreviewVersion?.artifactVersionId ?? storyPreview.lineageRootId,
                              accentText: '#78350f',
                              accentBorder: '#fde68a',
                              middleContent: renderStoryDraftPanel()
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: '#475569' }}>
                        {t('lots.workspace.story.empty' as TranslationKey)}
                      </div>
                    )}
                  </div>
                </section>
              </>
            ) : (
              <>
            {/* Office UX v0 (findings #95): one toolbar instead of the quick-actions,
                "Sembrar en varias unidades" and split/merge cards. Split / merge
                now start from a lot's Acciones menu. */}
            {canEditLots && (
              <section
                data-testid="lots-toolbar"
                style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
              >
                <GradientButton
                  type="button"
                  onClick={openNewLotPanel}
                  data-testid="lots-new-lot"
                  title={t('lots.newLot.hint')}
                >
                  {t('lots.newLot.button')}
                </GradientButton>
                {canUseLineageTools && selectedFarm ? (
                  <GradientButton
                    type="button"
                    variant="secondary"
                    onClick={() => setIsStockIntoUnitsOpen(true)}
                    data-testid="lots-stock-into-units-open"
                    title={t('lots.stockUnits.subtitle')}
                  >
                    {t('lots.stockUnits.title')}
                  </GradientButton>
                ) : null}
              </section>
            )}

            {processingSiteSummary ? (
              // v2.2 T3-C-05: collapsible secondary section (mirrors the
              // Biomass `<details>` pattern from finding #69). Collapsed by
              // default so the operator lands on the primary lots table +
              // create form without scrolling past this KPI card.
              <details
                data-testid="lots-processing-site-summary"
                style={{ border: '1px solid #fde68a', borderRadius: 12, padding: 16, backgroundColor: '#fffbeb' }}
              >
                <summary style={{ cursor: 'pointer' }}>
                  <h3 style={{ margin: 0, display: 'inline' }}>{t('lots.processingSiteSummary.title')}</h3>
                </summary>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginTop: 12, marginBottom: 12 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                      {t('lots.processingSiteSummary.subtitle', { site: selectedProcessingSiteLabel })}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                  <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.totalLots')}</strong>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#78350f' }}>{processingSiteSummary.totalLots}</div>
                  </div>
                  <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.activeLots')}</strong>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#78350f' }}>{processingSiteSummary.activeLots}</div>
                  </div>
                  <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.totalBiomass')}</strong>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#78350f' }}>
                      {processingSiteSummary.totalBiomassKg === null ? '—' : String(Number(processingSiteSummary.totalBiomassKg.toFixed(2)))}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.species')}</strong>
                    <div style={{ fontSize: 14, color: '#78350f' }}>
                      {processingSiteSummary.speciesLabels.length > 0
                        ? processingSiteSummary.speciesLabels.join(', ')
                        : t('lots.processingSiteSummary.noSpecies')}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.latestActivity')}</strong>
                    <div style={{ fontSize: 14, color: '#78350f' }}>
                      {processingSiteSummary.latestActivityLotKey
                        ? t('lots.processingSiteSummary.latestActivityValue', {
                          lot: processingSiteSummary.latestActivityLotKey,
                          timestamp: formatProcessingSiteActivityTimestamp(processingSiteSummary.latestActivityAt)
                        })
                        : t('lots.processingSiteSummary.noActivity')}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 8 }}>
                  <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.recentLots')}</strong>
                  {processingSiteSummary.recentLots.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#78350f' }}>{t('lots.processingSiteSummary.noRecentLots')}</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {processingSiteSummary.recentLots.map((lot) => {
                        const provenance = processingSiteDisplayedLotProvenanceByLotId[lot.lotId]
                        const provenanceEntries = processingSiteDisplayedLotProvenanceEntriesByLotId[lot.lotId] ?? []
                        const previousProvenance = provenanceEntries[1]
                        const provenanceOrigin = provenance ? formatRecentLotProvenanceOrigin(provenance) : undefined
                        const previousProvenanceOrigin = previousProvenance ? formatRecentLotProvenanceOrigin(previousProvenance) : undefined

                        return (
                          <div key={`${lot.lotId}-${lot.activityAt}`} style={{ display: 'grid', gap: 2 }}>
                            <strong style={{ fontSize: 13, color: '#78350f' }}>{lot.lotKey}</strong>
                            <div style={{ fontSize: 12, color: '#92400e' }}>
                              {t('lots.processingSiteSummary.recentLotMeta', {
                                stage: t(stageLabelKey[lot.stage]),
                                timestamp: formatProcessingSiteActivityTimestamp(lot.activityAt)
                              })}
                            </div>
                            {lot.sourceLotKey ? (
                              <div style={{ fontSize: 12, color: '#92400e' }}>
                                {t('lots.processingSiteSummary.recentLotSource', { sourceLot: lot.sourceLotKey })}
                              </div>
                            ) : null}
                            {provenance && provenanceOrigin ? (
                              <div style={{ fontSize: 12, color: '#92400e' }}>
                                {t('lots.processingSiteSummary.recentLotProvenanceWithTimestamp', {
                                  eventType: t(lotHistoryEventLabelKey[provenance.eventType]),
                                  origin: provenanceOrigin,
                                  timestamp: formatHistoryTimestamp(provenance.recordedAt)
                                })}
                              </div>
                            ) : null}
                            {previousProvenance && previousProvenanceOrigin ? (
                              <div style={{ fontSize: 12, color: '#92400e' }}>
                                {t('lots.processingSiteSummary.recentLotPreviousProvenanceWithTimestamp', {
                                  eventType: t(lotHistoryEventLabelKey[previousProvenance.eventType]),
                                  origin: previousProvenanceOrigin,
                                  timestamp: formatHistoryTimestamp(previousProvenance.recordedAt)
                                })}
                              </div>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 12, border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 8 }}>
                  <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.siteActivity')}</strong>
                  {processingSiteActivityEntries.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#78350f' }}>{t('lots.processingSiteSummary.noSiteActivity')}</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ padding: '6px 10px', borderRadius: 999, backgroundColor: '#ecfdf5', color: '#047857', fontSize: 12, fontWeight: 600 }}>
                          {t('lots.processingSiteSummary.siteActivityTransfers', { count: processingSiteActivityBreakdown.transferCount })}
                        </div>
                        <div style={{ padding: '6px 10px', borderRadius: 999, backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>
                          {t('lots.processingSiteSummary.siteActivityMoves', { count: processingSiteActivityBreakdown.moveCount })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(['all', 'transfer', 'move'] as const).map((filterValue) => {
                          const isSelected = processingSiteActivityFilter === filterValue
                          return (
                            <button
                              key={filterValue}
                              type="button"
                              onClick={() => setProcessingSiteActivityFilter(filterValue)}
                              style={{
                                padding: '6px 10px',
                                borderRadius: 999,
                                border: isSelected ? '1px solid #92400e' : '1px solid #fcd34d',
                                backgroundColor: isSelected ? '#fef3c7' : '#ffffff',
                                color: '#92400e',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              {t(
                                filterValue === 'transfer'
                                  ? 'lots.processingSiteSummary.siteActivityFilterTransfers'
                                  : filterValue === 'move'
                                    ? 'lots.processingSiteSummary.siteActivityFilterMoves'
                                    : 'lots.processingSiteSummary.siteActivityFilterAll'
                              )}
                            </button>
                          )
                        })}
                      </div>
                      {processingSiteActivityOriginSummary.length > 0 ? (
                        <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#fffbeb', padding: 10, display: 'grid', gap: 6 }}>
                          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.originSummaryTitle')}</strong>
                          <div style={{ display: 'grid', gap: 4 }}>
                            {processingSiteActivityOriginSummary.map((entry, index) => {
                              const originRecencyBadge = getProcessingSiteActivityOriginRecencyBadge(entry.latestRecordedAt)
                              const originRecencyBadgeStyle = originRecencyBadge === 'stale'
                                ? { backgroundColor: '#fef2f2', color: '#b91c1c' }
                                : originRecencyBadge === 'last24h'
                                  ? { backgroundColor: '#ecfdf5', color: '#065f46' }
                                  : originRecencyBadge === 'last7d'
                                    ? { backgroundColor: '#eff6ff', color: '#1d4ed8' }
                                    : { backgroundColor: '#f8fafc', color: '#475569' }

                              return (
                              <div key={`${entry.fromLocationType ?? 'unknown'}-${entry.fromLocationId ?? 'unknown'}-${entry.latestRecordedAt}-${index}`} style={{ fontSize: 12, color: '#78350f' }}>
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  borderRadius: 999,
                                  padding: '1px 8px',
                                  marginRight: 6,
                                  backgroundColor: originRecencyBadgeStyle.backgroundColor,
                                  color: originRecencyBadgeStyle.color,
                                  border: '1px solid #e5e7eb',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.2
                                }}>
                                  {formatProcessingSiteActivityOriginRecencyBadgeLabel(
                                    originRecencyBadge
                                  )}
                                </span>
                                {t('lots.processingSiteSummary.originSummaryEntry', {
                                  origin: formatProcessingSiteActivityOriginSummaryLabel(entry),
                                  total: entry.totalCount,
                                  transfers: entry.transferCount,
                                  moves: entry.moveCount,
                                  timestamp: formatHistoryTimestamp(entry.latestRecordedAt)
                                })}
                              </div>
                              )
                            })}
                          </div>
                          <div style={{ fontSize: 12, color: '#78350f' }}>
                            {t('lots.processingSiteSummary.originRecencyBreakdown', {
                              last24h: processingSiteActivityOriginRecencyBreakdown.last24HoursCount,
                              last7d: processingSiteActivityOriginRecencyBreakdown.last7DaysCount,
                              stale: processingSiteActivityOriginRecencyBreakdown.staleCount,
                              unknown: processingSiteActivityOriginRecencyBreakdown.unknownCount
                            })}
                          </div>
                        </div>
                      ) : null}
                      {processingSiteActivityRecencySummary.totalEntries > 0 ? (
                        <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#fffbeb', padding: 10, display: 'grid', gap: 4 }}>
                          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.recencyWindowTitle')}</strong>
                          <div style={{ fontSize: 12, color: '#78350f' }}>
                            {t('lots.processingSiteSummary.recencyWindowValue', {
                              last24h: processingSiteActivityRecencySummary.last24HoursCount,
                              last7d: processingSiteActivityRecencySummary.last7DaysCount
                            })}
                          </div>
                          {processingSiteActivityRecencySummary.isStale ? (
                            <div style={{ fontSize: 12, color: '#b45309' }}>
                              {t('lots.processingSiteSummary.recencyWindowStaleWarning')}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {latestFilteredProcessingSiteActivityEntry ? (() => {
                        const origin = formatRecentLotProvenanceOrigin(latestFilteredProcessingSiteActivityEntry)
                        const latestArrivalLot = lotById.get(latestFilteredProcessingSiteActivityEntry.lotId) ?? null
                        if (!origin) return null

                        return (
                          <div style={{ border: '1px solid #fde68a', borderRadius: 10, backgroundColor: '#fffbeb', padding: 10, display: 'grid', gap: 4 }}>
                            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.processingSiteSummary.latestArrival')}</strong>
                            <div style={{ fontSize: 12, color: '#78350f' }}>
                              {t('lots.processingSiteSummary.latestArrivalEntry', {
                                lot: lotKeyById.get(latestFilteredProcessingSiteActivityEntry.lotId) ?? latestFilteredProcessingSiteActivityEntry.lotId,
                                eventType: t(lotHistoryEventLabelKey[latestFilteredProcessingSiteActivityEntry.eventType]),
                                origin,
                                timestamp: formatHistoryTimestamp(latestFilteredProcessingSiteActivityEntry.recordedAt)
                              })}
                            </div>
                            {latestArrivalLot ? (
                              <div>
                                <button
                                  type="button"
                                  onClick={() => handleHistoryToggle(latestArrivalLot)}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    border: '1px solid #f59e0b',
                                    backgroundColor: selectedHistoryLotId === latestArrivalLot.id ? '#fef3c7' : '#ffffff',
                                    color: '#92400e',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >
                                  {selectedHistoryLotId === latestArrivalLot.id ? t('lots.history.button.hide') : t('lots.history.button.show')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        )
                      })() : null}
                      {filteredProcessingSiteActivityEntries.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#78350f' }}>{t('lots.processingSiteSummary.noSiteActivityForFilter')}</div>
                      ) : filteredProcessingSiteActivityEntries.map((entry) => {
                        const origin = formatRecentLotProvenanceOrigin(entry)
                        if (!origin) return null

                        return (
                          <div key={`${entry.lotId}-${entry.eventType}-${entry.recordedAt}`} style={{ fontSize: 12, color: '#92400e' }}>
                            {t('lots.processingSiteSummary.siteActivityEntry', {
                              lot: lotKeyById.get(entry.lotId) ?? entry.lotId,
                              eventType: t(lotHistoryEventLabelKey[entry.eventType]),
                              origin,
                              timestamp: formatHistoryTimestamp(entry.recordedAt)
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </details>
            ) : null}

            <Drawer
              open={isStockIntoUnitsOpen}
              onClose={() => setIsStockIntoUnitsOpen(false)}
              title={t('lots.stockUnits.title')}
            >
              {selectedFarm ? (
                <StockIntoUnitsForm
                  farmId={selectedFarmId}
                  farmName={selectedFarm.name}
                  units={(selectedFarm.productionUnits ?? []).map((unit: { id: string; name?: string }) => ({
                    id: String(unit.id),
                    name: String(unit.name ?? unit.id)
                  }))}
                  existingLots={lineageLots}
                  farmMetadata={(selectedFarm as { metadata?: Record<string, unknown> }).metadata ?? null}
                  disabled={!canUseLineageTools}
                  onDone={async ({ parentLot, childLots, pendingSync }) => {
                    invalidateLotCache({ farmId: selectedFarmId })
                    const stockedMessage = t('lots.stockUnits.success', { name: childLots[0]?.lotKey ?? '', count: childLots.length })
                    setStatusMessage(stockedMessage)
                    setStockedCostLink(parentLot?.id && childLots.length > 1
                      ? {
                          message: stockedMessage,
                          farmId: selectedFarmId,
                          rootId: childLots[0]?.lineageRootId || parentLot.id,
                          date: String(parentLot.stockingDate ?? childLots[0]?.stockingDate ?? '').slice(0, 10),
                          eggs: childLots.some((lot) => isHatcheryLot(lot))
                        }
                      : null)
                    setIsStockIntoUnitsOpen(false)
                    await refreshVisibleAndLineageLots({ preferLocal: true })
                    // findings #152 (same fix as v2.1 #61): the per-unit arrival / table copies go through the
                    // outbox, so re-read the banner's sync status once they are sent.
                    if (pendingSync) void pendingSync.then(() => refreshLotSyncStatusFnRef.current())
                  }}
                />
              ) : null}
            </Drawer>

            <Drawer
              open={isLineageDrawerOpen}
              onClose={() => setIsLineageDrawerOpen(false)}
              title={t('lots.lineage.title')}
            >
              <p style={{ marginTop: 0, marginBottom: 12, color: '#4b5563' }}>
                {t('lots.lineage.subtitle')}
              </p>

          {!selectedFarmId ? (
            <div style={{ fontSize: 13, color: '#6b7280' }}>{t('lots.lineage.hint.selectContext')}</div>
          ) : null}

          {!canEditLots ? (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
              {t('lots.lineage.readonly.banner', { role: principal?.role ?? 'viewer' })}
            </div>
          ) : null}

          {selectedFacilityIsProcessingPlant ? (
            <div style={{ marginTop: 8, fontSize: 13, color: '#92400e' }}>
              {t('lots.lineage.processingPlantNotice')}
            </div>
          ) : null}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
            <div style={{ flex: '1 1 360px', minWidth: 280, border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, backgroundColor: '#f9fafb' }}>
              <h4 style={{ marginTop: 0, marginBottom: 12 }}>{t('lots.lineage.split.title')}</h4>

              {splitError && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{splitError}</div>}

              {lineageQuickOptions.length > 0 ? (
                <div
                  style={{
                    marginBottom: 10,
                    borderRadius: 10,
                    border: '1px solid #dbeafe',
                    backgroundColor: '#ffffff',
                    padding: 12,
                    display: 'grid',
                    gap: 8
                  }}
                >
                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    <span>{t('lots.lineage.quickPickerLabel')}</span>
                    <input
                      type="search"
                      value={splitSourceSearchQuery}
                      onChange={(event) => setSplitSourceSearchQuery(event.target.value)}
                      disabled={!canUseLineageTools || splitSaving || loadingLineageLots}
                      placeholder={t('lots.lineage.quickPickerPlaceholder')}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    />
                  </label>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    {t(splitSourceSearchQuery.trim()
                      ? 'lots.lineage.quickPickerMatches'
                      : 'lots.lineage.quickPickerSuggested')}
                  </div>
                  {visibleSplitQuickOptions.length > 0 ? (
                    <div style={{ display: 'grid', gap: 6, maxHeight: 208, overflowY: 'auto' }}>
                      {visibleSplitQuickOptions.map((option) => {
                        const isSelected = option.id === splitSourceLotId
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              setSplitSourceLotId(option.id)
                              setSplitSourceSearchQuery('')
                            }}
                            disabled={!canUseLineageTools || splitSaving || loadingLineageLots}
                            style={{
                              textAlign: 'left',
                              borderRadius: 8,
                              border: isSelected ? '1px solid #0f766e' : '1px solid #cbd5e1',
                              backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                              padding: '10px 12px',
                              display: 'grid',
                              gap: 2,
                              cursor: canUseLineageTools && !splitSaving && !loadingLineageLots ? 'pointer' : 'not-allowed',
                              opacity: canUseLineageTools && !splitSaving && !loadingLineageLots ? 1 : 0.55
                            }}
                          >
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{option.label}</span>
                            <span style={{ fontSize: 12, color: '#475569' }}>{option.productionUnitLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {t('lots.lineage.quickPickerEmpty')}
                    </div>
                  )}
                </div>
              ) : null}

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.split.sourceLotLabel')}</span>
                <select
                  value={splitSourceLotId}
                  onChange={(event) => {
                    setSplitSourceLotId(event.target.value)
                    setSplitSourceSearchQuery('')
                  }}
                  disabled={!canUseLineageTools || splitSaving || loadingLineageLots}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                >
                  <option value="">{t('lots.lineage.split.selectLot')}</option>
                  {lineageSourceOptionGroups.map((group) => (
                    <optgroup key={group.productionUnitId || group.label} label={group.label}>
                      {group.options.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>

              {selectedSplitSourceLot ? renderLineageSourceContext(selectedSplitSourceLot) : null}

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.split.recordedAtLabel')}</span>
                <DateTimeLocalInput
                  value={splitRecordedAt}
                  onChangeValue={setSplitRecordedAt}
                  disabled={!canUseLineageTools || splitSaving}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.split.newLotKeyALabel')}</span>
                <input
                  type="text"
                  value={splitChildKeyA}
                  onChange={(event) => setSplitChildKeyA(event.target.value)}
                  disabled={!canUseLineageTools || splitSaving}
                  placeholder={t('lots.lineage.split.placeholderKeyA')}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.split.newLotKeyBLabel')}</span>
                <input
                  type="text"
                  value={splitChildKeyB}
                  onChange={(event) => setSplitChildKeyB(event.target.value)}
                  disabled={!canUseLineageTools || splitSaving}
                  placeholder={t('lots.lineage.split.placeholderKeyB')}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.split.notesLabel')}</span>
                <textarea
                  value={splitNotes}
                  onChange={(event) => setSplitNotes(event.target.value)}
                  disabled={!canUseLineageTools || splitSaving}
                  rows={2}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GradientButton type="button" disabled={!canUseLineageTools || splitSaving} onClick={() => void handleSplit()}>
                  {splitSaving ? t('lots.saving') : t('lots.lineage.split.recordButton')}
                </GradientButton>
              </div>
            </div>

            <div style={{ flex: '1 1 360px', minWidth: 280, border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, backgroundColor: '#f9fafb' }}>
              <h4 style={{ marginTop: 0, marginBottom: 12 }}>{t('lots.lineage.merge.title')}</h4>

              {mergeError && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{mergeError}</div>}

              {lineageQuickOptions.length > 0 ? (
                <div
                  style={{
                    marginBottom: 10,
                    borderRadius: 10,
                    border: '1px solid #dbeafe',
                    backgroundColor: '#ffffff',
                    padding: 12,
                    display: 'grid',
                    gap: 8
                  }}
                >
                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    <span>{t('lots.lineage.quickPickerLabel')}</span>
                    <input
                      type="search"
                      value={mergeSourceSearchQuery}
                      onChange={(event) => setMergeSourceSearchQuery(event.target.value)}
                      disabled={!canUseLineageTools || mergeSaving || loadingLineageLots}
                      placeholder={t('lots.lineage.quickPickerPlaceholder')}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    />
                  </label>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    {t(mergeSourceSearchQuery.trim()
                      ? 'lots.lineage.quickPickerMatches'
                      : 'lots.lineage.quickPickerSuggested')}
                  </div>
                  {visibleMergeQuickOptions.length > 0 ? (
                    <div style={{ display: 'grid', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                      {visibleMergeQuickOptions.map((option) => {
                        const isSelected = mergeSourceLotIds.includes(option.id)
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => toggleMergeSourceLotId(option.id)}
                            disabled={!canUseLineageTools || mergeSaving || loadingLineageLots}
                            style={{
                              textAlign: 'left',
                              borderRadius: 8,
                              border: isSelected ? '1px solid #0f766e' : '1px solid #cbd5e1',
                              backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                              padding: '10px 12px',
                              display: 'grid',
                              gap: 2,
                              cursor: canUseLineageTools && !mergeSaving && !loadingLineageLots ? 'pointer' : 'not-allowed',
                              opacity: canUseLineageTools && !mergeSaving && !loadingLineageLots ? 1 : 0.55
                            }}
                          >
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{option.label}</span>
                            <span style={{ fontSize: 12, color: '#475569' }}>{option.productionUnitLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {t('lots.lineage.quickPickerEmpty')}
                    </div>
                  )}
                </div>
              ) : null}

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.merge.sourceLotsLabel')}</span>
                <select
                  multiple
                  value={mergeSourceLotIds}
                  onChange={(event) => {
                    const selected = Array.from(event.target.selectedOptions).map((opt) => opt.value)
                    setMergeSourceLotIds(selected)
                  }}
                  disabled={!canUseLineageTools || mergeSaving || loadingLineageLots}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5', minHeight: 110 }}
                >
                  {lineageSourceOptionGroups.map((group) => (
                    <optgroup key={group.productionUnitId || group.label} label={group.label}>
                      {group.options.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>

              {selectedMergeSourceLots.length > 0 ? (
                <div
                  style={{
                    marginBottom: 10,
                    borderRadius: 10,
                    border: '1px solid #dbeafe',
                    backgroundColor: '#f8fafc',
                    padding: 12,
                    display: 'grid',
                    gap: 8
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13, color: '#0f172a' }}>
                      {t('lots.lineage.merge.selectedSummary', { count: selectedMergeSourceLots.length })}
                    </strong>
                    <button
                      type="button"
                      onClick={() => {
                        setMergeSourceLotIds([])
                        setIsMergeReviewExpanded(false)
                      }}
                      disabled={!canUseLineageTools || mergeSaving}
                      style={{
                        borderRadius: 999,
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#334155',
                        padding: '4px 10px',
                        fontSize: 12,
                        cursor: canUseLineageTools && !mergeSaving ? 'pointer' : 'not-allowed',
                        opacity: canUseLineageTools && !mergeSaving ? 1 : 0.55
                      }}
                    >
                      {t('lots.lineage.merge.clearSelection')}
                    </button>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {mergeSourceChipGroups.map((group) => (
                      <div key={`merge-chip-group-${group.productionUnitId}`} style={{ display: 'grid', gap: 6 }}>
                        {mergeSourceChipGroups.length > 1 ? (
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
                            {t('lots.lineage.merge.groupSummary', {
                              unit: group.productionUnitLabel,
                              count: group.lots.length
                            })}
                          </div>
                        ) : null}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {group.lots.map((lot) => {
                            const index = selectedMergeSourceLots.findIndex((candidate) => candidate.id === lot.id)
                            const canMoveEarlier = index > 0
                            const canMoveLater = index < selectedMergeSourceLots.length - 1

                            return (
                              <div
                                key={`selected-${lot.id}`}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  borderRadius: 999,
                                  border: '1px solid #86efac',
                                  backgroundColor: '#ecfdf5',
                                  color: '#166534',
                                  padding: '6px 10px',
                                  opacity: canUseLineageTools && !mergeSaving ? 1 : 0.55
                                }}
                              >
                                <span style={{ fontWeight: 700 }}>{lot.lotKey ?? lot.id}</span>
                                <span style={{ fontSize: 12, color: '#047857' }}>{group.productionUnitLabel}</span>
                                <button
                                  type="button"
                                  onClick={() => moveMergeSourceLotId(lot.id, 'earlier')}
                                  disabled={!canUseLineageTools || mergeSaving || !canMoveEarlier}
                                  style={{
                                    borderRadius: 999,
                                    border: '1px solid #a7f3d0',
                                    backgroundColor: '#ffffff',
                                    color: '#166534',
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    cursor: canUseLineageTools && !mergeSaving && canMoveEarlier ? 'pointer' : 'not-allowed',
                                    opacity: canUseLineageTools && !mergeSaving && canMoveEarlier ? 1 : 0.45
                                  }}
                                >
                                  {t('lots.lineage.merge.moveSourceEarlier')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveMergeSourceLotId(lot.id, 'later')}
                                  disabled={!canUseLineageTools || mergeSaving || !canMoveLater}
                                  style={{
                                    borderRadius: 999,
                                    border: '1px solid #a7f3d0',
                                    backgroundColor: '#ffffff',
                                    color: '#166534',
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    cursor: canUseLineageTools && !mergeSaving && canMoveLater ? 'pointer' : 'not-allowed',
                                    opacity: canUseLineageTools && !mergeSaving && canMoveLater ? 1 : 0.45
                                  }}
                                >
                                  {t('lots.lineage.merge.moveSourceLater')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleMergeSourceLotId(lot.id)}
                                  disabled={!canUseLineageTools || mergeSaving}
                                  style={{
                                    borderRadius: 999,
                                    border: '1px solid #a7f3d0',
                                    backgroundColor: '#ffffff',
                                    color: '#166534',
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    cursor: canUseLineageTools && !mergeSaving ? 'pointer' : 'not-allowed',
                                    opacity: canUseLineageTools && !mergeSaving ? 1 : 0.55
                                  }}
                                >
                                  {t('lots.lineage.merge.removeSource')}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedMergeSourceLots.length > 0 ? (
                <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                  {mergeSourceReviewState.canToggle ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        borderRadius: 10,
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        padding: '10px 12px'
                      }}
                    >
                      <span style={{ fontSize: 12, color: '#475569' }}>
                        {t(
                          mergeSourceReviewState.isCollapsed
                            ? 'lots.lineage.merge.review.compactSummary'
                            : 'lots.lineage.merge.review.expandedSummary',
                          {
                            shown: visibleMergeReviewLots.length,
                            total: mergeSourceReviewState.totalCount,
                            hidden: mergeSourceReviewState.hiddenCount
                          }
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsMergeReviewExpanded((prev) => !prev)}
                        disabled={!canUseLineageTools || mergeSaving}
                        style={{
                          borderRadius: 999,
                          border: '1px solid #cbd5e1',
                          backgroundColor: '#f8fafc',
                          color: '#334155',
                          padding: '4px 10px',
                          fontSize: 12,
                          cursor: canUseLineageTools && !mergeSaving ? 'pointer' : 'not-allowed',
                          opacity: canUseLineageTools && !mergeSaving ? 1 : 0.55
                        }}
                      >
                        {t(
                          mergeSourceReviewState.isCollapsed
                            ? 'lots.lineage.merge.review.showAll'
                            : 'lots.lineage.merge.review.showLess'
                        )}
                      </button>
                    </div>
                  ) : null}
                  {visibleMergeReviewLots.map((lot) => renderLineageSourceContext(lot))}
                </div>
              ) : null}

              {mergeSelectionGuidance ? (
                <div
                  style={{
                    marginBottom: 10,
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 13,
                    backgroundColor: mergeSelectionGuidance.tone === 'warning'
                      ? '#fffbeb'
                      : mergeSelectionGuidance.tone === 'success'
                        ? '#ecfdf5'
                        : '#eff6ff',
                    color: mergeSelectionGuidance.tone === 'warning'
                      ? '#92400e'
                      : mergeSelectionGuidance.tone === 'success'
                        ? '#047857'
                        : '#1d4ed8',
                    border: mergeSelectionGuidance.tone === 'warning'
                      ? '1px solid #fde68a'
                      : mergeSelectionGuidance.tone === 'success'
                        ? '1px solid #a7f3d0'
                        : '1px solid #bfdbfe'
                  }}
                >
                  {mergeSelectionGuidance.message}
                </div>
              ) : null}

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.merge.recordedAtLabel')}</span>
                <DateTimeLocalInput
                  value={mergeRecordedAt}
                  onChangeValue={setMergeRecordedAt}
                  disabled={!canUseLineageTools || mergeSaving}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.merge.newMergedKeyLabel')}</span>
                <input
                  type="text"
                  value={mergeTargetKey}
                  onChange={(event) => setMergeTargetKey(event.target.value)}
                  disabled={!canUseLineageTools || mergeSaving}
                  placeholder={t('lots.lineage.merge.placeholderKey')}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={mergeMarkInactive}
                  onChange={(event) => setMergeMarkInactive(event.target.checked)}
                  disabled={!canUseLineageTools || mergeSaving}
                />
                <span>{t('lots.lineage.merge.markInactiveLabel')}</span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, marginBottom: 10 }}>
                <span>{t('lots.lineage.merge.notesLabel')}</span>
                <textarea
                  value={mergeNotes}
                  onChange={(event) => setMergeNotes(event.target.value)}
                  disabled={!canUseLineageTools || mergeSaving}
                  rows={2}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                />
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GradientButton type="button" disabled={!canUseLineageTools || mergeSaving} onClick={() => void handleMerge()}>
                  {mergeSaving ? t('lots.saving') : t('lots.lineage.merge.recordButton')}
                </GradientButton>
              </div>
            </div>
          </div>
        </Drawer>

        {/* Office UX v0 (findings #95): the lot list fills the page; a lot's
            detail, the create / edit / move form and the operations open in
            side panels instead of permanent sections. */}
        <section data-testid="lots-list" style={{ display: 'grid', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12
            }}
          >
            <div>
              <h3 style={{ margin: '0 0 4px 0' }}>{t('lots.table.sectionTitle')}</h3>
              <p style={{ margin: '0 0 4px 0', fontSize: 13, color: '#111827' }}>
                {t(lotsTableSummary.key, lotsTableSummary.variables)}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#4b5563' }}>{t('lots.button.refreshLotsTooltip')}</p>
            </div>
            <GradientButton
              type="button"
              size="sm"
              onClick={handleManualRefresh}
              disabled={loadingLots}
              loading={loadingLots}
              title={t('lots.button.refreshLotsTooltip')}
              aria-label={t('lots.button.refreshLotsTooltip')}
              style={{ minWidth: 170, whiteSpace: 'nowrap' }}
            >
              {loadingLots ? t('lots.status.refreshing') : t('lots.button.refreshLots')}
            </GradientButton>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <PillTabs<LotListFilter>
              ariaLabel={t('lots.list.filterLabel')}
              testId="lots-stage-tabs"
              active={lotListFilter}
              onChange={setLotListFilter}
              tabs={LOT_LIST_FILTERS.map((filter) => ({
                key: filter,
                label: filter === 'all' ? t('lots.list.filter.all') : t(stageLabelKey[filter]),
                count: lotStageCounts[filter],
                testId: `lots-stage-tab-${filter}`
              }))}
            />
            <input
              type="search"
              data-testid="lots-search"
              value={lotSearchQuery}
              onChange={(event) => setLotSearchQuery(event.target.value)}
              placeholder={t('lots.list.searchPlaceholder')}
              aria-label={t('lots.list.searchPlaceholder')}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5f5', minWidth: 240 }}
            />
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid #e5e7eb', borderRadius: 12, backgroundColor: '#ffffff' }}>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>{t('lots.table.lot')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.facility')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.productionUnit')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.status')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.species')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.stockingDate')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.harvestDate')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.biomassKg')}</th>
                  <th style={{ padding: 10 }}>{t('lots.table.lineage')}</th>
                  <th style={{ padding: 10, width: 1 }}>{t('lots.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {isHydratingContext || loadingLots ? (
                  <tr>
                    <td colSpan={10} style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
                      {t('lots.table.loading')}
                    </td>
                  </tr>
                ) : displayedLots.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
                      {t(lotsTableEmptyKey)}
                    </td>
                  </tr>
                ) : listedLots.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: 18, textAlign: 'center', color: '#6b7280' }} data-testid="lots-list-empty-filter">
                      {t('lots.list.emptyFilter')}{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setLotListFilter('all')
                          setLotSearchQuery('')
                        }}
                        style={{ border: 'none', background: 'none', color: '#1d4ed8', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                      >
                        {t('lots.list.showAll')}
                      </button>
                    </td>
                  </tr>
                ) : (
                  listedLots.map((lot) => {
                    const displayedLineage = resolveDisplayedLotLineageSummary({
                      lotId: lot.id,
                      ledgerLineageSummaryByLotId
                    })
                    const parents = displayedLineage.parents
                    const children = displayedLineage.children
                    const hasAny = parents.length > 0 || children.length > 0
                    const tooltipParts: string[] = []
                    if (parents.length > 0) {
                      tooltipParts.push(`${t('lots.lineage.parentsPrefix')} ${parents.map((id) => lotKeyById.get(id) ?? id).join(', ')}`)
                    }
                    if (children.length > 0) {
                      tooltipParts.push(`${t('lots.lineage.childrenPrefix')} ${children.map((id) => lotKeyById.get(id) ?? id).join(', ')}`)
                    }
                    const tooltip = tooltipParts.join('\n')
                    const displayedLotProvenance = processingSiteDisplayedLotProvenanceByLotId[lot.id]
                    const displayedLotProvenanceOrigin = displayedLotProvenance
                      ? formatRecentLotProvenanceOrigin(displayedLotProvenance)
                      : undefined
                    const isOpen = selectedHistoryLotId === lot.id
                    return (
                      <tr
                        key={lot.id}
                        data-testid={`lots-row-${lot.id}`}
                        onClick={() => openLotDetail(lot, 'summary')}
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer', backgroundColor: isOpen ? '#eff6ff' : undefined }}
                      >
                        <td style={{ padding: 10, fontWeight: 600 }}>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation()
                                openLotDetail(lot, 'summary')
                              }}
                              style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
                            >
                              {lot.lotKey}
                            </button>
                            {isHatcheryLot(lot) ? (
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1' }}>{t('lots.newLot.eggs')}</span>
                            ) : null}
                            {selectedFacilityIsProcessingPlant && selectedProcessingSiteId && displayedLotProvenance && displayedLotProvenanceOrigin ? (
                              <span style={{ fontSize: 12, fontWeight: 400, color: '#4b5563' }}>
                                {t('lots.processingSiteSummary.recentLotProvenance', {
                                  eventType: t(lotHistoryEventLabelKey[displayedLotProvenance.eventType]),
                                  origin: displayedLotProvenanceOrigin
                                })}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td style={{ padding: 10 }}>{farmLookup.get(lot.farmId) ?? lot.farmId}</td>
                        <td style={{ padding: 10 }}>{resolveLotSubLocationLabel(lot) ?? '—'}</td>
                        <td style={{ padding: 10 }}>{t(stageLabelKey[lot.stage])}</td>
                        <td style={{ padding: 10 }}>{lot.species ?? '—'}</td>
                        <td style={{ padding: 10 }}>{lot.stockingDate ? dateInputValue(lot.stockingDate) : '—'}</td>
                        <td style={{ padding: 10 }}>{lot.harvestDate ? dateInputValue(lot.harvestDate) : '—'}</td>
                        <td style={{ padding: 10 }}>{lot.biomassKg ?? '—'}</td>
                        <td style={{ padding: 10 }} title={tooltip}>
                          {hasAny ? t('lots.lineage.summary', { parents: parents.length, children: children.length }) : '—'}
                        </td>
                        <td style={{ padding: 10 }} onClick={(event) => event.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {/* Traceability stays one click from the list (GLOBALG.A.P. / ASC / BAP). */}
                            <button
                              type="button"
                              data-testid={`lots-row-history-${lot.id}`}
                              onClick={() => openLotDetail(lot, 'history')}
                              style={{
                                padding: '6px 10px',
                                borderRadius: 6,
                                border: '1px solid #9ca3af',
                                backgroundColor: isOpen && lotDetailTab === 'history' ? '#e0f2fe' : '#f9fafb',
                                color: '#111827',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {t('lots.history.button.show')}
                            </button>
                            <LotActionsMenu
                              label={t('lots.actions.menu')}
                              testId={`lots-row-actions-${lot.id}`}
                              items={buildLotActionItems(lot)}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          <DeletedLotsSection
            farmId={selectedFarmId}
            timeZone={farmTimeZone(selectedFarmId)}
            unitName={(unitId) => (unitId ? (productionUnitLookup.get(unitId) ?? '—') : '—')}
            canEdit={canEditLots}
            onRestored={() => { void refreshVisibleAndLineageLots({ preferLocal: false }) }}
          />
        </section>

        <Drawer
          open={Boolean(selectedHistoryLotId)}
          onClose={closeHistoryPanel}
          title={t('lots.detail.title', { lot: selectedHistoryLotDisplayLabel })}
          width="wide"
        >
          {selectedHistoryLotId ? (
            <div data-testid="lots-detail" style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#475569' }}>
                  {selectedHistoryLotCurrentLocationLabel} · {selectedHistoryLotCurrentStageLabel}
                  {selectedHistoryLot && isHatcheryLot(selectedHistoryLot) ? ` · ${t('lots.newLot.eggs')}` : ''}
                </span>
                {selectedHistoryLot ? (
                  <LotActionsMenu
                    label={t('lots.actions.menu')}
                    testId="lots-detail-actions"
                    items={buildLotActionItems(selectedHistoryLot)}
                  />
                ) : null}
              </div>

              <PillTabs<LotDetailTab>
                ariaLabel={t('lots.detail.tabsLabel')}
                testId="lots-detail-tabs"
                active={activeLotDetailTab}
                onChange={setLotDetailTab}
                tabs={lotDetailTabs.map((tab) => ({
                  key: tab,
                  label: t(LOT_DETAIL_TAB_LABEL_KEY[tab]),
                  testId: `lots-detail-tab-${tab}`
                }))}
              />

              {activeLotDetailTab === 'summary' ? (
                <div data-testid="lots-detail-summary" style={{ display: 'grid', gap: 12 }}>
                  {selectedHistoryLot ? (
                    <div style={{ marginBottom: 12, padding: 12, borderRadius: 10, border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', display: 'grid', gap: 10 }}>
                      <strong style={{ fontSize: 13, color: '#1d4ed8' }}>{t('lots.history.current.title')}</strong>
                      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.history.current.location')}</span>
                          <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLotCurrentLocationLabel}</span>
                        </div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.history.field.stage')}</span>
                          <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLotCurrentStageLabel}</span>
                        </div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.history.field.species')}</span>
                          <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLot.species?.trim() || '—'}</span>
                        </div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.history.field.biomassKg')}</span>
                          <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLot.biomassKg == null ? '—' : String(selectedHistoryLot.biomassKg)}</span>
                        </div>
                        {selectedHistoryLotLatestProvenanceLabel ? (
                          <div style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.history.current.latestOrigin')}</span>
                            <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLotLatestProvenanceLabel}</span>
                          </div>
                        ) : null}
                      </div>
                      {selectedHistoryLot.locationType === 'processing-site' && selectedHistoryLotRecentProvenanceEntries.length > 0 ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <span style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.history.current.timeline')}</span>
                          <div style={{ display: 'grid', gap: 6 }}>
                            {selectedHistoryLotRecentProvenanceEntries.map((provenance, index) => {
                              const origin = formatRecentLotProvenanceOrigin(provenance)
                              if (!origin) {
                                return null
                              }

                              const chainEntry = t('lots.history.current.recentChainValue', {
                                eventType: t(lotHistoryEventLabelKey[provenance.eventType]),
                                origin,
                                timestamp: formatHistoryTimestamp(provenance.recordedAt)
                              })

                              return (
                                <div key={`${provenance.eventType}-${provenance.recordedAt}-${index}`} style={{ fontSize: 13, color: '#0f172a' }}>
                                  {t('lots.history.current.timelineStep', {
                                    step: index + 1,
                                    entry: chainEntry
                                  })}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {selectedHistoryLot ? (
                    <div style={{ padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.detail.kind')}</span>
                        <span style={{ fontSize: 13, color: '#0f172a' }}>
                          {isHatcheryLot(selectedHistoryLot) ? t('lots.newLot.eggs') : t('lots.newLot.fish')}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.table.stockingDate')}</span>
                        <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLot.stockingDate ? dateInputValue(selectedHistoryLot.stockingDate) : '—'}</span>
                      </div>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.table.harvestDate')}</span>
                        <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLot.harvestDate ? dateInputValue(selectedHistoryLot.harvestDate) : '—'}</span>
                      </div>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.table.lineage')}</span>
                        <span style={{ fontSize: 13, color: '#0f172a' }}>
                          {selectedHistoryLotLineage.parents.length + selectedHistoryLotLineage.children.length > 0
                            ? t('lots.lineage.summary', { parents: selectedHistoryLotLineage.parents.length, children: selectedHistoryLotLineage.children.length })
                            : '—'}
                        </span>
                      </div>
                    </div>
                  ) : null}
                  {selectedHistoryLot && selectedHistoryLot.productionUnitId && isLotActiveForInteraction(selectedHistoryLot.stage) ? (
                    <LotTransferPlanRow
                      lot={selectedHistoryLot}
                      farm={availableFarms.find((candidate) => candidate.id === selectedHistoryLot.farmId) as WizardFarm | undefined}
                      canEdit={canEditLots}
                      onChanged={() => { void refreshVisibleAndLineageLots({ preferLocal: true }) }}
                    />
                  ) : null}
                  {selectedHistoryLot && selectedHistoryLot.productionUnitId ? (
                    <LotCapacityRow
                      lot={selectedHistoryLot}
                      farm={availableFarms.find((candidate) => candidate.id === selectedHistoryLot.farmId) as { metadata?: Record<string, unknown> } | undefined}
                      facilityLots={lineageSourceLots}
                      canEdit={canEditLots}
                      onChanged={() => { void refreshVisibleAndLineageLots({ preferLocal: true }) }}
                    />
                  ) : null}
                  {lotDetailTabs.includes('hatchery') || lotDetailTabs.includes('projection') ? (
                    <div style={{ fontSize: 13, color: '#475569' }}>
                      <button
                        type="button"
                        onClick={() => setLotDetailTab(lotDetailTabs.includes('hatchery') ? 'hatchery' : 'projection')}
                        style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {t(lotDetailTabs.includes('hatchery') ? 'lots.detail.openHatchery' : 'lots.detail.openProjection')}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {activeLotDetailTab === 'hatchery' && selectedHistoryLot ? (
                <LotHatcherySection
                  lot={selectedHistoryLot}
                  canEdit={canEditLots}
                  onLotChanged={() => { void refreshVisibleAndLineageLots({ preferLocal: true }) }}
                />
              ) : null}

              {/* Hatchery v0 (#84): eggs and yolk-sac alevins get no growth / feed projection. */}
              {activeLotDetailTab === 'projection' && selectedHistoryLot ? (
                <LotProjectionSection
                  lot={selectedHistoryLot}
                  facilityTargetWeightG={readHarvestTargetWeightG(availableFarms.find((farm) => farm.id === selectedHistoryLot.farmId))}
                  canEdit={canEditLots}
                />
              ) : null}

              {activeLotDetailTab === 'history' ? (
                <div data-testid="lots-detail-history" style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0' }}>{t('lots.history.title', { lot: selectedHistoryLotDisplayLabel })}</h4>
                    <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{t('lots.history.subtitle')}</p>
                  </div>
                  {/* "¿Este lote llegó como ovas?" — and, once past first
                      feeding, the lot's egg record — stay in Historial. */}
                  {selectedHistoryLot && isLotDetailEligible && !isHatcheryLot(selectedHistoryLot) ? (
                    <LotHatcherySection
                      lot={selectedHistoryLot}
                      canEdit={canEditLots}
                      onLotChanged={() => { void refreshVisibleAndLineageLots({ preferLocal: true }) }}
                    />
                  ) : null}
                  {selectedHistoryLot ? <LotCountDifferencesSection lot={selectedHistoryLot} canEdit={canEditLots} /> : null}
                  {historyLoading ? (
                    <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.history.loading')}</div>
                  ) : historyError ? (
                    <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
                      {historyError}
                    </div>
                  ) : scopedHistoryEvents.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.history.empty')}</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                      {scopedHistoryEvents.map((event) => {
                        const fieldChanges = buildHistoryFieldChanges(event)
                        const locationSummary = buildHistoryLocationSummary(event)
                        const detailLines = (historyDetailsById.get(event.id) ?? []).map((detail) => formatHistoryDetail(detail, selectedHistoryLot?.farmId))
                        const saysNothing = event.eventType === 'update' && fieldChanges.length === 0 && !locationSummary && detailLines.length === 0 && !event.notes
                        const sourceLots = (event.eventType === 'split' || event.eventType === 'merge') && event.sourceLotIds.length > 0
                          ? formatHistoryLotIds(event.sourceLotIds, lotKeyById)
                          : undefined
                        const targetLots = (event.eventType === 'split' || event.eventType === 'merge' || event.eventType === 'create') && event.targetLotIds.length > 0
                          ? formatHistoryLotIds(event.targetLotIds, lotKeyById)
                          : undefined

                        return (
                          <article key={event.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, backgroundColor: '#ffffff', padding: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                              <span style={{ ...getLotHistoryBadgeStyle(event.eventType), padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                {t(lotHistoryEventLabelKey[event.eventType])}
                              </span>
                              <span style={{ fontSize: 12, color: '#64748b' }}>{formatHistoryTimestamp(event.recordedAt)}</span>
                            </div>

                            <div style={{ display: 'grid', gap: 8, fontSize: 13, color: '#0f172a' }}>
                              {sourceLots && (
                                <div><strong>{t('lots.history.sourceLots')}</strong> {sourceLots}</div>
                              )}

                              {targetLots && (
                                <div><strong>{t('lots.history.targetLots')}</strong> {targetLots}</div>
                              )}

                              {locationSummary && (
                                <div><strong>{t('lots.history.location')}</strong> {locationSummary}</div>
                              )}

                              {detailLines.map((line) => (
                                <div key={`${event.id}-${line}`} data-testid="lots-history-detail">{line}</div>
                              ))}

                              {saysNothing && (
                                <div style={{ color: '#64748b' }}>{t('lots.history.dataUpdated')}</div>
                              )}

                              {fieldChanges.length > 0 && (
                                <div>
                                  <strong>{t('lots.history.changes')}</strong>
                                  <div style={{ display: 'grid', gap: 4, marginTop: 6 }}>
                                    {fieldChanges.map((change) => (
                                      <div key={`${event.id}-${change.field}`}>
                                        {t(lotHistorySnapshotFieldLabelKey[change.field])}: {change.before}{' -> '}{change.after}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {event.notes && (
                                <div><strong>{t('lots.history.notes')}</strong> {event.notes}</div>
                              )}
                            </div>
                          </article>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : null}

              {activeLotDetailTab === 'costs' && selectedHistoryLot ? (
                <div data-testid="lots-detail-costs">
                  <LotCostSection
                    farmId={selectedHistoryLot.farmId}
                    productionUnitId={selectedHistoryLot.productionUnitId || undefined}
                    lots={[selectedHistoryLot]}
                    canEdit={canEditLots}
                    selectedLotId={selectedHistoryLot.id}
                    onSelectLot={() => undefined}
                  />
                </div>
              ) : null}

              {activeLotDetailTab === 'treatments' && selectedHistoryLot ? (
                <LotTreatmentsSection
                  lot={selectedHistoryLot}
                  canEdit={canEditLots && selectedHistoryLot.stage !== 'Harvested'}
                  refreshKey={treatmentRefreshKey}
                  onRecord={() => setTreatmentLot(selectedHistoryLot)}
                />
              ) : null}

              {activeLotDetailTab === 'traceability' ? (
                <div data-testid="lots-detail-traceability" style={{ display: 'grid', gap: 12 }}>
                  <div style={{ padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'grid', gap: 10 }}>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.detail.lineageRoot')}</span>
                      <span style={{ fontSize: 13, color: '#0f172a' }}>
                        {selectedHistoryLot
                          ? (lotKeyById.get(selectedHistoryLot.lineageRootId ?? selectedHistoryLot.id) ?? selectedHistoryLot.sourceLotKey ?? selectedHistoryLot.lotKey)
                          : '—'}
                      </span>
                    </div>
                    {([
                      ['parents', 'lots.detail.parents', selectedHistoryLotLineage.parents],
                      ['children', 'lots.detail.children', selectedHistoryLotLineage.children]
                    ] as const).map(([kind, labelKey, ids]) => (
                      <div key={kind} style={{ display: 'grid', gap: 4 }} data-testid={`lots-detail-lineage-${kind}`}>
                        <span style={{ fontSize: 12, color: '#475569' }}>{t(labelKey)}</span>
                        {ids.length === 0 ? (
                          <span style={{ fontSize: 13, color: '#0f172a' }}>—</span>
                        ) : (
                          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {ids.map((id) => {
                              const related = lotById.get(id)
                              return related ? (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => openLotDetail(related, 'traceability')}
                                  style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                                >
                                  {related.lotKey}
                                </button>
                              ) : (
                                <span key={id} style={{ fontSize: 13, color: '#0f172a' }}>{lotKeyById.get(id) ?? id}</span>
                              )
                            })}
                          </span>
                        )}
                      </div>
                    ))}
                    {selectedHistoryLotLatestProvenanceLabel ? (
                      <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.history.current.latestOrigin')}</span>
                        <span style={{ fontSize: 13, color: '#0f172a' }}>{selectedHistoryLotLatestProvenanceLabel}</span>
                      </div>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13, color: '#475569' }}>
                    {t('lots.detail.traceabilityHint')}{' '}
                    <button
                      type="button"
                      onClick={() => setLotDetailTab('history')}
                      style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {t('lots.detail.openHistory')}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </Drawer>

        <Drawer
          open={lotFormPanelOpen}
          onClose={closeLotFormPanel}
          title={isNewLotForm && newLotKind === null ? t('lots.newLot.title') : t(formPresentation.titleKey)}
        >
          {isNewLotForm && newLotKind === null ? (
            <div data-testid="lots-new-lot-kind" style={{ display: 'grid', gap: 12 }}>
              <p style={{ margin: 0, fontWeight: 600 }}>{t('lots.newLot.question')}</p>
              {(['fish', 'eggs'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  data-testid={`lots-new-lot-${kind}`}
                  onClick={() => chooseNewLotKind(kind)}
                  style={{
                    display: 'grid',
                    gap: 4,
                    textAlign: 'left',
                    padding: 14,
                    borderRadius: 12,
                    border: '1px solid #cbd5f5',
                    backgroundColor: kind === 'eggs' ? '#f0f9ff' : '#f9fafb',
                    cursor: 'pointer'
                  }}
                >
                  <strong style={{ fontSize: 15 }}>{kind === 'eggs' ? `🥚 ${t('lots.newLot.eggs')}` : `🐟 ${t('lots.newLot.fish')}`}</strong>
                  <span style={{ fontSize: 13, color: '#475569' }}>{t(kind === 'eggs' ? 'lots.newLot.eggsHelp' : 'lots.newLot.fishHelp')}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {isNewLotForm ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 13, color: '#334155' }} data-testid="lots-new-lot-kind-chosen">
                  <strong>{newLotKind === 'eggs' ? `🥚 ${t('lots.newLot.eggs')}` : `🐟 ${t('lots.newLot.fish')}`}</strong>
                  <button
                    type="button"
                    onClick={() => setNewLotKind(null)}
                    style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {t('lots.newLot.change')}
                  </button>
                </div>
              ) : null}
              <form
                ref={lotFormAnchorRef}
                onSubmit={handleSubmit}
                data-testid="lots-form"
              >
                {formPresentation.noticeKey && (
                  <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#0f766e' }}>
                    {t(formPresentation.noticeKey)}
                  </p>
                )}
                {isPlantReadOnlyWriteContext ? (
                  <div style={{ margin: '0 0 12px 0', display: 'grid', gap: 6 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                      {t('lots.form.processingPlantWriteGuard')}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: '#78350f' }}>
                      {t('lots.form.processingPlantIntakeNote')}
                    </p>
                  </div>
                ) : null}
                {showTransferDestinationMode && (
                  <div
                    style={{
                      margin: '0 0 12px 0',
                      borderRadius: 10,
                      border: '1px solid #dbeafe',
                      backgroundColor: '#ffffff',
                      padding: 12,
                      display: 'grid',
                      gap: 8
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      {t('lots.form.transferDestinationTypeLabel')}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {transferDestinationModeOptions.map((option) => {
                        const isSelected = form.targetLocationType === option
                        return (
                          <label
                            key={option}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '8px 10px',
                              borderRadius: 999,
                              border: isSelected ? '1px solid #0f766e' : '1px solid #cbd5e1',
                              backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                              cursor: canEditLots ? 'pointer' : 'not-allowed',
                              opacity: canEditLots ? 1 : 0.55,
                              fontSize: 13,
                              color: '#0f172a'
                            }}
                          >
                            <input
                              type="radio"
                              name="transfer-location-type"
                              value={option}
                              checked={isSelected}
                              onChange={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  targetLocationType: option,
                                  productionUnitId: option === 'processing-site' ? '' : prev.productionUnitId,
                                  processingSiteId: option === 'processing-site' ? prev.processingSiteId : ''
                                }))
                                setDestinationSearchQuery('')
                              }}
                              disabled={!canEditLots}
                            />
                            {t(option === 'farm'
                              ? 'lots.form.transferDestinationType.farm'
                              : option === 'processing-site'
                                ? 'lots.form.transferDestinationType.processingSite'
                                : 'lots.form.transferDestinationType.productionUnit')}
                          </label>
                        )
                      })}
                    </div>
                    {form.targetLocationType === 'farm' && (
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {t('lots.form.transferDestinationType.farmHelp')}
                      </div>
                    )}
                    {form.targetLocationType === 'processing-site' && (
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {t('lots.form.transferDestinationType.processingSiteHelp')}
                      </div>
                    )}
                  </div>
                )}
                {formPresentation.locationSummary && (
                  <div
                    style={{
                      margin: '0 0 12px 0',
                      borderRadius: 10,
                      border: '1px solid #bfdbfe',
                      backgroundColor: '#eff6ff',
                      padding: 12,
                      display: 'grid',
                      gap: 8,
                      fontSize: 13,
                      color: '#0f172a'
                    }}
                  >
                    <div>
                      <strong>{t('lots.form.location.current')}</strong> {formatDraftLocationLabel(
                        sourceLotForForm?.farmId ?? '',
                        sourceLotForForm?.productionUnitId ?? '',
                        sourceLotForForm?.locationType === 'processing-site'
                          ? 'processing-site'
                          : sourceLotForForm?.locationType === 'farm'
                            ? 'farm'
                            : 'production-unit',
                        sourceLotForForm?.locationId ?? sourceLotForForm?.productionUnitId ?? ''
                      )}
                    </div>
                    <div>
                      <strong>{t('lots.form.location.target')}</strong> {formatDraftLocationLabel(
                        formPresentation.locationSummary.targetFarmId,
                        formPresentation.locationSummary.targetProductionUnitId,
                        formPresentation.locationSummary.targetLocationType,
                        formPresentation.locationSummary.targetLocationId
                      )}
                    </div>
                  </div>
                )}
                {transferIntentGuidance && (
                  <div
                    style={{
                      margin: '0 0 12px 0',
                      borderRadius: 10,
                      padding: 12,
                      fontSize: 13,
                      backgroundColor: transferIntentGuidance.tone === 'warning'
                        ? '#fffbeb'
                        : transferIntentGuidance.tone === 'success'
                          ? '#ecfdf5'
                          : '#eff6ff',
                      color: transferIntentGuidance.tone === 'warning'
                        ? '#92400e'
                        : transferIntentGuidance.tone === 'success'
                          ? '#047857'
                          : '#1d4ed8',
                      border: transferIntentGuidance.tone === 'warning'
                        ? '1px solid #fde68a'
                        : transferIntentGuidance.tone === 'success'
                          ? '1px solid #a7f3d0'
                          : '1px solid #bfdbfe'
                    }}
                  >
                    {transferIntentGuidance.message}
                  </div>
                )}
                {!isPlantReadOnlyWriteContext ? (
                  <>
                    {destinationOptions.length > 1 && !isUnitLockedInEdit && (
                      <div
                        style={{
                          borderRadius: 10,
                          border: '1px solid #dbeafe',
                          backgroundColor: '#ffffff',
                          padding: 12,
                          display: 'grid',
                          gap: 8
                        }}
                      >
                        <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                          {t('lots.form.destinationSearchLabel')}
                          <input
                            type="search"
                            value={destinationSearchQuery}
                            onChange={(event) => setDestinationSearchQuery(event.target.value)}
                            disabled={!canEditLots}
                            placeholder={t('lots.form.destinationSearchPlaceholder')}
                            style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                          />
                        </label>
                        <div style={{ fontSize: 12, color: '#475569' }}>
                          {t(destinationSearchQuery.trim()
                            ? 'lots.form.destinationSearchMatches'
                            : 'lots.form.destinationSearchSuggested')}
                        </div>
                        {visibleDestinationOptions.length > 0 ? (
                          <div style={{ display: 'grid', gap: 6, maxHeight: 192, overflowY: 'auto' }}>
                            {visibleDestinationOptions.map((option) => {
                              const isSelected = form.targetLocationType === 'farm'
                                ? option.farmId === form.farmId
                                : form.targetLocationType === 'processing-site'
                                  ? option.farmId === form.farmId && option.processingSiteId === form.processingSiteId
                                  : option.farmId === form.farmId && option.productionUnitId === form.productionUnitId
                              const selectedContextUnitLabel = (
                                form.targetLocationType === 'farm' && option.farmId === form.farmId && form.productionUnitId
                                  ? (productionUnitLookup.get(form.productionUnitId) ?? form.productionUnitId)
                                  : option.productionUnitLabel
                              )
                              return (
                                <button
                                  key={`${option.farmId}:${option.targetLocationType}:${option.processingSiteId || option.productionUnitId}`}
                                  type="button"
                                  onClick={() => handleDestinationSelect(option)}
                                  disabled={!canEditLots}
                                  style={{
                                    textAlign: 'left',
                                    borderRadius: 8,
                                    border: isSelected ? '1px solid #0f766e' : '1px solid #cbd5e1',
                                    backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                                    padding: '10px 12px',
                                    display: 'grid',
                                    gap: 2,
                                    cursor: canEditLots ? 'pointer' : 'not-allowed',
                                    opacity: canEditLots ? 1 : 0.55
                                  }}
                                >
                                  {form.targetLocationType === 'farm' ? (
                                    <>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{option.farmLabel}</span>
                                      {selectedContextUnitLabel ? (
                                        <span style={{ fontSize: 12, color: '#475569' }}>
                                          {t('lots.form.destinationContextUnit', { unit: selectedContextUnitLabel })}
                                        </span>
                                      ) : null}
                                    </>
                                  ) : form.targetLocationType === 'processing-site' ? (
                                    <>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{option.processingSiteLabel}</span>
                                      <span style={{ fontSize: 12, color: '#475569' }}>{option.farmLabel}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{option.productionUnitLabel}</span>
                                      <span style={{ fontSize: 12, color: '#475569' }}>{option.farmLabel}</span>
                                    </>
                                  )}
                                </button>
                              )
                            })}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {t('lots.form.destinationSearchEmpty')}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    {t('lots.form.lotKeyLabel')}
                    <input
                      type="text"
                      value={form.lotKey}
                      onChange={(event) => handleChange('lotKey', event.target.value)}
                      disabled={!canEditLots}
                      placeholder={t('lots.form.lotKeyPlaceholder')}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    {t('lots.form.facilityLabel')}
                    <select
                      value={form.farmId}
                      onChange={(event) => {
                        const nextFarm = event.target.value
                        handleChange('farmId', nextFarm)
                        handleChange('productionUnitId', '')
                        handleChange('processingSiteId', '')
                        setDestinationSearchQuery('')
                      }}
                      disabled={!canEditLots || isUnitLockedInEdit}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    >
                      <option value="">{t('lots.form.facilityPlaceholder')}</option>
                      {availableFarms.map((farm) => (
                        <option key={farm.id ?? farm.name} value={farm.id ?? ''}>
                          {farm.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  {showProcessingSiteSelector ? (
                    <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                      {t('lots.form.processingSiteLabel')}
                      <select
                        value={form.processingSiteId}
                        onChange={(event) => {
                          handleChange('processingSiteId', event.target.value)
                          setDestinationSearchQuery('')
                        }}
                        disabled={!form.farmId || !canEditLots}
                        style={{
                          padding: 10,
                          borderRadius: 8,
                          border: '1px solid #cbd5f5',
                          backgroundColor: !form.farmId ? '#f1f5f9' : '#ffffff'
                        }}
                      >
                        <option value="">{t('lots.form.processingSitePlaceholder')}</option>
                        {selectedFarmProcessingSites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.label}
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {t('lots.form.processingSiteHelp')}
                      </div>
                    </label>
                  ) : (
                    <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                      {t('lots.form.productionUnitLabel')}
                      <select
                        value={form.productionUnitId}
                        onChange={(event) => {
                          handleChange('productionUnitId', event.target.value)
                          setDestinationSearchQuery('')
                        }}
                        disabled={!form.farmId || !canEditLots || isUnitLockedInEdit}
                        style={{
                          padding: 10,
                          borderRadius: 8,
                          border: '1px solid #cbd5f5',
                          backgroundColor: !form.farmId ? '#f1f5f9' : '#ffffff'
                        }}
                      >
                        <option value="">{t(form.targetLocationType === 'farm' ? 'lots.form.productionUnitOptionalPlaceholder' : 'lots.form.productionUnitPlaceholder')}</option>
                        {(form.farmId ? availableFarms.find((farm) => farm.id === form.farmId)?.productionUnits ?? [] : [])
                          .map((unit) => (
                            <option key={unit.id ?? unit.name} value={unit.id ?? ''}>
                              {unit.name}
                            </option>
                          ))}
                      </select>
                      {/* findings #104: warn, never block */}
                      {sameNameMoveTarget ? null : (
                        <UnitOccupiedWarning farmId={form.farmId} unitId={form.productionUnitId} excludeLotId={form.id || undefined} />
                      )}
                      {isUnitLockedInEdit && sourceLotForForm ? (
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#475569' }} data-testid="lots-form-unit-locked">
                          {t('lots.move.editUnitLocked')}{' '}
                          <button
                            type="button"
                            onClick={() => handleTransfer(sourceLotForForm)}
                            style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                            data-testid="lots-form-open-move"
                          >
                            {t('lots.button.transfer')}
                          </button>
                        </span>
                      ) : null}
                      {form.targetLocationType === 'farm' ? (
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {t('lots.form.productionUnitOptionalHelp')}
                        </div>
                      ) : null}
                    </label>
                  )}

                  {isMoveForm && sourceLotForForm ? (
                    <MoveFields
                      moveAt={moveAt}
                      onMoveAtChange={setMoveAt}
                      timeZone={farmTimeZone(sourceLotForForm.farmId)}
                      disabled={!canEditLots}
                      sourceLot={sourceLotForForm}
                      source={{
                        farmId: sourceLotForForm.farmId,
                        farmName: farmLookup.get(sourceLotForForm.farmId),
                        productionUnitId: sourceLotForForm.productionUnitId ?? '',
                        productionUnitName: productionUnitLookup.get(sourceLotForForm.productionUnitId ?? '')
                      }}
                      sameNameTarget={sameNameMoveTarget}
                      fishMoved={fishMoved}
                      onFishMovedChange={setFishMoved}
                      onStocksChange={setFishMoveStocks}
                      arrivalUnit={arrivalUnit}
                      arrivalCount={arrivalCount}
                      onArrivalCountChange={setArrivalCount}
                      diedInMove={diedInMove}
                      onDiedInMoveChange={setDiedInMove}
                    />
                  ) : null}
                  {isMoveForm && sourceLotForForm && !checkingMoveDestination ? (
                    <TransferProtocolFields
                      sourceLot={sourceLotForForm}
                      sourceFarm={availableFarms.find((farm) => farm.id === sourceLotForForm.farmId) as WizardFarm | undefined}
                      destination={protocolDestination}
                      sourceUnitId={sourceLotForForm.productionUnitId ?? ''}
                      fishAfterMove={protocolFishAfterMove}
                      moveAt={moveAt}
                      timeZone={farmTimeZone(sourceLotForForm.farmId)}
                      disabled={!canEditLots}
                      onChange={setTransferRationDraft}
                    />
                  ) : null}
                  {isMoveForm && checkingMoveDestination ? (
                    <div style={{ fontSize: 12, color: '#475569' }} data-testid="lots-move-checking-destination">{t('lots.move.checkingDestination')}</div>
                  ) : null}

                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    {t('lots.form.stageLabel')}
                    <select
                      value={form.stage}
                      onChange={(event) => handleChange('stage', event.target.value as LotStage)}
                      disabled={!canEditLots}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    >
                      {stageOptions.map((option) => (
                        <option key={option} value={option}>
                          {t(stageLabelKey[option])}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    {t('lots.form.speciesLabel')}
                    <input
                      type="text"
                      value={form.species}
                      onChange={(event) => handleChange('species', event.target.value)}
                      disabled={!canEditLots}
                      placeholder={t('lots.form.optionalPlaceholder')}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    />
                  </label>

                  {/* Ovas: the arrival date below is the stocking date. */}
                  {!isEggLotForm ? (
                    <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                      {t('lots.form.stockingDateLabel')}
                      <DateInput
                        value={form.stockingDate}
                        onChangeValue={(nextValue) => handleChange('stockingDate', nextValue)}
                        pickerOnly
                        disabled={!canEditLots}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                      />
                    </label>
                  ) : null}

                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    {t('lots.form.harvestDateLabel')}
                    <DateInput
                      value={form.harvestDate}
                      onChangeValue={(nextValue) => handleChange('harvestDate', nextValue)}
                      pickerOnly
                      disabled={!canEditLots}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    />
                  </label>
                  {form.id && form.stage === 'Harvested' ? <WithdrawalWarning lotId={form.id} atDate={form.harvestDate} /> : null}

                  <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
                    {t('lots.form.biomassKgLabel')}
                    <input
                      type="text"
                      value={form.biomassKg}
                      onChange={(event) => handleChange('biomassKg', event.target.value)}
                      disabled={!canEditLots}
                      placeholder={t('lots.form.optionalPlaceholder')}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                    />
                  </label>

                  {isNewLotForm && newLotKind !== null ? (
                    <CapacityTablePicker
                      farm={availableFarms.find((candidate) => candidate.id === form.farmId) as { metadata?: Record<string, unknown> } | undefined}
                      value={newLotCapacityId}
                      onChange={setNewLotCapacityId}
                      disabled={!canEditLots}
                      testId="lots-new-lot-capacity"
                    />
                  ) : null}

                  {isEggLotForm ? (
                    <div data-testid="lots-new-lot-egg-arrival" style={{ display: 'grid', gap: 10, padding: 10, borderRadius: 8, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                      <strong style={{ fontSize: 13 }}>{t('lots.hatchery.arrival.title')}</strong>
                      <EggArrivalFields draft={eggArrivalDraft} onChange={(update) => setEggArrivalDraft(update)} />
                    </div>
                  ) : null}

                  {formError && (
                    <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>{formError}</div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <GradientButton type="submit" disabled={saving || !canEditLots || isTransferSubmitBlocked}>
                      {saving ? t('lots.saving') : t(formPresentation.submitKey)}
                    </GradientButton>
                    <button
                      type="button"
                      onClick={closeLotFormPanel}
                      disabled={saving}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 8,
                        border: '1px solid #cbd5f5',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer'
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                    </div>
                  </>
                ) : null}
              </form>
            </>
          )}
        </Drawer>
              </>
            )}
          </>
        )}
      </div>
      {treatmentLot ? (
        <LotTreatmentDialog
          open
          lot={treatmentLot}
          siblingLots={lots.filter((candidate) => (
            candidate.id !== treatmentLot.id
            && candidate.farmId === treatmentLot.farmId
            && candidate.lotKey === treatmentLot.lotKey
            && candidate.stage === 'Active'
          ))}
          unitNameById={new Map((findFarmById(availableFarms, treatmentLot.farmId)?.productionUnits ?? []).map((unit) => [unit.id, unit.name]))}
          currency={facilityCurrencyOrFallback(findFarmById(availableFarms, treatmentLot.farmId))}
          onClose={() => setTreatmentLot(null)}
          onSaved={() => setTreatmentRefreshKey((key) => key + 1)}
        />
      ) : null}
    </PageShell>
  )
}
